import React, { useState, useEffect, useCallback } from 'react';
import * as chrono from 'chrono-node';
import './App.css';
import { webApi, setWebAuthToken, getWebApiErrorMessage, setOnUnauthorized } from './services/api';
import { webConfig } from './config/config';
import CompanyDashboardPage from './pages/companyDashboard';
import ApplicantDashboardPage from './pages/applicantDashboard';
import { Link } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import MainLayout from './components/layout/MainLayout';
import { webAppConfig } from './config/appConfig';

// ── Types ──────────────────────────────────────────────────────────────
type Role = 'job_seeker' | 'company' | 'admin';
type View = 'auth' | 'seeker' | 'company' | 'admin';
type AdminTab = 'analytics' | 'job-approvals' | 'performance' | 'registration' | 'manage-tabs';
type AuthTab = 'login' | 'register';
type SeekerTab = 'dashboard' | 'browse' | 'best-match' | 'my-applications' | 'your-invitations' | 'profile';
type CompanyTab = 'dashboard' | 'jobs' | 'best-matches' | 'profile';
type JobStatus = 'OPEN' | 'IN_SELECTION' | 'FILLED' | 'COMPLETED';
type ApplicationStatus =
  | 'APPLIED'
  | 'RESPONSE_AWAITED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'COMPLETED'
  | 'NO_SHOW';

type SafeUser = {
  id: number;
  role: Role;
  fullName: string;
  email: string;
  phone?: string | null;
  skills?: string[];
  workExperienceYears?: number | null;
  availability?: 'full-time' | 'part-time' | 'freelance' | 'hourly' | null;
  resumeUrl?: string | null;
  reviews?: string[];
  ratingAverage?: number | null;
  ratingCount?: number | null;
  profilePictureUrl?: string | null;
  companyName?: string | null;
  industry?: string | null;
  companySize?: number | null;
  companyWebsite?: string | null;
  verifiedStatus?: boolean;
  companyLogoUrl?: string | null;
  locations?: string[];
  city?: string | null;
  state?: string | null;
  country?: string | null;
};

type RegisterForm = {
  userType: RegistrationUserType;
  fullName: string;
  email: string;
  password: string;
  acceptedLegalPolicies: boolean;
  phone: string;
  skills: string[];
  workExperienceYears: number | '';
  availability: '' | 'full-time' | 'part-time' | 'freelance' | 'hourly';
  resumeUrl: string;
  reviews: string[];
  ratingAverage: number | '';
  ratingCount: number | '';
  profilePictureUrl: string;
  city: string;
  state: string;
  country: string;
  industry: string;
  companySize: number | '';
  verifiedStatus: boolean;
  companyLogoUrl: string;
  locations: string[];
  companyWebsite: string;
  companyName: string;
};

type LoginForm = {
  email: string;
  password: string;
};

type JobForm = {
  title: string;
  companyName: string;
  category?: string;
  location: string;
  description: string;
  requiredSkills: string[];
  workerSlots: number;
  rateType: 'hourly' | 'fixed';
  rateAmount: number;
  startAt: string;
  endAt: string;
};

type ParsedJobSuggestion = Partial<{
  title: string;
  companyName: string;
  category: string;
  location: string;
  description: string;
  requiredSkills: string[];
  rateType: 'hourly' | 'fixed' | 'daily';
  rateAmount: number;
  startAt: string;
  endAt: string;
  workerSlots: number;
}>;

type Job = {
  id: number;
  companyUserId?: number;
  applicationId?: number;
  title: string;
  companyName: string;
  category?: string | null;
  location: string;
  rateType: 'hourly' | 'fixed';
  rateAmount: number;
  startAt: string;
  endAt: string;
  requiredSkills: string[];
  description: string;
  workerSlots: number;
  filledSlots: number;
  status: JobStatus;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvalReviewedAt?: string | null;
  publishedAt?: string | null;
  createdAt?: string;
  latestApplicationStatus?: ApplicationStatus | null;
  rejectionReason?: string | null;
  reapplyAt?: string | null;
  canApply?: boolean;
  reviews?: JobReview[];
  applicantReviews?: JobReview[];
  company?: {
    id: number;
    name: string;
    logo?: string | null;
    ratingAverage?: number | null;
    ratingCount?: number;
    reviews?: JobReview[];
  };
};

type CompanyJob = Job & {
  reviews?: JobReview[];
  applications: Array<{
    id: number;
    job_seeker_user_id?: number;
    applicant_name: string;
    applicant_email: string;
    applicant_phone?: string;
    applicant_city?: string;
    applicant_availability?: string;
    applicant_expected_rate_min?: number;
    applicant_expected_rate_max?: number;
    applicant_skills?: string[];
    applicant_bio?: string;
    applicant_state?: string;
    applicant_country?: string;
    applicant_work_experience_years?: number;
    applicant_resume_url?: string;
    applicant_profile_picture_url?: string;
    applicant_rating_average?: number;
    applicant_rating_count?: number;
    applicant_reviews?: unknown[];
    status: ApplicationStatus;
    rejection_reason?: string;
    rejected_by?: 'company' | 'job_seeker' | null;
  }>;
};

type JobReview = {
  id: number;
  job_id: number;
  job_title?: string;
  reviewer_user_id: number;
  reviewee_user_id: number;
  reviewer_type?: string;
  rating: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
  reviewer_name?: string;
  reviewee_name?: string;
  jobId?: number;
  reviewerUserId?: number;
  revieweeUserId?: number;
  jobTitle?: string;
  reviewerType?: string;
  createdAt?: string;
  updatedAt?: string;
  reviewerName?: string;
  revieweeName?: string;
};

type ReviewFeedResponse = {
  target: {
    id: number;
    userType: 'company' | 'job_seeker';
    name: string;
    averageRating: number | null;
    totalReviews: number;
  };
  reviews: JobReview[];
};

type CompanyDashboardData = {
  totalJobsCreated: number;
  totalJobsOpen: number;
  totalJobsClosed: number;
  positionsPosted: number;
  positionsFilled: number;
  remainingPositions: number;
  totalApplicants: number;
  acceptedApplicants: number;
  companyRating: number | null;
  totalReviews: number;
  funnel: {
    jobsPosted: number;
    applicationsReceived: number;
    applicantsAccepted: number;
    jobsCompleted: number;
  };
  companyRatingDetails?: Array<{
    applicantName: string;
    jobTitle: string;
    rating: number;
    comment: string | null;
    createdAt: string;
  }>;
  jobs: Array<{
    jobId: number;
    jobTitle: string;
    totalPositions: number;
    positionsFilled: number;
    remainingPositions: number;
    totalApplicants: number;
    acceptedApplicants: number;
    jobStatus: string;
    jobRating: number | null;
  }>;
};

type ApplicantDashboardData = {
  applicationsSubmitted: number;
  applicationsAccepted: number;
  applicationsRejected: number;
  applicationsPending: number;
  jobsCompleted: number;
  totalHoursWorked: number;
  averageRating: number | null;
  totalReviews: number;
  completionRate: number;
  acceptanceRate: number;
  noShowRate: number;
  ratingsFromCompanies?: Array<{
    companyName: string;
    jobTitle: string;
    rating: number;
    comment: string | null;
    createdAt: string;
  }>;
  activity: Array<{
    jobId: number;
    jobTitle: string;
    company: string;
    applicationStatus: string;
    dateApplied: string;
    jobStatus: string;
  }>;
};

type NotificationItem = {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
};

type SeekerInvitation = {
  id: number;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  note?: string | null;
  createdAt: string;
  job: {
    id: number;
    title: string;
    companyName: string;
    location: string;
    rateType: 'hourly' | 'fixed';
    rateAmount: number;
    startAt: string;
    endAt: string;
    requiredSkills: string[];
    description: string;
    workerSlots: number;
    filledSlots: number;
    status: JobStatus;
  };
};

type CompanyBestMatchCandidate = {
  userId: number;
  fullName: string | null;
  email: string;
  phone?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  availability?: string | null;
  workExperienceYears?: number | null;
  resumeUrl?: string | null;
  profilePictureUrl?: string | null;
  ratingAverage?: number | null;
  ratingCount?: number;
  skills: string[];
  matchScore: number;
  matchedSkills: string[];
  locationMatched: boolean;
  alreadyApplicant: boolean;
  latestApplicationStatus: ApplicationStatus | null;
  autoRejectedByFilledPosition: boolean;
  eligibleForInvite: boolean;
  inviteStatus: 'PENDING' | 'ACCEPTED' | 'REJECTED' | null;
  inviteId: number | null;
};

type CompanyBestMatchGroup = {
  job: {
    id: number;
    title: string;
    companyName: string;
    location: string;
    rateType: 'hourly' | 'fixed';
    rateAmount: number;
    startAt: string;
    endAt: string;
    requiredSkills: string[];
    description: string;
    workerSlots: number;
    filledSlots: number;
    status: JobStatus;
  };
  candidates: CompanyBestMatchCandidate[];
};

type ToastType = 'success' | 'error';
type Toast = { id: number; type: ToastType; message: string };

type AdminAnalyticsTraffic = {
  kpis: {
    uniqueVisitors: number;
    pageViews: number;
    sessions: number;
  };
  trends: {
    userGrowth: Array<{ bucket: string; value: number }>;
    jobPosting: Array<{ bucket: string; value: number }>;
  };
};

type AdminAnalyticsUsers = {
  activeUsers: {
    daily: number;
    weekly: number;
    monthly: number;
  };
  growth: {
    daily: Array<{ bucket: string; value: number }>;
    weekly: Array<{ bucket: string; value: number }>;
    monthly: Array<{ bucket: string; value: number }>;
  };
  breakdown: Array<{ role: string; value: number }>;
  registrySummary?: {
    totalUsers: number;
    totalCompanies: number;
    users: Array<{
      id: number;
      name: string;
      email: string;
      createdAt: string;
      isDeleted?: boolean;
      deletedAt?: string | null;
      purgedAt?: string | null;
    }>;
    companies: Array<{
      id: number;
      name: string;
      email: string;
      createdAt: string;
      isDeleted?: boolean;
      deletedAt?: string | null;
      purgedAt?: string | null;
    }>;
  };
};

type AdminJobControlSettings = {
  autoApproveJobs: boolean;
  sendAutoEmailOnNewJob: boolean;
  updatedAt?: string;
};

type AdminPendingApprovalJob = {
  id: number;
  companyUserId: number;
  companyName: string;
  title: string;
  location: string;
  rateType: 'hourly' | 'fixed';
  rateAmount: number;
  startAt: string;
  endAt: string;
  workerSlots: number;
  status: JobStatus;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  createdAt: string;
};

type AdminAnalyticsConversion = {
  summary: {
    uniqueVisitors: number;
    pageViews: number;
    sessions: number;
    conversionRate: number;
    jobsCompleted: number;
  };
  conversion: {
    submitted: number;
    accepted: number;
    completed: number;
  };
  trends: {
    jobPosting: Array<{ bucket: string; value: number }>;
  };
};

type AdminCompanyPerformanceSummary = {
  companyUserId: number;
  companyName: string;
  email: string;
  totalJobsCreated: number;
  activeJobs: number;
  completedJobs: number;
  totalSpent: number;
};

type AdminCompanyPerformanceDetail = AdminCompanyPerformanceSummary & {
  totalApplicants: number;
  acceptedApplicants: number;
  totalHoursContracted: number;
};

type AdminCandidatePerformanceSummary = {
  candidateUserId: number;
  candidateName: string;
  email: string;
  applicationsSubmitted: number;
  jobsCompleted: number;
  totalHoursWorked: number;
  totalEarned: number;
};

type AdminCandidatePerformanceDetail = AdminCandidatePerformanceSummary & {
  applicationsAccepted: number;
  applicationsRejected: number;
  applicationsPending: number;
  averageRating: number | null;
  totalReviews: number;
};

type RegistrationUserType = 'job_seeker' | 'company';
type RegistrationFieldConfig = {
  userType: RegistrationUserType;
  fieldKey: string;
  isVisible: boolean;
  isMandatory: boolean;
  sortOrder: number;
};

type FieldCatalogItem = {
  fieldKey: string;
  label: string;
};

type TabPreferenceConfig = {
  role: Role;
  tabOrder: string[];
  tabVisibility: Record<string, boolean>;
  tabLabels: Record<string, string>;
};

const SEEKER_FIELD_CATALOG: FieldCatalogItem[] = [
  { fieldKey: 'fullName', label: 'Username' },
  { fieldKey: 'email', label: 'Email' },
  { fieldKey: 'password', label: 'Password' },
  { fieldKey: 'skills', label: 'Skill Set' },
  { fieldKey: 'resumeUrl', label: 'Resume upload' },
  { fieldKey: 'phone', label: 'Phone' },
  { fieldKey: 'city', label: 'Location' },
  { fieldKey: 'state', label: 'State' },
  { fieldKey: 'country', label: 'Country' },
  { fieldKey: 'availability', label: 'Availability' },
  { fieldKey: 'workExperienceYears', label: 'Work Experience (years)' },
  { fieldKey: 'profilePictureUrl', label: 'Profile Picture' },
  { fieldKey: 'ratingAverage', label: 'Rating' },
  { fieldKey: 'ratingCount', label: 'Review Count' },
];

const COMPANY_FIELD_CATALOG: FieldCatalogItem[] = [
  { fieldKey: 'fullName', label: 'Username' },
  { fieldKey: 'email', label: 'Email' },
  { fieldKey: 'password', label: 'Password' },
  { fieldKey: 'companyName', label: 'Company Name' },
  { fieldKey: 'industry', label: 'Industry' },
  { fieldKey: 'companyWebsite', label: 'Company Website' },
  { fieldKey: 'location', label: 'Location' },
  { fieldKey: 'phone', label: 'Phone' },
  { fieldKey: 'companySize', label: 'Company Size' },
  { fieldKey: 'verifiedStatus', label: 'Verified Status' },
  { fieldKey: 'companyLogoUrl', label: 'Company Logo' },
];

const DEFAULT_FIELD_RULES: Record<RegistrationUserType, { mandatory: Set<string>; visible: Set<string> }> = {
  job_seeker: {
    mandatory: new Set(['fullName', 'email', 'password', 'skills', 'resumeUrl']),
    visible: new Set(['fullName', 'email', 'password', 'skills', 'resumeUrl']),
  },
  company: {
    mandatory: new Set(['fullName', 'email', 'password', 'companyName', 'industry', 'location']),
    visible: new Set(['fullName', 'email', 'password', 'companyName', 'industry', 'location']),
  },
};

const ROLE_TAB_KEYS: Record<Role, string[]> = {
  job_seeker: ['dashboard', 'browse', 'best-match', 'my-applications', 'your-invitations', 'profile'],
  company: ['dashboard', 'jobs', 'best-matches', 'profile'],
  admin: ['analytics', 'job-approvals', 'performance', 'registration', 'manage-tabs'],
};

const DEFAULT_TAB_LABELS: Record<Role, Record<string, string>> = {
  job_seeker: {
    dashboard: 'Dashboard',
    browse: 'Browse Jobs',
    'best-match': 'Best Match',
    'my-applications': 'My Applications',
    'your-invitations': 'Your Invitations',
    profile: 'Profile',
  },
  company: {
    dashboard: 'Dashboard',
    jobs: 'Job Management',
    'best-matches': 'Best Matches',
    profile: 'Company Profile',
  },
  admin: {
    analytics: 'Analytics Dashboard',
    'job-approvals': 'Job Approvals',
    performance: 'Performance Dashboard',
    registration: 'Registration Field Management',
    'manage-tabs': 'Manage Global Tab',
  },
};

const buildDefaultTabPreferenceConfig = (role: Role): TabPreferenceConfig => ({
  role,
  tabOrder: [...ROLE_TAB_KEYS[role]],
  tabVisibility: Object.fromEntries(ROLE_TAB_KEYS[role].map((key) => [key, true])) as Record<string, boolean>,
  tabLabels: {},
});

function normalizeTabPreferenceConfig(role: Role, config?: Partial<TabPreferenceConfig> | null): TabPreferenceConfig {
  const defaults = buildDefaultTabPreferenceConfig(role);
  if (!config) return defaults;

  const incomingOrder = Array.isArray(config.tabOrder)
    ? config.tabOrder.filter((key): key is string => typeof key === 'string' && ROLE_TAB_KEYS[role].includes(key))
    : [];

  const mergedOrder = [
    ...incomingOrder,
    ...ROLE_TAB_KEYS[role].filter((key) => !incomingOrder.includes(key)),
  ];

  const incomingVisibility = (config.tabVisibility ?? {}) as Record<string, boolean>;
  const mergedVisibility: Record<string, boolean> = {
    ...defaults.tabVisibility,
    ...incomingVisibility,
  };

  const incomingLabels = (config.tabLabels ?? {}) as Record<string, string>;

  return {
    role,
    tabOrder: mergedOrder,
    tabVisibility: mergedVisibility,
    tabLabels: incomingLabels,
  };
}

function getCatalog(userType: RegistrationUserType) {
  return userType === 'job_seeker' ? SEEKER_FIELD_CATALOG : COMPANY_FIELD_CATALOG;
}

function normalizeFieldConfigs(
  userType: RegistrationUserType,
  configs: RegistrationFieldConfig[],
): RegistrationFieldConfig[] {
  const byKey = new Map(configs.filter((cfg) => cfg.userType === userType).map((cfg) => [cfg.fieldKey, cfg]));
  const catalog = getCatalog(userType);
  const defaults = DEFAULT_FIELD_RULES[userType];

  const normalized = catalog.map((field, index) => {
    const existing = byKey.get(field.fieldKey);
    return {
      userType,
      fieldKey: field.fieldKey,
      isVisible: existing?.isVisible ?? defaults.visible.has(field.fieldKey),
      isMandatory: existing?.isMandatory ?? defaults.mandatory.has(field.fieldKey),
      sortOrder: existing?.sortOrder ?? index,
    };
  });

  const extras = configs.filter(
    (cfg) => cfg.userType === userType && !catalog.some((item) => item.fieldKey === cfg.fieldKey),
  );

  return [...normalized, ...extras];
}

// ── Initial State ──────────────────────────────────────────────────────
const initialRegister: RegisterForm = {
  userType: 'job_seeker',
  fullName: '',
  email: '',
  password: '',
  acceptedLegalPolicies: false,
  phone: '',
  skills: [],
  workExperienceYears: '',
  availability: '',
  resumeUrl: '',
  reviews: [],
  ratingAverage: '',
  ratingCount: '',
  profilePictureUrl: '',
  city: '',
  state: '',
  country: '',
  industry: '',
  companySize: '',
  verifiedStatus: false,
  companyLogoUrl: '',
  locations: [],
  companyWebsite: '',
  companyName: '',
};

const initialLogin: LoginForm = {
  email: '',
  password: '',
};

const initialJob: JobForm = {
  title: '',
  companyName: '',
  category: '',
  location: '',
  description: '',
  requiredSkills: [],
  workerSlots: 1,
  rateType: 'hourly',
  rateAmount: 0,
  startAt: '',
  endAt: '',
};

const buildDefaultJobForm = (user: SafeUser | null): JobForm => ({
  title: '',
  companyName: user?.companyName?.trim() || user?.fullName || '',
  category: '',
  location: user?.city?.trim() || user?.locations?.[0] || '',
  description: '',
  requiredSkills: [],
  workerSlots: 1,
  rateType: 'hourly',
  rateAmount: 0,
  startAt: '',
  endAt: '',
});

// ── Helpers ────────────────────────────────────────────────────────────
function fmtDateTime(date: string | null | undefined) {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function renderStars(rating: number | null | undefined) {
  const safe = Math.max(0, Math.min(5, Math.round(rating ?? 0)));
  return `${'⭐'.repeat(safe)}${'☆'.repeat(5 - safe)}`;
}

function renderRatingSummary(ratingAverage: number | null | undefined, ratingCount: number | null | undefined) {
  const total = ratingCount ?? 0;
  if (typeof ratingAverage === 'number' && total > 0) {
    return `⭐ ${ratingAverage.toFixed(1)} (${total} review${total === 1 ? '' : 's'})`;
  }

  return '⭐ New';
}

function normalizeJobReview(review: JobReview): JobReview {
  return {
    ...review,
    job_id: review.job_id ?? review.jobId ?? 0,
    job_title: review.job_title ?? review.jobTitle,
    reviewer_user_id: review.reviewer_user_id ?? review.reviewerUserId ?? 0,
    reviewee_user_id: review.reviewee_user_id ?? review.revieweeUserId ?? 0,
    reviewer_type: review.reviewer_type ?? review.reviewerType,
    created_at: review.created_at ?? review.createdAt ?? new Date().toISOString(),
    updated_at: review.updated_at ?? review.updatedAt ?? review.created_at ?? review.createdAt ?? new Date().toISOString(),
    reviewer_name: review.reviewer_name ?? review.reviewerName,
    reviewee_name: review.reviewee_name ?? review.revieweeName,
  };
}

function formatLegacyReviewEntry(entry: unknown) {
  if (typeof entry === 'string') return entry;
  if (!entry || typeof entry !== 'object') return null;
  const item = entry as Record<string, unknown>;
  const rating = typeof item.rating === 'number' ? item.rating : null;
  const comment = typeof item.comment === 'string' ? item.comment : '';
  const reviewer = typeof item.reviewerName === 'string'
    ? item.reviewerName
    : typeof item.reviewer_name === 'string'
      ? item.reviewer_name
      : 'Reviewer';
  const createdAt = typeof item.createdAt === 'string'
    ? item.createdAt
    : typeof item.created_at === 'string'
      ? item.created_at
      : null;

  const details = [
    rating ? `${renderStars(rating)} (${rating}/5)` : null,
    comment || null,
    `By: ${reviewer}`,
    createdAt ? `Date: ${fmtDateTime(createdAt)}` : null,
  ].filter(Boolean);

  return details.join(' • ');
}

function prettyApplicationStatus(status: ApplicationStatus) {
  switch (status) {
    case 'RESPONSE_AWAITED':
      return 'Awaiting response';
    case 'ACCEPTED':
      return 'Accepted';
    case 'COMPLETED':
      return 'Completed';
    case 'REJECTED':
      return 'Rejected';
    case 'NO_SHOW':
      return 'No show';
    default:
      return 'Applied';
  }
}

function pillClass(status: string) {
  switch (status) {
    case 'APPLIED':
    case 'RESPONSE_AWAITED':
      return 'pill pill-pending';
    case 'ACCEPTED':
    case 'COMPLETED':
      return 'pill pill-confirmed';
    case 'REJECTED':
    case 'NO_SHOW':
      return 'pill pill-rejected';
    case 'OPEN': return 'pill pill-open';
    case 'IN_SELECTION': return 'pill pill-pending';
    case 'FILLED': return 'pill pill-filled';
    default: return 'pill pill-neutral';
  }
}

function getInitial(name?: string | null) {
  const safe = (name ?? '').trim();
  return (safe.charAt(0) || '?').toUpperCase();
}

function buildResumeFilename(name?: string | null) {
  const safeName = (name ?? 'candidate')
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/(^-|-$)/g, '');

  return `${safeName || 'candidate'}-resume.pdf`;
}

function normalizeResumeUrl(value?: string | null): string | null {
  const raw = value?.trim();
  if (!raw) return null;

  const isAlreadyUrl = /^(data:|https?:|blob:|file:|\/)/i.test(raw);
  if (isAlreadyUrl) return raw;

  const compact = raw.replaceAll(/\s+/g, '');
  const isLikelyBase64 = /^[A-Za-z0-9+/=]+$/.test(compact) && compact.length > 128;
  if (isLikelyBase64) {
    return `data:application/pdf;base64,${compact}`;
  }

  return raw;
}

function toDateTimeLocalValue(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  const tzOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Unable to read file'));
    reader.readAsDataURL(file);
  });
}

function isJwtExpired(token: string): boolean {
  try {
    const [, payload] = token.split('.');
    if (!payload) return true;
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = JSON.parse(globalThis.atob(padded)) as { exp?: number };
    if (typeof decoded.exp !== 'number') return true;
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
}

function handleModalOverlayKeyDown(event: React.KeyboardEvent<HTMLDivElement>, onClose: () => void) {
  if (event.key === 'Escape') {
    event.preventDefault();
    onClose();
  }
}

const PRESET_SKILLS = [
  'React',
  'TypeScript',
  'Node.js',
  'JavaScript',
  'SQL',
  'UI/UX',
  'QA',
  'DevOps',
  'Project Management',
];

const SKILL_ENTRY_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9 +.#&()/-]{0,49}$/;
const INVALID_SKILL_DELIMITER_PATTERN = /[;|\n\t]/;

function parseAndValidateSkillsInput(value: string): { skills: string[]; error: string | null } {
  const raw = value.trim();
  if (!raw) return { skills: [], error: null };

  if (INVALID_SKILL_DELIMITER_PATTERN.test(value)) {
    return { skills: [], error: 'Use commas to separate skills (example: React, Node.js, TypeScript).' };
  }

  const parts = value.split(',');
  if (parts.some((part) => part.trim().length === 0)) {
    return { skills: [], error: 'Remove empty values. Avoid leading, trailing, or double commas.' };
  }

  const deduped = new Map<string, string>();
  for (const part of parts) {
    const skill = part.trim();
    if (!SKILL_ENTRY_PATTERN.test(skill)) {
      return {
        skills: [],
        error: `Invalid skill format: "${skill}". Use letters, numbers, spaces, and . # & ( ) / -`,
      };
    }

    const key = skill.toLowerCase();
    if (!deduped.has(key)) {
      deduped.set(key, skill);
    }
  }

  return { skills: Array.from(deduped.values()), error: null };
}

const BEST_MATCH_TOKEN_GROUPS = [
  ['mow', 'mowing', 'mowed', 'lawn', 'yard', 'backyard', 'grass', 'landscaping', 'weed', 'weeding', 'yardwork'],
  ['paint', 'painting', 'painter', 'painted'],
  ['clean', 'cleaning', 'cleaner', 'housekeeping', 'janitor'],
  ['deliver', 'delivery', 'driver', 'driving', 'courier'],
  ['plumb', 'plumber', 'plumbing'],
  ['electric', 'electrical', 'electrician'],
  ['carpenter', 'carpentry', 'woodwork'],
  ['react', 'reactjs'],
  ['node', 'nodejs', 'node.js'],
  ['typescript', 'ts'],
  ['javascript', 'js'],
] as const;

const BEST_MATCH_DOMAIN_CLUSTERS: Record<string, string[]> = {
  lawncare: ['mow', 'mowing', 'mowed', 'lawn', 'yard', 'backyard', 'grass', 'weed', 'weeding', 'landscaping', 'gardening', 'garden'],
  cleaning: ['clean', 'cleaning', 'cleaner', 'housekeeping', 'janitor'],
  delivery: ['deliver', 'delivery', 'driver', 'driving', 'courier', 'logistics'],
  trades: ['plumb', 'plumber', 'plumbing', 'electric', 'electrical', 'electrician', 'carpenter', 'carpentry', 'construction', 'hvac', 'technician'],
  tech: ['react', 'reactjs', 'node', 'nodejs', 'node.js', 'typescript', 'javascript', 'backend', 'frontend', 'software', 'developer', 'engineer', 'api', 'database'],
};

function normalizeBestMatchToken(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replaceAll(/[^a-z0-9]/g, '')
    .replaceAll(/(ing|ers|er|ed|es|s)$/g, '');
}

function expandBestMatchToken(token: string): Set<string> {
  const expanded = new Set<string>([token]);
  BEST_MATCH_TOKEN_GROUPS.forEach((group) => {
    if (group.some((item) => normalizeBestMatchToken(item) === token)) {
      group.forEach((item) => expanded.add(normalizeBestMatchToken(item)));
    }
  });
  return expanded;
}

function buildBestMatchTokenSet(parts: Array<string | null | undefined>): Set<string> {
  const tokens = new Set<string>();
  for (const part of parts) {
    if (!part) continue;
    const raw = part.toLowerCase().trim();
    if (!raw) continue;

    const phraseToken = normalizeBestMatchToken(raw);
    if (phraseToken) {
      expandBestMatchToken(phraseToken).forEach((token) => tokens.add(token));
    }

    raw
      .split(/[^a-z0-9]+/g)
      .map((segment) => normalizeBestMatchToken(segment))
      .filter(Boolean)
      .forEach((token) => {
        expandBestMatchToken(token).forEach((expandedToken) => tokens.add(expandedToken));
      });
  }
  return tokens;
}

function classifyPrimaryBestMatchDomain(tokens: Set<string>): string | null {
  let bestDomain: string | null = null;
  let bestScore = 0;

  Object.entries(BEST_MATCH_DOMAIN_CLUSTERS).forEach(([domain, rawTokens]) => {
    const clusterTokens = rawTokens.map((token) => normalizeBestMatchToken(token));
    const score = clusterTokens.reduce((count, token) => count + (tokens.has(token) ? 1 : 0), 0);
    if (score > bestScore) {
      bestScore = score;
      bestDomain = domain;
    }
  });

  return bestScore > 0 ? bestDomain : null;
}

function normalizeJobInputText(input: string): string {
  return input
    .replaceAll(/\bsocial\s+medial\b/gi, 'social media')
    .replaceAll(/\bsocial\s+meida\b/gi, 'social media')
    .replaceAll(/\bsocal\s+media\b/gi, 'social media')
    .replaceAll(/\bmarkting\b/gi, 'marketing')
    .replaceAll(/\bmarketting\b/gi, 'marketing')
    .replaceAll(/\bpromtion\b/gi, 'promotion')
    .replaceAll(/\bcontant\b/gi, 'content')
    .replaceAll(/\s+/g, ' ')
    .trim();
}

function buildStrictSkillTokenSet(parts: Array<string | null | undefined>): Set<string> {
  const tokens = new Set<string>();
  parts.forEach((part) => {
    if (!part) return;
    part
      .toLowerCase()
      .split(/[^a-z0-9]+/g)
      .map((segment) => normalizeBestMatchToken(segment))
      .filter(Boolean)
      .forEach((token) => tokens.add(token));
  });
  return tokens;
}

function inferCategoryFromText(input: string): string {
  const lower = normalizeJobInputText(input).toLowerCase();
  if (/(plumb|electric|electrcian|hvac|repair|technician)/.test(lower)) return 'Skilled Trades';
  if (/(restaurant|fast\s*food|mcdonald'?s|wendy'?s|chipotle|panda\s*express|kitchen\s+crew|prep\s+cook|dishwasher|server|drive[-\s]?thru|barista|takeout|food\s*truck|catering|boba|coffee\s*shop)/.test(lower)) {
    return 'Hospitality & Food Service';
  }
  if (/(costco|retail|warehouse|forklift|inventory|stocker|wholesale|food\s*court|membership|cashier|electronics\s+sales)/.test(lower)) {
    return 'Retail & Warehouse';
  }
  if (/(renovat|remodel|home improvement|home renovation)/.test(lower)) return 'Construction';
  if (/(deliver|driver|logistics|warehouse)/.test(lower)) return 'Logistics';
  if (/(nurse|care|medical|health)/.test(lower)) return 'Healthcare';
  if (/(marketing|market(?:ing)?\s+person|social\s+med(?:ia|ial)|seo|campaign|brand|growth\s+marketing|digital\s+marketing)/.test(lower)) return 'Marketing';
  if (/(clean|janitor|housekeeping)/.test(lower)) return 'Cleaning';
  if (/(developer|engineer|react|node|typescript|software)/.test(lower)) return 'Technology';
  if (/(construction|carpenter|labor)/.test(lower)) return 'Construction';
  return '';
}

function extractDateTimeLocalFromText(input: string): string {
  const explicitDate = /\b(\d{4}-\d{2}-\d{2})(?:[ T](\d{1,2}:\d{2}))?/.exec(input);
  if (explicitDate) {
    const datePart = explicitDate[1];
    const timePart = explicitDate[2] ?? '09:00';
    return `${datePart}T${timePart}`;
  }

  const parsed = chrono.parse(input, new Date(), { forwardDate: true });
  const first = parsed[0]?.start?.date();
  return normalizeLocalDateTime(first?.toISOString() ?? '');
}

function normalizeLocalDateTime(dateLike: string): string {
  const parsed = new Date(dateLike);
  if (Number.isNaN(parsed.getTime())) return '';
  const tzOffsetMs = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

function extractDateRangeFromText(input: string): { startAt?: string; endAt?: string } {
  const extractLabeledDateTime = (source: string, labelPattern: RegExp): string | undefined => {
    const lineMatch = labelPattern.exec(source);
    if (!lineMatch?.[1]) return undefined;
    const raw = lineMatch[1].trim();
    if (!raw) return undefined;
    const parsed = chrono.parseDate(raw, new Date(), { forwardDate: true });
    if (!parsed) return undefined;
    return normalizeLocalDateTime(parsed.toISOString()) || undefined;
  };

  const labeledStart = extractLabeledDateTime(
    input,
    /\bstart\s*date(?:\s*&\s*time|\s*time)?\s*[:-]\s*([^\n\r]+)/i,
  );
  const labeledEnd = extractLabeledDateTime(
    input,
    /\bend\s*date(?:\s*&\s*time|\s*time)?\s*[:-]\s*([^\n\r]+)/i,
  );
  if (labeledStart || labeledEnd) {
    const [normalizedStart, normalizedEnd] = labeledStart && labeledEnd && labeledStart > labeledEnd
      ? [labeledEnd, labeledStart]
      : [labeledStart, labeledEnd];

    return {
      startAt: normalizedStart,
      endAt: normalizedEnd,
    };
  }

  const parsed = chrono.parse(input, new Date(), { forwardDate: true });
  const ranged = parsed.find((result) => result.start && result.end);
  if (ranged?.start && ranged.end) {
    const startAt = normalizeLocalDateTime(ranged.start.date().toISOString()) || undefined;
    const endAt = normalizeLocalDateTime(ranged.end.date().toISOString()) || undefined;
    const [normalizedStart, normalizedEnd] = startAt && endAt && startAt > endAt
      ? [endAt, startAt]
      : [startAt, endAt];

    return {
      startAt: normalizedStart,
      endAt: normalizedEnd,
    };
  }

  const first = parsed[0]?.start?.date();
  const second = parsed[1]?.start?.date();
  if (first || second) {
    const startAt = first ? normalizeLocalDateTime(first.toISOString()) || undefined : undefined;
    const endAt = second ? normalizeLocalDateTime(second.toISOString()) || undefined : undefined;
    const [normalizedStart, normalizedEnd] = startAt && endAt && startAt > endAt
      ? [endAt, startAt]
      : [startAt, endAt];

    return {
      startAt: normalizedStart,
      endAt: normalizedEnd,
    };
  }

  const fallbackStart = extractDateTimeLocalFromText(input);
  return {
    startAt: fallbackStart || undefined,
  };
}

function inferSkillsFromText(input: string): string[] {
  const lower = normalizeJobInputText(input).toLowerCase();
  const detected = new Set<string>();

  const explicitSkills = /\b(?:required\s*skills?|skill\s*set|skillset)\s*[:-]\s*([^\n\r]+)/i.exec(input)?.[1];
  if (explicitSkills) {
    const parsed = parseAndValidateSkillsInput(explicitSkills);
    if (!parsed.error && parsed.skills.length) {
      parsed.skills.forEach((skill) => detected.add(skill));
    }
  }

  const addIf = (pattern: RegExp, skill: string) => {
    if (pattern.test(lower)) detected.add(skill);
  };

  addIf(/\bplumb(?:er|ing)?\b/, 'plumbing');
  addIf(/\belectr(?:ic(?:ian|al)?|cian)\b/, 'electrical');
  if (/\b(?:ceiling\s+)?fan\b/.test(lower) && /\b(?:hang|install|mount|fix|replace|wire)\b/.test(lower)) {
    detected.add('ceiling fan installation');
    detected.add('safe electrical wiring');
  }
  addIf(/\bhvac\b/, 'hvac');
  addIf(/\bcarpent(?:er|ry)\b/, 'carpentry');
  addIf(/\bpaint(?:er|ing)?\b/, 'painting');
  if (/\bpaint(?:er|ing)?\b/.test(lower) && /\b(room|interior|wall|house|home)\b/.test(lower)) {
    detected.add('interior painting');
    detected.add('surface preparation');
  }
  addIf(/\bclean(?:er|ing)?\b/, 'cleaning');
  addIf(/\bdelivery|driver\b/, 'driving');
  addIf(/\bheavy\s+lifting\b|\blift(?:ing)?\b/, 'heavy lifting');
  addIf(/\bphysically\s+fit\b|\bphysical\s+fitness\b/, 'physical fitness');
  addIf(/\breliable\b|\breliability\b/, 'reliability');
  addIf(/\bmoving\s+truck\b|\bloading\b|\bunloading\b/, 'moving assistance');
  addIf(/\bschedul(?:e|ing)\b/, 'scheduling');
  addIf(/\bproject\s+coordinat(?:ion|or)\b/, 'project coordination');
  addIf(/\bcommunicat(?:ion|e|ing)\b/, 'communication');
  if (/\b(home\s+renovat(?:ion|e)|renovat(?:ion|e)|remodel(?:ing)?)\b/.test(lower)) {
    detected.add('home-renovation knowledge');
  }
  if (/\b(?:furniture|sofa|bed|appliance|boxes?)\b/.test(lower) && /\b(load|moving|truck|lift)/.test(lower)) {
    detected.add('furniture loading');
  }
  addIf(/\breact\b/, 'react');
  addIf(/\bnode(?:\.js)?\b/, 'node.js');
  addIf(/\btypescript\b/, 'typescript');
  addIf(/\bjavascript\b/, 'javascript');
  addIf(/\bsql\b|\bmysql\b|\bpostgres(?:ql)?\b|\bquery\b|\bdatabase\b/, 'sql');
  addIf(/\betl\b|\bdata\s*pipeline\b|\bdata\s*warehouse\b/, 'etl');
  addIf(/\bpower\s*bi\b|\btableau\b|\breport(?:ing)?\b|\bdashboard\b/, 'reporting');
  addIf(/\bdata\s*analyst\b|\banalytics\b|\bbi\b/, 'data analysis');
  addIf(/\bmarketing\b|\bdigital\s+marketing\b/, 'digital marketing');
  addIf(/\bsocial\s+med(?:ia|ial)\b/, 'social media marketing');
  addIf(/\bseo\b|\bsearch\s+engine\s+optimization\b/, 'seo');
  addIf(/\bcampaign\b|\bad\s+campaign\b/, 'campaign management');
  addIf(/\bcontent\b|\bcopywriting\b/, 'content creation');
  addIf(/\blead\s+generation\b|\bacquisition\b/, 'lead generation');
  addIf(/\bbrand(?:ing)?\b/, 'brand marketing');
  addIf(/\bfood\s*safety\b|\bservsafe\b|\bhaccp\b/, 'food safety compliance');
  addIf(/\bcashier\b|\bpos\b/, 'pos handling');
  addIf(/\bcustomer\s+service\b|\bguest\s+service\b/, 'customer service');
  addIf(/\bprep\s*cook\b|\bfood\s*prep\b/, 'food preparation');
  addIf(/\bportion(?:ing)?\b/, 'portion control');
  addIf(/\bdrive[-\s]?thru\b/, 'drive-thru operations');
  addIf(/\bdishwasher\b/, 'dishwashing and sanitation');
  addIf(/\bbarista\b|\bespresso\b/, 'barista operations');
  addIf(/\bforklift\b/, 'forklift operation');
  addIf(/\binventory\b|\bstock(?:ing|er)?\b|\bscanning\b/, 'inventory management');
  addIf(/\bwarehouse\b/, 'warehouse operations');
  addIf(/\belectronics\s+sales\b/, 'electronics product support');
  addIf(/\bphotograph(?:er|y)?\b|\bfood\s+photos?\b/, 'food photography');
  addIf(/\bpos\b.*\b(setup|install|configure|configuration)\b|\bpoint\s+of\s+sale\b/, 'pos setup');
  addIf(/\bhandyman\b/, 'general maintenance');
  addIf(/\bkitchen\s+exhaust\b/, 'kitchen exhaust cleaning');

  return Array.from(detected);
}

function inferTitleFromText(input: string): string {
  const lower = normalizeJobInputText(input).toLowerCase();
  const isRestaurantContext = /\b(restaurant|fast\s*food|mcdonald'?s|wendy'?s|chipotle|panda\s*express|burger|boba|coffee\s*shop|takeout|food\s*truck|kitchen)\b/.test(lower);

  if (/\bsocial\s+med(?:ia|ial)\b/.test(lower)) {
    const isPartTime = /\b(part[-\s]?time|\d+\s*(?:hours?|hrs?)\s*(?:per|\/)\s*week|weekly)\b/.test(lower);
    return isPartTime ? 'Part-Time Social Media Specialist' : 'Social Media Specialist';
  }

  if (/\b(tiktok|instagram)\b/.test(lower) && isRestaurantContext) return 'Restaurant Social Media Manager';
  if (/\bsocial\s+media\b/.test(lower) && isRestaurantContext) return 'Restaurant Social Media Manager';
  if (/\bkitchen\s+exhaust\s+cleaner\b/.test(lower)) return 'Kitchen Exhaust Cleaner';
  if (/\blicensed\b/.test(lower) && /\belectr(?:ic(?:ian|al)?|cian)\b/.test(lower) && /\b(restaurant|kitchen\s+equipment)\b/.test(lower)) {
    return 'Licensed Commercial Electrician';
  }
  if (/\bphotograph(?:er|y)?\b/.test(lower) && /\b(menu|food\s+photos?)\b/.test(lower)) return 'Food Photographer';
  if (/\bhandyman\b/.test(lower) && /\b(restaurant|seating|tables?)\b/.test(lower)) return 'Restaurant Handyman';
  if (/\bpos\b/.test(lower) && /\b(setup|install|configuration|expert)\b/.test(lower)) return 'POS System Setup Specialist';
  if (/\bcatering\s+assistant\b/.test(lower)) return 'Catering Assistant';
  if (/\bfood\s*truck\b/.test(lower) && /\bhelper\b/.test(lower)) return 'Food Truck Helper';
  if (/\brestaurant\s+manager\b/.test(lower)) return 'Restaurant Manager';
  if (/\bdelivery\s+coordinator\b/.test(lower) && /\b(takeout|food)\b/.test(lower)) return 'Food Delivery Coordinator';
  if (/\bdrive[-\s]?thru\b/.test(lower)) return 'Drive-Thru Associate';
  if (/\bdishwasher\b/.test(lower)) return 'Restaurant Dishwasher';
  if (/\bprep\s*cook\b/.test(lower)) return 'Prep Cook';
  if (/\bkitchen\s+crew\b/.test(lower)) return 'Kitchen Crew Member';
  if (/\bbarista\b/.test(lower)) return 'Barista';
  if (/\bserver\b/.test(lower) && isRestaurantContext) return 'Restaurant Server';
  if (/\bcashier\b/.test(lower) && isRestaurantContext) return 'Restaurant Cashier';
  if (/\bwarehouse\s+associate\b/.test(lower)) return 'Warehouse Associate';
  if (/\bforklift\s+operator\b/.test(lower)) return 'Forklift Operator';
  if (/\bmembership\b/.test(lower) && /\bcustomer\s+service\b/.test(lower)) return 'Membership Customer Service Associate';
  if (/\bgrocery\s+stocker\b/.test(lower)) return 'Grocery Stocker';
  if (/\bcashier\b/.test(lower) && /\b(retail|wholesale)\b/.test(lower)) return 'Retail Cashier';
  if (/\bfood\s+court\s+worker\b/.test(lower)) return 'Food Court Worker';
  if (/\binventory\s+assistant\b/.test(lower)) return 'Inventory Assistant';
  if (/\bretail\s+supervisor\b/.test(lower)) return 'Retail Supervisor';
  if (/\bbakery\s+assistant\b/.test(lower)) return 'Bakery Assistant';
  if (/\belectronics\s+sales\s+associate\b/.test(lower)) return 'Electronics Sales Associate';

  if (/\b(marketing|social\s+media|seo|campaign|brand|growth\s+marketing|digital\s+marketing)\b/.test(lower)) {
    const isPartTime = /\b(part[-\s]?time|\d+\s*(?:hours?|hrs?)\s*(?:per|\/)\s*week|weekly)\b/.test(lower);
    return isPartTime ? 'Part-Time Marketing Specialist' : 'Marketing Specialist';
  }
  if (/\belectr(?:ic(?:ian|al)?|cian)\b/.test(lower) && /\b(?:ceiling\s+)?fan\b/.test(lower)) {
    return 'Residential Electrician';
  }
  if (/\b(?:moving\s+truck|load(?:ing)?|heavy\s+lifting|furniture|boxes?)\b/.test(lower)) {
    return 'Moving Help and Loading Assistance';
  }
  if (/\bpaint(?:er|ing)?\b/.test(lower) && /\broom\b/.test(lower)) return 'Room Painting Job';
  if (/\bpaint(?:er|ing)?\b/.test(lower)) return 'Painting Job';
  if (/\belectr(?:ic(?:ian|al)?|cian)\b/.test(lower)) return 'Electrical Job';
  if (/\bplumb(?:er|ing)?\b/.test(lower)) return 'Plumbing Job';

  const roleMatch = /\b(?:need|looking for|hire|hiring|create|post)\s+(?:someone|a|an)?\s*(?:to\s+help\s+with\s+)?([^,.]+?)(?:\s+in\s+|\s+from\s+|\.|,|$)/i.exec(input);
  const explicitJob = /\b([a-z][a-z\- ]{1,40})\s+job\b/i.exec(input);
  const seed = explicitJob?.[1] ?? roleMatch?.[1] ?? '';
  const cleaned = seed
    .replace(/\b(?:someone|help|with|a|an|the)\b/gi, ' ')
    .replace(/^to\s+/i, '')
    .replace(/\bin\s+my\s+(?:house|home)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return '';
  const normalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  return /job$/i.test(normalized) ? normalized : `${normalized} Job`;
}

function inferLocationFromText(input: string): string | undefined {
  const normalizedInput = normalizeJobInputText(input);
  if (/\b(remote|work\s*from\s*home|wfh|remote[-\s]?only)\b/i.test(normalizedInput)) {
    return 'Remote';
  }

  const cleanupLocation = (value: string): string => {
    return value
      .replace(/\([^)]*\)/g, '')
      .replace(/[.,]+$/g, '')
      .trim();
  };

  const explicitLocation = /\b(?:job\s*)?location\s*[:-]\s*([^\n\r]+)/i.exec(normalizedInput)?.[1];
  if (explicitLocation) {
    const normalized = cleanupLocation(explicitLocation);
    if (normalized) return normalized;
  }

  const candidates = Array.from(normalizedInput.matchAll(/\b(?:in|at)\s+([a-zA-Z][a-zA-Z .'-]{1,60})/gi))
    .map((match) => match[1]?.trim())
    .filter((value): value is string => Boolean(value));

  const cleanedCandidates = candidates
    .map((candidate) => {
      let cleaned = candidate
        .replace(/[.,]+$/g, '')
        .split(/\b(?:between|from|on|for|budget|rate|pay|cost|please|starting|beginning|total)\b/i)[0]
        ?.trim() ?? '';

      if (/\bin\b/i.test(cleaned)) {
        const nested = cleaned.split(/\bin\b/i).pop()?.trim();
        if (nested) cleaned = nested;
      }

      cleaned = cleaned
        .replace(/^(?:my|our)\s+house\s+in\s+/i, '')
        .replace(/^(?:my|our)\s+home\s+in\s+/i, '')
        .replace(/^(?:my|our)\s+(?:house|home)\b/i, '')
        .trim();

      cleaned = cleanupLocation(cleaned);
      return cleaned;
    })
    .filter((value) => value.length >= 2 && value.length <= 40 && !/\d/.test(value))
    .filter((value) => !/\b(?:moving truck|truck|boxes?|furniture|job|task)\b/i.test(value))
    .filter((value) => !/^\s*(?:a|an)?\s*(?:day|days|week|weeks|month|months)\s*$/i.test(value))
    .filter((value) => !/^(?:for\s+)?\d+\s*(?:day|days|week|weeks|month|months)\s*$/i.test(value));

  if (!cleanedCandidates.length) return undefined;
  return cleanedCandidates.at(-1);
}

function compactSuggestion(suggestion: ParsedJobSuggestion): ParsedJobSuggestion {
  const compact: ParsedJobSuggestion = {};

  if (suggestion.title?.trim()) compact.title = suggestion.title.trim();
  if (suggestion.companyName?.trim()) compact.companyName = suggestion.companyName.trim();
  if (suggestion.category?.trim()) compact.category = suggestion.category.trim();
  if (suggestion.location?.trim()) compact.location = suggestion.location.trim();
  if (suggestion.description?.trim()) compact.description = suggestion.description;
  if (Array.isArray(suggestion.requiredSkills) && suggestion.requiredSkills.length) compact.requiredSkills = suggestion.requiredSkills;
  if (suggestion.rateType) compact.rateType = suggestion.rateType;
  if (typeof suggestion.rateAmount === 'number' && suggestion.rateAmount > 0) compact.rateAmount = suggestion.rateAmount;
  if (suggestion.startAt) compact.startAt = suggestion.startAt;
  if (suggestion.endAt) compact.endAt = suggestion.endAt;
  if (typeof suggestion.workerSlots === 'number' && suggestion.workerSlots > 0) compact.workerSlots = suggestion.workerSlots;

  return compact;
}

function normalizeSuggestionForInput(input: string, merged: ParsedJobSuggestion, fallback: ParsedJobSuggestion): ParsedJobSuggestion {
  const normalized: ParsedJobSuggestion = {
    ...compactSuggestion(fallback),
    ...compactSuggestion(merged),
  };

  const lower = input.toLowerCase();
  const fixedBudgetHint = /\bbudget\b[^.\n]*\btotal\b|\btotal\b[^.\n]*\bbudget\b|\bflat\b|\ball[-\s]?in\b|\bfixed\b/.test(lower);
  if (fixedBudgetHint && typeof normalized.rateAmount === 'number' && normalized.rateAmount > 0) {
    normalized.rateType = 'fixed';
  }

  if (!normalized.title && fallback.title) normalized.title = fallback.title;
  if (!normalized.location && fallback.location) normalized.location = fallback.location;
  if (!(normalized.requiredSkills?.length) && fallback.requiredSkills?.length) {
    normalized.requiredSkills = fallback.requiredSkills;
  }

  return normalized;
}

function buildConciseJobTitle(title: string | undefined, input: string): string {
  const fallback = inferTitleFromText(input) || 'General Service Task';
  const seed = (title?.trim() || fallback).replaceAll(/\s+/g, ' ').trim();
  const words = seed.split(/\s+/).filter(Boolean);
  if (seed.length <= 72 && words.length <= 8) return seed;
  const clipped = words.slice(0, 8).join(' ').trim();
  return clipped || fallback;
}

function toTitleCaseWords(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      if (/^[A-Z]{2,}$/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

function normalizeDisplayRoleTitle(rawTitle: string, input: string): string {
  if (/\belectr(?:ic(?:ian|al)?|cian)\b/i.test(input) && /\b(?:ceiling\s+)?fan\b/i.test(input)) {
    return 'Residential Electrician';
  }
  const fallback = inferTitleFromText(input) || 'General Service Task';
  const cleaned = rawTitle
    .replace(/^\s*(?:create|post|need|looking\s+for|hire|hiring)\s+/i, '')
    .replace(/\bfor\s+\d+\s*(?:day|days|week|weeks|month|months)\b/ig, '')
    .replace(/\b\d+\s*(?:hours?|hrs?)\s*(?:per|\/)\s*(?:day|week|month)\b/ig, '')
    .replace(/\bstarting\s+[^,.;\n]+/ig, '')
    .replace(/\$\s*\d+(?:\.\d+)?(?:\s*(?:per|\/)\s*(?:hour|hr|day|week|month))?/ig, '')
    .replace(/\bmarking\b/ig, 'marketing')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/\s+job\s*$/i, '')
    .replaceAll(/\s+/g, ' ')
    .trim();

  const roleSeed = cleaned || fallback.replace(/\s+job\s*$/i, '').trim();
  const normalized = toTitleCaseWords(roleSeed);
  return normalized || 'General Service Task';
}

function extractDurationPhrase(input: string): string | undefined {
  return /\b(\d+\s*(?:day|days|week|weeks|month|months))\b/i.exec(input)?.[1];
}

function extractStartPhrase(input: string): string | undefined {
  const start = /\bstarting\s+([^,.;\n]+)/i.exec(input)?.[1]?.trim();
  if (start) return `starting ${start}`;
  if (/\btomorrow\b/i.test(input)) return 'starting tomorrow';
  return undefined;
}

function extractUrgencyPhrase(input: string): string | undefined {
  if (/\b(immediately|urgent|urgently|asap|right\s+away|today)\b/i.test(input)) {
    return 'as soon as possible';
  }
  return undefined;
}

function buildRoleGoal(input: string, location: string, lowered: string): string {
  const duration = extractDurationPhrase(input);
  const startPhrase = extractStartPhrase(input);
  const urgency = extractUrgencyPhrase(input);
  const hourlyRate = /\$\s*([\d.]+)\s*(?:per\s*hour|\/\s*hr|hourly)?/i.exec(input)?.[1];
  const hasKnownLocation = !/location to be confirmed/i.test(location);
  const isRestaurant = /\b(restaurant|fast\s*food|kitchen\s+crew|prep\s*cook|dishwasher|server|barista|drive[-\s]?thru|takeout|food\s*truck|catering)\b/.test(lowered);
  const isRetailWarehouse = /\b(retail|warehouse|forklift|inventory|stocker|costco|wholesale|food\s*court|cashier|electronics\s+sales)\b/.test(lowered);
  const isMarketingWork = /\b(marketing|social\s+media|tiktok|instagram|seo|campaign|brand|lead\s+generation)\b/.test(lowered);

  let deliverable = 'the agreed project deliverables to the client standard';
  if (/web\s+developer|frontend|react|website|web\s*app/i.test(lowered)) {
    deliverable = 'a clean, responsive web solution that meets the client requirements';
  } else if (/backend|api|server/i.test(lowered)) {
    deliverable = 'a reliable backend implementation that meets the client requirements';
  } else if (/sql|database|data/i.test(lowered)) {
    deliverable = 'an accurate, production-ready data solution for the client';
  } else if (/\bkitchen\s+exhaust\s+cleaner\b/.test(lowered)) {
    return `Complete deep kitchen exhaust cleaning and degreasing${urgency ? ` ${urgency}` : ''}.`;
  } else if (/\bpos\b/.test(lowered) && /\bsetup|install|configuration\b/.test(lowered)) {
    return 'Deliver a fully configured POS setup with tested checkout workflow and staff-ready handoff.';
  } else if (/\bfood\s+photographer\b/.test(lowered)) {
    return 'Capture high-quality, menu-ready food images that improve customer appeal and online conversion.';
  } else if (/\blicensed\s+commercial\s+electrician\b/.test(lowered)) {
    return `Install kitchen electrical equipment safely and code-compliant${urgency ? ` ${urgency}` : ''}.`;
  } else if (/\belectr(?:ic(?:ian|al)?|cian)\b/.test(lowered) && /\b(?:ceiling\s+)?fan\b/.test(lowered)) {
    const urgencySuffix = urgency ? ` ${urgency}` : '';
    return `Complete safe ceiling fan installation${urgencySuffix}.`;
  } else if (isMarketingWork) {
    const brand = /\btaskway\b/i.test(input) ? 'TaskWay' : 'the business';
    const timeline = duration ? ` over ${duration}` : '';
    const start = startPhrase ? `, ${startPhrase}` : '';
    return `Increase awareness and user engagement for ${brand}${timeline}${start}.`;
  } else if (isRestaurant) {
    const urgencySuffix = urgency ? ` ${urgency}` : '';
    return `Support fast, accurate, and safe food-service operations${urgencySuffix}.`;
  } else if (isRetailWarehouse) {
    return 'Maintain smooth retail and warehouse operations with accurate stocking, service, and safe handling.';
  }

  const parts = [
    hasKnownLocation ? `Deliver ${deliverable} in ${location}` : `Deliver ${deliverable}`,
    duration ? `within ${duration}` : '',
    startPhrase ?? '',
    !startPhrase && urgency ? urgency : '',
    hourlyRate ? `at $${hourlyRate}/hour` : '',
  ].filter(Boolean);

  return `${parts.join(', ')}.`;
}

type StructuredJobDescription = {
  normalized: string;
  role: string | null;
  keyTasks: string[];
  mustHaves: string[];
  goal: string | null;
  hasStructure: boolean;
};

function normalizeStructuredJobDescriptionText(description: string): string {
  const source = description.replaceAll(/\r\n?/g, '\n').trim();
  if (!source) return '';

  return source
    .replace(/\bCurrent\s+Role\s*:/gi, 'Role:')
    .replace(/\s+(Role\s*:|Key\s*Tasks\s*:|Must[-\s]?Haves\s*:|Goal\s*:)/gi, '\n$1')
    .replace(/(Key\s*Tasks\s*:|Must[-\s]?Haves\s*:)\s*-\s*/gi, '$1\n- ')
    .replace(/\s+-\s+/g, '\n- ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function parseStructuredJobDescription(description: string | null | undefined): StructuredJobDescription {
  const normalized = normalizeStructuredJobDescriptionText(description ?? '');
  if (!normalized) {
    return {
      normalized: '',
      role: null,
      keyTasks: [],
      mustHaves: [],
      goal: null,
      hasStructure: false,
    };
  }

  const lines = normalized
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  let role: string | null = null;
  let goal: string | null = null;
  const keyTasks: string[] = [];
  const mustHaves: string[] = [];
  let activeSection: 'keyTasks' | 'mustHaves' | null = null;

  const appendToActiveSection = (value: string) => {
    if (!value) return;
    const cleaned = value.replace(/^[-•]\s*/, '').trim();
    if (!cleaned) return;
    if (activeSection === 'keyTasks') keyTasks.push(cleaned);
    if (activeSection === 'mustHaves') mustHaves.push(cleaned);
  };

  lines.forEach((line) => {
    const roleMatch = /^Role\s*:\s*(.+)$/i.exec(line);
    if (roleMatch) {
      role = roleMatch[1].trim();
      activeSection = null;
      return;
    }

    const keyTasksMatch = /^Key\s*Tasks\s*:\s*(.*)$/i.exec(line);
    if (keyTasksMatch) {
      activeSection = 'keyTasks';
      appendToActiveSection(keyTasksMatch[1]);
      return;
    }

    const mustHavesMatch = /^Must[-\s]?Haves\s*:\s*(.*)$/i.exec(line);
    if (mustHavesMatch) {
      activeSection = 'mustHaves';
      appendToActiveSection(mustHavesMatch[1]);
      return;
    }

    const goalMatch = /^Goal\s*:\s*(.+)$/i.exec(line);
    if (goalMatch) {
      goal = goalMatch[1].trim();
      activeSection = null;
      return;
    }

    appendToActiveSection(line);
  });

  return {
    normalized,
    role,
    keyTasks,
    mustHaves,
    goal,
    hasStructure: Boolean(role || keyTasks.length || mustHaves.length || goal),
  };
}

function buildJobDescriptionCardPreview(description: string | null | undefined): string {
  const parsed = parseStructuredJobDescription(description);
  if (!parsed.normalized) return 'No description provided.';
  if (!parsed.hasStructure) return parsed.normalized;

  const previewParts = [
    parsed.role ? `Role: ${parsed.role}` : null,
    parsed.keyTasks[0] ? `Key Task: ${parsed.keyTasks[0]}` : null,
    parsed.goal ? `Goal: ${parsed.goal}` : null,
  ].filter(Boolean);

  return previewParts.join('\n');
}

function renderJobDescriptionContent(description: string | null | undefined, fallback = 'No description provided.') {
  const parsed = parseStructuredJobDescription(description);
  if (!parsed.normalized) {
    return <p className="detail-text">{fallback}</p>;
  }

  if (!parsed.hasStructure) {
    return <p className="detail-text description-preline">{parsed.normalized}</p>;
  }

  return (
    <div className="job-description-structured">
      {parsed.role && (
        <p className="detail-text job-description-role">
          <strong>Role:</strong> {parsed.role}
        </p>
      )}

      {parsed.keyTasks.length > 0 && (
        <div>
          <p className="detail-text job-description-heading">Key Tasks</p>
          <ul className="job-description-list">
            {parsed.keyTasks.map((task) => (
              <li key={`task-${task}`} className="detail-text">{task}</li>
            ))}
          </ul>
        </div>
      )}

      {parsed.mustHaves.length > 0 && (
        <div>
          <p className="detail-text job-description-heading">Must-Haves</p>
          <ul className="job-description-list">
            {parsed.mustHaves.map((item) => (
              <li key={`must-${item}`} className="detail-text">{item}</li>
            ))}
          </ul>
        </div>
      )}

      {parsed.goal && (
        <p className="detail-text job-description-goal">
          <strong>Goal:</strong> {parsed.goal}
        </p>
      )}
    </div>
  );
}

export function buildProfessionalJobDescription(input: string, suggestion: ParsedJobSuggestion): string {
  const normalizedInput = normalizeJobInputText(input);
  const baseDescription = suggestion.description?.trim() || input.trim();
  if (/\bCurrent\s+Role\s*:|\bRole\s*:|\bKey\s*Tasks\s*:|\bMust[-\s]?Haves\s*:|\bGoal\s*:/i.test(baseDescription)) {
    return normalizeStructuredJobDescriptionText(baseDescription);
  }

  const conciseTitle = buildConciseJobTitle(suggestion.title, normalizedInput);
  const title = normalizeDisplayRoleTitle(conciseTitle, normalizedInput);
  const category = suggestion.category?.trim() || inferCategoryFromText(normalizedInput) || 'General';
  const location = suggestion.location?.trim() || inferLocationFromText(normalizedInput) || 'Location to be confirmed';
  const skills = (suggestion.requiredSkills ?? inferSkillsFromText(normalizedInput)).slice(0, 6);
  const lowered = `${title} ${baseDescription}`.toLowerCase();
  const isRestaurant = /\b(restaurant|fast\s*food|mcdonald'?s|wendy'?s|chipotle|panda\s*express|kitchen\s+crew|prep\s*cook|dishwasher|server|barista|drive[-\s]?thru|takeout|food\s*truck|catering|boba|coffee\s*shop)\b/.test(lowered);
  const isRetailWarehouse = /\b(retail|warehouse|forklift|inventory|stocker|costco|wholesale|food\s*court|membership|cashier|electronics\s+sales)\b/.test(lowered);

  // Improved Key Tasks
  let inferredTasks: string[] = [];
  if (/\bkitchen\s+exhaust\s+cleaner\b/.test(lowered)) {
    inferredTasks = [
      'Degrease exhaust hoods, filters, and duct-access points to kitchen-standard cleanliness.',
      'Follow fire-safety procedures while handling grease and cleaning chemicals.',
      'Document completed cleaning and highlight any maintenance risks.',
    ];
  } else if (/\bpos\b/.test(lowered) && /\b(setup|install|configuration|expert)\b/.test(lowered)) {
    inferredTasks = [
      'Install and configure POS hardware, software, and payment settings.',
      'Test transactions, receipt printing, and reporting workflows end-to-end.',
      'Train staff on daily checkout, refunds, and basic troubleshooting.',
    ];
  } else if (/\bfood\s+photographer\b/.test(lowered)) {
    inferredTasks = [
      'Plan and execute food photo sessions for menu and social channels.',
      'Style dishes and lighting to highlight texture, freshness, and brand tone.',
      'Deliver edited, platform-ready assets for print and digital use.',
    ];
  } else if (/\brestaurant\s+handyman\b/.test(lowered)) {
    inferredTasks = [
      'Repair restaurant seating, tables, and minor fixtures safely.',
      'Inspect high-use furniture for stability and preventive fixes.',
      'Complete repairs with minimal disruption to service hours.',
    ];
  } else if (/\blicensed\s+commercial\s+electrician\b/.test(lowered)) {
    inferredTasks = [
      'Install and connect restaurant kitchen equipment to electrical supply.',
      'Verify load, grounding, and protection devices to code requirements.',
      'Run functional and safety tests before turnover.',
    ];
  } else if (/\belectr(?:ic(?:ian|al)?|cian)\b/.test(lowered) && /\b(?:ceiling\s+)?fan\b/.test(lowered)) {
    inferredTasks = [
      'Install and securely mount the ceiling fan.',
      'Verify wiring, electrical connections, and balance.',
      'Test operation and confirm safe performance before handoff.',
    ];
  } else if (/\b(marketing|social\s+media|seo|campaign|brand|lead\s+generation|digital\s+marketing)\b/.test(lowered)) {
    inferredTasks = [
      'Plan and execute weekly marketing activities aligned to campaign goals.',
      'Create social and/or content assets to improve visibility and engagement.',
      'Track performance metrics and optimize based on results.'
    ];
  } else if (isRestaurant) {
    inferredTasks = [
      'Support fast, accurate service during peak shift demand.',
      'Follow food safety, cleanliness, and station procedures consistently.',
      'Coordinate with team members to keep orders moving on time.',
    ];
  } else if (isRetailWarehouse) {
    inferredTasks = [
      'Handle stocking, inventory checks, and floor readiness for each shift.',
      'Assist customers with efficient, friendly in-store service.',
      'Follow safety procedures for equipment use and material handling.',
    ];
  } else if (/\breact|frontend|ui|typescript|javascript\b/.test(lowered)) {
    inferredTasks = [
      'Develop a responsive web application using React and TypeScript.',
      'Collaborate with stakeholders to deliver features on a tight timeline.',
      'Ensure code quality, maintainability, and cross-browser compatibility.'
    ];
  } else if (/\bnode|backend|api|server|integration\b/.test(lowered)) {
    inferredTasks = [
      'Design and implement backend APIs and integrations.',
      'Write clean, maintainable server-side code.',
      'Ensure reliability and performance of backend services.'
    ];
  } else if (/\bsql|database|query|etl|report|schema|data\b/.test(lowered)) {
    inferredTasks = [
      'Build and optimize SQL queries and data pipelines.',
      'Translate business requirements into actionable data insights.',
      'Maintain data quality and consistency.'
    ];
  } else if (/\bplumb|electr(?:ic|cian)|carpent|paint|clean|delivery|driver|technician\b/.test(lowered)) {
    inferredTasks = [
      'Perform on-site work safely and according to local standards.',
      'Use appropriate tools and materials to complete the requested service.',
      'Verify quality and functionality before job completion.'
    ];
  } else {
    inferredTasks = [
      'Assess the request scope and execute the required work professionally.',
      'Maintain clear, timely communication with the client throughout delivery.',
      'Complete the task to quality standards within the agreed timeline.'
    ];
  }

  // Improved Must-Haves
  let mustHaves: string[] = [];
  if (/\bkitchen\s+exhaust\s+cleaner\b/.test(lowered)) {
    mustHaves = [
      '- Experience with commercial kitchen exhaust and hood cleaning',
      '- Knowledge of fire-safety and grease-handling procedures',
      '- Ability to work efficiently during off-hours or urgent callouts',
    ];
  } else if (/\bpos\b/.test(lowered) && /\b(setup|install|configuration|expert)\b/.test(lowered)) {
    mustHaves = [
      '- Experience with POS setup, payment terminals, and receipt systems',
      '- Ability to troubleshoot connectivity, printer, and checkout issues',
      '- Clear training communication for non-technical staff',
    ];
  } else if (/\bfood\s+photographer\b/.test(lowered)) {
    mustHaves = [
      '- Portfolio demonstrating strong food/menu photography',
      '- Editing skills for color, lighting, and brand consistency',
      '- Reliable delivery of web-ready and print-ready files',
    ];
  } else if (/\brestaurant\s+handyman\b/.test(lowered)) {
    mustHaves = [
      '- Hands-on repair experience for furniture and fixtures',
      '- Safe tool handling and clean finish workmanship',
      '- Ability to complete repairs quickly during operational windows',
    ];
  } else if (/\blicensed\s+commercial\s+electrician\b/.test(lowered)) {
    mustHaves = [
      '- Active electrical license and commercial installation experience',
      '- Knowledge of code compliance for kitchen equipment circuits',
      '- Strong safety practices and clear commissioning documentation',
    ];
  } else if (/\belectr(?:ic(?:ian|al)?|cian)\b/.test(lowered) && /\b(?:ceiling\s+)?fan\b/.test(lowered)) {
    mustHaves = [
      '- Experience with residential electrical and ceiling fan installations',
      '- Proper electrical tools and strong safety practices',
      '- Ability to troubleshoot wiring and complete clean, secure mounting',
    ];
  } else if (/\b(marketing|social\s+media|seo|campaign|brand|digital\s+marketing)\b/.test(lowered)) {
    mustHaves = [
      '- Experience in digital, growth, or social media marketing',
      '- Ability to create campaign content and analyze performance metrics',
      '- Strong communication and ability to work independently',
    ];
  } else if (isRestaurant) {
    mustHaves = [
      '- Experience in restaurant, fast-food, or café shift operations',
      '- Strong food safety, sanitation, and customer service habits',
      '- Reliability during peak hours, weekends, or late-night shifts',
    ];
  } else if (isRetailWarehouse) {
    mustHaves = [
      '- Experience with retail or warehouse workflows and inventory accuracy',
      '- Customer service mindset with dependable shift performance',
      '- Safe material handling; forklift certification when applicable',
    ];
  } else if (/\bweb\s+developer|frontend|react|typescript|javascript\b/.test(lowered)) {
    mustHaves = [
      '- Proven experience building modern web applications',
      '- Strong JavaScript/TypeScript, HTML, and CSS fundamentals',
      '- Ability to deliver production-ready work on a short timeline',
    ];
  } else if (/\bnode|backend|api|server|integration\b/.test(lowered)) {
    mustHaves = [
      '- Experience building and maintaining APIs in production',
      '- Strong debugging, testing, and code quality practices',
      '- Clear communication and ownership of deliverables',
    ];
  } else if (skills.length) {
    mustHaves = [
      `- Demonstrated experience with ${skills.join(', ')}`,
      '- Ability to deliver quality work on a short timeline',
      '- Strong communication and accountability',
    ];
  } else {
    mustHaves = [
      '- Relevant hands-on experience for the role',
      '- Ability to deliver within agreed timelines',
      '- Strong communication and accountability',
    ];
  }

  const goal = buildRoleGoal(normalizedInput, location, lowered);
  const roleLabel = /^general$/i.test(category)
    ? title
    : `${title} (${category})`;

  const output = [
    `Role: ${roleLabel} — ${location}`,
    '',
    'Key Tasks:',
    ...inferredTasks.map((task) => `- ${task}`),
    '',
    'Must-Haves:',
    ...mustHaves,
    '',
    `Goal: ${goal}`,
  ].join('\n');

  return output;
}

function finalizeAiJobAutoFields(input: string, suggestion: ParsedJobSuggestion, fallback: ParsedJobSuggestion): ParsedJobSuggestion {
  const normalizedInput = normalizeJobInputText(input);
  const next = { ...suggestion };
  next.title = buildConciseJobTitle(suggestion.title, normalizedInput);
  const resolvedSkills = suggestion.requiredSkills?.length
    ? suggestion.requiredSkills
    : fallback.requiredSkills?.length
      ? fallback.requiredSkills
      : inferSkillsFromText(normalizedInput);
  next.requiredSkills = resolvedSkills;
  next.category = suggestion.category?.trim()
    || fallback.category?.trim()
    || inferCategoryFromText(normalizedInput)
    || 'General';
  next.description = buildProfessionalJobDescription(normalizedInput, {
    ...fallback,
    ...next,
    requiredSkills: resolvedSkills,
  });

  return next;
}

function parseJobSuggestionFromText(input: string): ParsedJobSuggestion {
  const text = normalizeJobInputText(input).trim();
  if (!text) return {};

  const location = inferLocationFromText(text);
  const explicitCurrencyRate = /\$\s*(\d+(?:\.\d+)?)\s*(per\s*hour|\/\s*hr|hourly|per\s*day|\/day|daily|per\s*post|\/\s*post|per\s*task|\/\s*task|fixed|flat)?/i.exec(text);
  const keywordRate = /\b(?:rate|pay|budget|cost)\b\s*(?:is|:|=)?\s*\$?(\d+(?:\.\d+)?)\s*(per\s*hour|\/\s*hr|hourly|per\s*day|\/day|daily|per\s*post|\/\s*post|per\s*task|\/\s*task|fixed|flat)?/i.exec(text);
  const rateMatch = explicitCurrencyRate ?? keywordRate;
  const rateTypeMatch = /\b(hourly|per\s*hour|\/\s*hr|daily|per\s*day|per\s*post|\/\s*post|per\s*task|\/\s*task|fixed|flat)\b/i.exec(text);
  const workersMatch = /\b(\d+)\s+(?:worker|workers|people|person|staff)\b/i.exec(text);
  const inferredDates = extractDateRangeFromText(text);
  const fixedBudgetHint = /\bbudget\b[^.\n]*\btotal\b|\btotal\b[^.\n]*\bbudget\b|\bflat\b|\ball[-\s]?in\b|\bfixed\b|\bper\s*post\b|\/\s*post\b|\bper\s*task\b|\/\s*task\b/i.test(text);

  let inferredRateType: ParsedJobSuggestion['rateType'];
  if (rateTypeMatch) {
    const marker = rateTypeMatch[0].toLowerCase();
    if (/daily|per\s*day/.test(marker)) inferredRateType = 'daily';
    else if (/per\s*post|\/\s*post|per\s*task|\/\s*task/.test(marker)) inferredRateType = 'fixed';
    else if (/hourly|per\s*hour|\/\s*hr/.test(marker)) inferredRateType = 'hourly';
    else inferredRateType = 'fixed';
  } else if (fixedBudgetHint && rateMatch) {
    inferredRateType = 'fixed';
  }

  return {
    title: inferTitleFromText(text),
    location: location ?? '',
    description: text,
    category: inferCategoryFromText(text),
    requiredSkills: inferSkillsFromText(text),
    rateAmount: rateMatch ? Number.parseFloat(rateMatch[1]) : undefined,
    rateType: inferredRateType,
    startAt: inferredDates.startAt,
    endAt: inferredDates.endAt,
    workerSlots: workersMatch ? Math.max(1, Number.parseInt(workersMatch[1], 10)) : 1,
  };
}

// ════════════════════════════════════════════════════════════════════════
// MAIN APP COMPONENT
// ════════════════════════════════════════════════════════════════════════
function App() {
  // Auth state
  const [user, setUser] = useState<SafeUser | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(webAppConfig.storageKeys.authToken);
  });
  const [view, setView] = useState<View>('auth');
  const [authTab, setAuthTab] = useState<AuthTab>('login');
  const [adminTab, setAdminTab] = useState<AdminTab>('analytics');
  const [registerForm, setRegisterForm] = useState<RegisterForm>(initialRegister);
  const [loginForm, setLoginForm] = useState<LoginForm>(initialLogin);
  const [authLoading, setAuthLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(Boolean(token));
  const [registrationFieldConfigs, setRegistrationFieldConfigs] = useState<RegistrationFieldConfig[]>([]);
  const [fieldConfigLoading, setFieldConfigLoading] = useState(false);
  const [adminSavingByType, setAdminSavingByType] = useState<Record<RegistrationUserType, boolean>>({
    job_seeker: false,
    company: false,
  });
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerificationVerified, setEmailVerificationVerified] = useState(false);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [showForgotPasswordPanel, setShowForgotPasswordPanel] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [deletionPassword, setDeletionPassword] = useState('');
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionOtp, setDeletionOtp] = useState('');
  const [deletionRequesting, setDeletionRequesting] = useState(false);
  const [deletionConfirming, setDeletionConfirming] = useState(false);
  const [deletionOtpRequested, setDeletionOtpRequested] = useState(false);
  const [deletionRestoreDeadline, setDeletionRestoreDeadline] = useState<string | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem(webAppConfig.storageKeys.theme);
    return stored === 'dark' ? 'dark' : 'light';
  });
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    phone: '',
    password: '',
    city: '',
    state: '',
    country: '',
    skills: '',
    resumeUrl: '',
    companyName: '',
    industry: '',
    companyWebsite: '',
    companySize: '',
  });

  const [adminTrafficAnalytics, setAdminTrafficAnalytics] = useState<AdminAnalyticsTraffic | null>(null);
  const [adminUserAnalytics, setAdminUserAnalytics] = useState<AdminAnalyticsUsers | null>(null);
  const [adminConversionAnalytics, setAdminConversionAnalytics] = useState<AdminAnalyticsConversion | null>(null);
  const [adminCompanyPerformanceList, setAdminCompanyPerformanceList] = useState<AdminCompanyPerformanceSummary[]>([]);
  const [adminCandidatePerformanceList, setAdminCandidatePerformanceList] = useState<AdminCandidatePerformanceSummary[]>([]);
  const [selectedAdminCompanyId, setSelectedAdminCompanyId] = useState<number | null>(null);
  const [selectedAdminCandidateId, setSelectedAdminCandidateId] = useState<number | null>(null);
  const [adminCompanyPerformance, setAdminCompanyPerformance] = useState<AdminCompanyPerformanceDetail | null>(null);
  const [adminCandidatePerformance, setAdminCandidatePerformance] = useState<AdminCandidatePerformanceDetail | null>(null);
  const [adminAnalyticsLoading, setAdminAnalyticsLoading] = useState(false);
  const [adminPerformanceLoading, setAdminPerformanceLoading] = useState(false);
  const [adminJobControlsLoading, setAdminJobControlsLoading] = useState(false);
  const [adminJobControlsSaving, setAdminJobControlsSaving] = useState(false);
  const [adminPendingDecisionLoadingId, setAdminPendingDecisionLoadingId] = useState<number | null>(null);
  const [adminJobControls, setAdminJobControls] = useState<AdminJobControlSettings>({
    autoApproveJobs: true,
    sendAutoEmailOnNewJob: false,
  });
  const [adminPendingApprovalJobs, setAdminPendingApprovalJobs] = useState<AdminPendingApprovalJob[]>([]);
  const [adminMetricDetail, setAdminMetricDetail] = useState<{
    title: string;
    rows: Array<{ label: string; value: number | string }>;
  } | null>(null);
  const [adminMetricPage, setAdminMetricPage] = useState(1);
  const [tabPreferencesByRole, setTabPreferencesByRole] = useState<Record<Role, TabPreferenceConfig>>({
    job_seeker: buildDefaultTabPreferenceConfig('job_seeker'),
    company: buildDefaultTabPreferenceConfig('company'),
    admin: buildDefaultTabPreferenceConfig('admin'),
  });
  const [showTabPreferencesModal, setShowTabPreferencesModal] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [tabPreferencesDraft, setTabPreferencesDraft] = useState<TabPreferenceConfig | null>(null);
  const [tabPreferencesSaving, setTabPreferencesSaving] = useState(false);
  const [adminDefaultTabRole, setAdminDefaultTabRole] = useState<RegistrationUserType>('job_seeker');
  const [adminDefaultTabsLoading, setAdminDefaultTabsLoading] = useState(false);

  // Seeker state
  const [seekerTab, setSeekerTab] = useState<SeekerTab>('dashboard');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [myApplications, setMyApplications] = useState<Job[]>([]);
  const [myInvitations, setMyInvitations] = useState<SeekerInvitation[]>([]);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationRespondingId, setInvitationRespondingId] = useState<number | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [seekerDataLoading, setSeekerDataLoading] = useState(false);
  const [browseFilters, setBrowseFilters] = useState({
    search: '',
    location: '',
    skill: '',
    category: '',
    minPay: '',
    maxPay: '',
  });
  const [browseFiltersCollapsed, setBrowseFiltersCollapsed] = useState(true);

  // Company state
  const [companyTab, setCompanyTab] = useState<CompanyTab>('dashboard');
  const [companyJobs, setCompanyJobs] = useState<CompanyJob[]>([]);
  const [companyBestMatches, setCompanyBestMatches] = useState<CompanyBestMatchGroup[]>([]);
  const [companyBestMatchesLoading, setCompanyBestMatchesLoading] = useState(false);
  const [inviteLoadingKey, setInviteLoadingKey] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);
  const [jobForm, setJobForm] = useState<JobForm>(buildDefaultJobForm(null));
  const [createJobLoading, setCreateJobLoading] = useState(false);
  const [rejectingAppId, setRejectingAppId] = useState<number | null>(null);
  const [companyActionType, setCompanyActionType] = useState<'REJECT_CANDIDATE' | 'NO_SHOW'>('REJECT_CANDIDATE');
  const [rejectReason, setRejectReason] = useState('');
  const [reviewLoading, setReviewLoading] = useState<number | null>(null);
  const [expandedApplicantId, setExpandedApplicantId] = useState<number | null>(null);
  const [companyDataLoading, setCompanyDataLoading] = useState(false);
  const [applicantPageByJob, setApplicantPageByJob] = useState<Record<number, number>>({});
  const [showDuplicateJobModal, setShowDuplicateJobModal] = useState(false);
  const [duplicateSourceJobId, setDuplicateSourceJobId] = useState<number | null>(null);
  const [duplicateJobForm, setDuplicateJobForm] = useState<JobForm>(initialJob);
  const [duplicateJobLoading, setDuplicateJobLoading] = useState(false);
  const [companyReviewDrafts, setCompanyReviewDrafts] = useState<Record<number, { rating: number; comment: string }>>({});
  const [companyReviewSubmittingId, setCompanyReviewSubmittingId] = useState<number | null>(null);
  const [companyReviewEditingByApp, setCompanyReviewEditingByApp] = useState<Record<number, boolean>>({});
  const [selectedJobReviews, setSelectedJobReviews] = useState<JobReview[]>([]);
  const [selectedJobReviewsLoading, setSelectedJobReviewsLoading] = useState(false);
  const [isEditingSeekerReview, setIsEditingSeekerReview] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [editingJobId, setEditingJobId] = useState<number | null>(null);
  const [editJobForm, setEditJobForm] = useState<JobForm>(initialJob);
  const [editJobLoading, setEditJobLoading] = useState(false);
  const [deleteJobLoadingId, setDeleteJobLoadingId] = useState<number | null>(null);
  const [viewJobLoadingId, setViewJobLoadingId] = useState<number | null>(null);
  const [selectedCompanyJob, setSelectedCompanyJob] = useState<CompanyJob | null>(null);

  const [companyDashboard, setCompanyDashboard] = useState<CompanyDashboardData | null>(null);
  const [companyDashboardLoading, setCompanyDashboardLoading] = useState(false);
  const [applicantDashboard, setApplicantDashboard] = useState<ApplicantDashboardData | null>(null);
  const [applicantDashboardLoading, setApplicantDashboardLoading] = useState(false);
  const [companyJobFilters, setCompanyJobFilters] = useState({
    search: '',
    status: 'ALL' as 'ALL' | JobStatus,
    sortBy: 'newest' as 'newest' | 'oldest' | 'pay-high' | 'pay-low' | 'applicants-high',
  });
  const [companyFiltersCollapsed, setCompanyFiltersCollapsed] = useState(true);

  // Notifications
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [receivedReviews, setReceivedReviews] = useState<JobReview[]>([]);
  const [receivedReviewSummary, setReceivedReviewSummary] = useState<ReviewFeedResponse['target'] | null>(null);
  const [receivedReviewsLoading, setReceivedReviewsLoading] = useState(false);
  const [respondLoading, setRespondLoading] = useState<number | null>(null);
  const [seekerRejectingAppId, setSeekerRejectingAppId] = useState<number | null>(null);
  const [seekerRejectReason, setSeekerRejectReason] = useState('');
  const [seekerReviewRating, setSeekerReviewRating] = useState(5);
  const [seekerReviewComment, setSeekerReviewComment] = useState('');
  const [seekerReviewSubmitting, setSeekerReviewSubmitting] = useState(false);

  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = React.useRef(0);

  const [registerSkillsInput, setRegisterSkillsInput] = useState('');
  const [profileSkillsInput, setProfileSkillsInput] = useState('');
  const [jobSkillsInput, setJobSkillsInput] = useState('');
  const [editJobSkillsInput, setEditJobSkillsInput] = useState('');
  const [skillsValidationErrors, setSkillsValidationErrors] = useState<{
    register?: string;
    profile?: string;
    createJob?: string;
    editJob?: string;
  }>({});

  const [aiDescriptionLoading, setAiDescriptionLoading] = useState(false);
  const [voiceTaskLoading, setVoiceTaskLoading] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [showCreateJobConfirmation, setShowCreateJobConfirmation] = useState(false);
  const [pendingCreateJobForm, setPendingCreateJobForm] = useState<JobForm | null>(null);
  const [aiChatPanelOpen, setAiChatPanelOpen] = useState(false);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [aiChatVoiceListening, setAiChatVoiceListening] = useState(false);
  const [aiChatMessages, setAiChatMessages] = useState<Array<{ role: 'user' | 'assistant'; message: string }>>([
    { role: 'assistant', message: 'Hi! Ask me anything, or tell me to create a job and I will prefill the form for review.' },
  ]);
  const voiceRecognitionRef = React.useRef<any>(null);
  const aiChatRecognitionRef = React.useRef<any>(null);
  const aiSuggestionAvailabilityRef = React.useRef<{ checkedAt: number; available: boolean }>({
    checkedAt: 0,
    available: true,
  });
  const lockedCompanyName = useCallback(() => {
    return user?.companyName?.trim() || user?.fullName?.trim() || '';
  }, [user?.companyName, user?.fullName]);

  const aiApiBaseUrl = webConfig.aiServiceUrl;

  const showToast = useCallback((type: ToastType, message: string) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const getFieldConfig = useCallback(
    (userType: RegistrationUserType, fieldKey: string) =>
      registrationFieldConfigs.find((cfg) => cfg.userType === userType && cfg.fieldKey === fieldKey),
    [registrationFieldConfigs],
  );

  const isFieldVisible = useCallback(
    (userType: RegistrationUserType, fieldKey: string) =>
      getFieldConfig(userType, fieldKey)?.isVisible ?? DEFAULT_FIELD_RULES[userType].visible.has(fieldKey),
    [getFieldConfig],
  );

  const isFieldMandatory = useCallback(
    (userType: RegistrationUserType, fieldKey: string) =>
      getFieldConfig(userType, fieldKey)?.isMandatory ?? DEFAULT_FIELD_RULES[userType].mandatory.has(fieldKey),
    [getFieldConfig],
  );

  const loadRegistrationFieldConfigs = useCallback(async () => {
    setFieldConfigLoading(true);
    try {
      const [seekerRes, companyRes] = await Promise.all([
        webApi.get('/auth/registration-fields', { params: { userType: 'job_seeker' } }),
        webApi.get('/auth/registration-fields', { params: { userType: 'company' } }),
      ]);

      const seekerConfigs = Array.isArray(seekerRes.data.fields) ? seekerRes.data.fields : [];
      const companyConfigs = Array.isArray(companyRes.data.fields) ? companyRes.data.fields : [];
      const merged = [
        ...normalizeFieldConfigs('job_seeker', seekerConfigs),
        ...normalizeFieldConfigs('company', companyConfigs),
      ];
      setRegistrationFieldConfigs(merged);
    } catch {
      const fallback = [
        ...normalizeFieldConfigs('job_seeker', []),
        ...normalizeFieldConfigs('company', []),
      ];
      setRegistrationFieldConfigs(fallback);
    } finally {
      setFieldConfigLoading(false);
    }
  }, []);

  const toggleFieldConfig = useCallback((userType: RegistrationUserType, fieldKey: string, mode: 'visible' | 'mandatory') => {
    setRegistrationFieldConfigs((prev) => {
      const existing = prev.find((cfg) => cfg.userType === userType && cfg.fieldKey === fieldKey);
      const next = existing
        ? prev.map((cfg) => {
            if (cfg.userType !== userType || cfg.fieldKey !== fieldKey) return cfg;
            if (mode === 'visible') {
              const isVisible = !cfg.isVisible;
              return { ...cfg, isVisible, isMandatory: isVisible ? cfg.isMandatory : false };
            }
            return { ...cfg, isMandatory: !cfg.isMandatory };
          })
        : [
            ...prev,
            {
              userType,
              fieldKey,
              isVisible: mode === 'visible',
              isMandatory: mode === 'mandatory',
              sortOrder: getCatalog(userType).findIndex((f) => f.fieldKey === fieldKey),
            },
          ];
      return next;
    });
  }, []);

  const saveFieldConfig = useCallback(async (userType: RegistrationUserType) => {
    setAdminSavingByType((prev) => ({ ...prev, [userType]: true }));
    try {
      const fields = registrationFieldConfigs
        .filter((cfg) => cfg.userType === userType)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((cfg) => ({
          fieldKey: cfg.fieldKey,
          isVisible: cfg.isVisible,
          isMandatory: cfg.isVisible ? cfg.isMandatory : false,
          sortOrder: cfg.sortOrder,
        }));

      await webApi.put('/auth/admin/registration-fields', { userType, fields });
      showToast('success', `${userType === 'job_seeker' ? 'Job seeker' : 'Company'} field settings updated.`);
      await loadRegistrationFieldConfigs();
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not update field settings'));
    } finally {
      setAdminSavingByType((prev) => ({ ...prev, [userType]: false }));
    }
  }, [loadRegistrationFieldConfigs, registrationFieldConfigs, showToast]);

  // ── Handle Logout ────────────────────────────────────────────────────
  const logout = useCallback(() => {
    webApi.post('/auth/logout').catch(() => {});
    localStorage.removeItem(webAppConfig.storageKeys.authToken);
    setToken(null);
    setUser(null);
    setWebAuthToken(null);
    setSessionLoading(false);
    setView('auth');
    setJobs([]);
    setMyApplications([]);
    setMyInvitations([]);
    setCompanyJobs([]);
    setCompanyBestMatches([]);
    setCompanyDashboard(null);
    setApplicantDashboard(null);
  }, []);

  // Setup 401 handler
  useEffect(() => {
    setOnUnauthorized(() => {
      logout();
      setView('auth');
      showToast('error', 'Session expired. Please log in again.');
    });
  }, [logout, showToast]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(webAppConfig.storageKeys.theme, theme);
  }, [theme]);

  useEffect(() => {
    setAvatarMenuOpen(false);
  }, [user?.id, view]);

  // ── Initialize from token ────────────────────────────────────────────
  useEffect(() => {
    loadRegistrationFieldConfigs().catch(() => {});
  }, [loadRegistrationFieldConfigs]);

  useEffect(() => {
    if (!token) {
      setWebAuthToken(null);
      setSessionLoading(false);
      setView('auth');
      return;
    }

    // Defensive reset: avoid showing previous user's cached dashboard/app counts
    // while the new session token is being validated and fresh data is loading.
    setJobs([]);
    setMyApplications([]);
    setMyInvitations([]);
    setCompanyJobs([]);
    setCompanyBestMatches([]);
    setCompanyDashboard(null);
    setApplicantDashboard(null);

    if (isJwtExpired(token)) {
      localStorage.removeItem(webAppConfig.storageKeys.authToken);
      setToken(null);
      setWebAuthToken(null);
      setSessionLoading(false);
      setView('auth');
      return;
    }

    setSessionLoading(true);
    setWebAuthToken(token);
    webApi.get('/auth/session/validate')
      .then((res) => {
        const valid = Boolean(res.data?.valid);
        const u = res.data?.user as SafeUser | null | undefined;
        if (valid && u) {
          setUser(u);
          if (u.role === 'company') {
            setCompanyTab('dashboard');
            setView('company');
          } else if (u.role === 'admin') {
            setView('admin');
          } else {
            setSeekerTab('dashboard');
            setView('seeker');
          }
        } else {
          localStorage.removeItem(webAppConfig.storageKeys.authToken);
          setUser(null);
          setView('auth');
          setWebAuthToken(null);
        }
      })
      .catch(() => {
        localStorage.removeItem(webAppConfig.storageKeys.authToken);
        setUser(null);
        setView('auth');
        setWebAuthToken(null);
      })
      .finally(() => {
        setSessionLoading(false);
      });
  }, [token]);

  useEffect(() => {
    setEmailVerificationSent(false);
    setEmailVerificationVerified(false);
    setEmailVerificationCode('');
  }, [registerForm.email]);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await webApi.get('/notifications');
      const notifs = Array.isArray(res.data.notifications) ? res.data.notifications : [];
      setNotifications(notifs);
      setUnreadCount(notifs.filter((n: NotificationItem) => !n.is_read).length);
    } catch {
      // ignore notification load failures
    }
  }, []);

  const loadSeekerData = useCallback(async () => {
    setSeekerDataLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(browseFilters).filter(([, value]) => String(value).trim().length > 0),
      );
      const [jobsRes, appsRes] = await Promise.all([
        webApi.get('/jobs', { params }),
        webApi.get('/jobs/applications/mine'),
      ]);
      setJobs(Array.isArray(jobsRes.data.jobs) ? jobsRes.data.jobs : []);
      setMyApplications(Array.isArray(appsRes.data.applications) ? appsRes.data.applications : []);
    } finally {
      setSeekerDataLoading(false);
    }
  }, [browseFilters]);

  const loadSeekerInvitations = useCallback(async () => {
    setInvitationLoading(true);
    try {
      const res = await webApi.get('/jobs/invitations/mine');
      setMyInvitations(Array.isArray(res.data.invitations) ? res.data.invitations : []);
    } catch {
      setMyInvitations([]);
    } finally {
      setInvitationLoading(false);
    }
  }, []);

  const loadApplicantDashboard = useCallback(async () => {
    setApplicantDashboardLoading(true);
    try {
      const res = await webApi.get('/applicant/dashboard');
      setApplicantDashboard(res.data as ApplicantDashboardData);
    } catch {
      setApplicantDashboard(null);
    } finally {
      setApplicantDashboardLoading(false);
    }
  }, []);

  const loadCompanyData = useCallback(async () => {
    setCompanyDataLoading(true);
    try {
      const res = await webApi.get('/jobs/mine');
      setCompanyJobs(Array.isArray(res.data.jobs) ? res.data.jobs : []);
    } finally {
      setCompanyDataLoading(false);
    }
  }, []);

  const loadCompanyBestMatches = useCallback(async () => {
    setCompanyBestMatchesLoading(true);
    try {
      const res = await webApi.get('/jobs/mine/best-matches');
      setCompanyBestMatches(Array.isArray(res.data.matches) ? res.data.matches : []);
    } catch {
      setCompanyBestMatches([]);
    } finally {
      setCompanyBestMatchesLoading(false);
    }
  }, []);

  const loadCompanyDashboard = useCallback(async () => {
    setCompanyDashboardLoading(true);
    try {
      const res = await webApi.get('/company/dashboard');
      setCompanyDashboard(res.data as CompanyDashboardData);
    } catch {
      setCompanyDashboard(null);
    } finally {
      setCompanyDashboardLoading(false);
    }
  }, []);

  const loadAdminAnalytics = useCallback(async () => {
    if (user?.role !== 'admin') return;
    setAdminAnalyticsLoading(true);
    try {
      const [trafficRes, usersRes, conversionRes] = await Promise.all([
        webApi.get('/admin/analytics/traffic'),
        webApi.get('/admin/analytics/users'),
        webApi.get('/admin/analytics/conversion'),
      ]);

      setAdminTrafficAnalytics(trafficRes.data as AdminAnalyticsTraffic);
      setAdminUserAnalytics(usersRes.data as AdminAnalyticsUsers);
      setAdminConversionAnalytics(conversionRes.data as AdminAnalyticsConversion);
    } catch {
      setAdminTrafficAnalytics(null);
      setAdminUserAnalytics(null);
      setAdminConversionAnalytics(null);
    } finally {
      setAdminAnalyticsLoading(false);
    }
  }, [user?.role]);

  const loadAdminJobControls = useCallback(async () => {
    if (user?.role !== 'admin') return;
    setAdminJobControlsLoading(true);
    try {
      const response = await webApi.get('/admin/analytics/job-controls');
      const settings = (response.data?.settings ?? {}) as Partial<AdminJobControlSettings>;
      const pendingJobs = Array.isArray(response.data?.pendingJobs)
        ? (response.data.pendingJobs as AdminPendingApprovalJob[])
        : [];

      setAdminJobControls({
        autoApproveJobs: settings.autoApproveJobs ?? true,
        sendAutoEmailOnNewJob: settings.sendAutoEmailOnNewJob ?? false,
        updatedAt: settings.updatedAt,
      });
      setAdminPendingApprovalJobs(pendingJobs);
    } catch {
      setAdminPendingApprovalJobs([]);
    } finally {
      setAdminJobControlsLoading(false);
    }
  }, [user?.role]);

  const saveAdminJobControls = useCallback(async () => {
    if (user?.role !== 'admin') return;

    setAdminJobControlsSaving(true);
    try {
      const response = await webApi.put('/admin/analytics/job-controls', {
        autoApproveJobs: adminJobControls.autoApproveJobs,
        sendAutoEmailOnNewJob: adminJobControls.sendAutoEmailOnNewJob,
      });

      const next = (response.data?.settings ?? {}) as Partial<AdminJobControlSettings>;
      setAdminJobControls((prev) => ({
        ...prev,
        autoApproveJobs: next.autoApproveJobs ?? prev.autoApproveJobs,
        sendAutoEmailOnNewJob: next.sendAutoEmailOnNewJob ?? prev.sendAutoEmailOnNewJob,
        updatedAt: next.updatedAt ?? prev.updatedAt,
      }));
      showToast('success', 'Job controls updated.');
      await loadAdminJobControls();
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not update job controls'));
    } finally {
      setAdminJobControlsSaving(false);
    }
  }, [adminJobControls.autoApproveJobs, adminJobControls.sendAutoEmailOnNewJob, loadAdminJobControls, showToast, user?.role]);

  const decidePendingJobApproval = useCallback(async (jobId: number, action: 'approve' | 'reject') => {
    if (user?.role !== 'admin') return;

    setAdminPendingDecisionLoadingId(jobId);
    try {
      await webApi.post(`/admin/analytics/job-controls/pending-jobs/${jobId}/decision`, { action });
      showToast('success', action === 'approve' ? 'Job approved successfully.' : 'Job rejected.');
      await Promise.all([loadAdminJobControls(), loadAdminAnalytics()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not update job approval'));
    } finally {
      setAdminPendingDecisionLoadingId(null);
    }
  }, [loadAdminAnalytics, loadAdminJobControls, showToast, user?.role]);

  const getRoleTabItems = useCallback((role: Role) => {
    const config = normalizeTabPreferenceConfig(role, tabPreferencesByRole[role]);
    const visibleOrder = config.tabOrder.filter((key) => config.tabVisibility[key] !== false);

    return visibleOrder.map((key) => ({
      key,
      label: config.tabLabels[key] || DEFAULT_TAB_LABELS[role][key] || key,
    }));
  }, [tabPreferencesByRole]);

  const loadMyTabPreferences = useCallback(async () => {
    if (!user) return;
    try {
      const response = await webApi.get('/preferences/tabs');
      const preferences = response.data?.preferences as TabPreferenceConfig | undefined;
      if (!preferences || !preferences.role) return;

      setTabPreferencesByRole((current) => ({
        ...current,
        [preferences.role]: normalizeTabPreferenceConfig(preferences.role, preferences),
      }));
    } catch {
      // keep defaults
    }
  }, [user]);

  const loadAdminRoleDefaults = useCallback(async (role: Role) => {
    if (user?.role !== 'admin') return;
    setAdminDefaultTabsLoading(true);
    try {
      const response = await webApi.get(`/preferences/tabs/admin/defaults/${role}`);
      const defaults = response.data?.defaults as TabPreferenceConfig | undefined;
      if (!defaults) return;

      setTabPreferencesByRole((current) => ({
        ...current,
        [role]: normalizeTabPreferenceConfig(role, defaults),
      }));
    } catch {
      // ignore and keep current
    } finally {
      setAdminDefaultTabsLoading(false);
    }
  }, [user?.role]);

  const openMyTabPreferences = useCallback(() => {
    if (!user) return;
    const current = normalizeTabPreferenceConfig(user.role, tabPreferencesByRole[user.role]);
    setTabPreferencesDraft({
      role: user.role,
      tabOrder: [...current.tabOrder],
      tabVisibility: { ...current.tabVisibility },
      tabLabels: { ...current.tabLabels },
    });
    setShowTabPreferencesModal(true);
  }, [tabPreferencesByRole, user]);

  const moveTabInDraft = useCallback((key: string, direction: 'left' | 'right') => {
    setTabPreferencesDraft((current) => {
      if (!current) return current;
      const index = current.tabOrder.indexOf(key);
      if (index < 0) return current;
      const target = direction === 'left' ? index - 1 : index + 1;
      if (target < 0 || target >= current.tabOrder.length) return current;
      const nextOrder = [...current.tabOrder];
      [nextOrder[index], nextOrder[target]] = [nextOrder[target], nextOrder[index]];
      return { ...current, tabOrder: nextOrder };
    });
  }, []);

  const saveMyTabPreferences = useCallback(async () => {
    if (!user || !tabPreferencesDraft) return;
    setTabPreferencesSaving(true);
    try {
      const payload = {
        tabOrder: tabPreferencesDraft.tabOrder,
        tabVisibility: tabPreferencesDraft.tabVisibility,
        tabLabels: tabPreferencesDraft.tabLabels,
      };
      const response = await webApi.put('/preferences/tabs', payload);
      const preferences = response.data?.preferences as TabPreferenceConfig | undefined;
      if (preferences?.role) {
        setTabPreferencesByRole((current) => ({
          ...current,
          [preferences.role]: normalizeTabPreferenceConfig(preferences.role, preferences),
        }));
      }
      setShowTabPreferencesModal(false);
      setTabPreferencesDraft(null);
      showToast('success', 'Tab preferences updated.');
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not update tab preferences'));
    } finally {
      setTabPreferencesSaving(false);
    }
  }, [showToast, tabPreferencesDraft, user]);

  const saveAdminRoleDefaults = useCallback(async () => {
    if (user?.role !== 'admin') return;
    const config = tabPreferencesByRole[adminDefaultTabRole] ?? buildDefaultTabPreferenceConfig(adminDefaultTabRole);
    setAdminDefaultTabsLoading(true);
    try {
      const response = await webApi.put(`/preferences/tabs/admin/defaults/${adminDefaultTabRole}`, {
        tabOrder: config.tabOrder,
        tabVisibility: config.tabVisibility,
        tabLabels: config.tabLabels,
      });
      const defaults = response.data?.defaults as TabPreferenceConfig | undefined;
      if (defaults) {
        setTabPreferencesByRole((current) => ({
          ...current,
          [adminDefaultTabRole]: normalizeTabPreferenceConfig(adminDefaultTabRole, defaults),
        }));
      }
      showToast('success', `${adminDefaultTabRole} default tabs updated.`);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not save role default tabs'));
    } finally {
      setAdminDefaultTabsLoading(false);
    }
  }, [adminDefaultTabRole, showToast, tabPreferencesByRole, user?.role]);

  const loadAdminPerformanceIndex = useCallback(async () => {
    if (user?.role !== 'admin') return;
    setAdminPerformanceLoading(true);
    try {
      const [companiesRes, candidatesRes] = await Promise.all([
        webApi.get('/admin/analytics/performance/companies'),
        webApi.get('/admin/analytics/performance/candidates'),
      ]);

      const companies = Array.isArray(companiesRes.data?.companies)
        ? (companiesRes.data.companies as AdminCompanyPerformanceSummary[])
        : [];
      const candidates = Array.isArray(candidatesRes.data?.candidates)
        ? (candidatesRes.data.candidates as AdminCandidatePerformanceSummary[])
        : [];

      setAdminCompanyPerformanceList(companies);
      setAdminCandidatePerformanceList(candidates);

      setSelectedAdminCompanyId((prev) => {
        if (companies.length === 0) return null;
        if (prev && companies.some((company) => company.companyUserId === prev)) return prev;
        return companies[0].companyUserId;
      });

      setSelectedAdminCandidateId((prev) => {
        if (candidates.length === 0) return null;
        if (prev && candidates.some((candidate) => candidate.candidateUserId === prev)) return prev;
        return candidates[0].candidateUserId;
      });
    } catch {
      setAdminCompanyPerformanceList([]);
      setAdminCandidatePerformanceList([]);
      setSelectedAdminCompanyId(null);
      setSelectedAdminCandidateId(null);
      setAdminCompanyPerformance(null);
      setAdminCandidatePerformance(null);
    } finally {
      setAdminPerformanceLoading(false);
    }
  }, [user?.role]);

  const loadAdminCompanyPerformanceDetail = useCallback(async (companyUserId: number) => {
    try {
      const response = await webApi.get(`/admin/analytics/performance/companies/${companyUserId}`);
      setAdminCompanyPerformance((response.data?.company as AdminCompanyPerformanceDetail | undefined) ?? null);
    } catch {
      setAdminCompanyPerformance(null);
    }
  }, []);

  const loadAdminCandidatePerformanceDetail = useCallback(async (candidateUserId: number) => {
    try {
      const response = await webApi.get(`/admin/analytics/performance/candidates/${candidateUserId}`);
      setAdminCandidatePerformance((response.data?.candidate as AdminCandidatePerformanceDetail | undefined) ?? null);
    } catch {
      setAdminCandidatePerformance(null);
    }
  }, []);

  const openAdminMetricDetail = useCallback((title: string, rows: Array<{ label: string; value: number | string }>) => {
    setAdminMetricDetail({ title, rows });
    setAdminMetricPage(1);
  }, []);

  // ── Fetch data based on view ─────────────────────────────────────────
  useEffect(() => {
    if (view === 'seeker' && user) {
      loadMyTabPreferences().catch(() => {});
      loadSeekerData().catch(() => {});
      loadApplicantDashboard().catch(() => {});
      loadSeekerInvitations().catch(() => {});
      loadNotifications().catch(() => {});
    }
    if (view === 'company' && user) {
      loadMyTabPreferences().catch(() => {});
      loadCompanyData().catch(() => {});
      loadCompanyDashboard().catch(() => {});
      loadCompanyBestMatches().catch(() => {});
      loadNotifications().catch(() => {});
    }
    if (view === 'admin' && user?.role === 'admin') {
      loadMyTabPreferences().catch(() => {});
      loadAdminRoleDefaults(adminDefaultTabRole).catch(() => {});
      loadAdminAnalytics().catch(() => {});
      loadAdminJobControls().catch(() => {});
      loadAdminPerformanceIndex().catch(() => {});
    }
  }, [
    view,
    user,
    loadApplicantDashboard,
    loadSeekerInvitations,
    loadCompanyDashboard,
    loadCompanyBestMatches,
    loadNotifications,
    loadAdminAnalytics,
    loadAdminJobControls,
    loadAdminPerformanceIndex,
    loadMyTabPreferences,
    loadAdminRoleDefaults,
    adminDefaultTabRole,
    loadSeekerData,
    loadCompanyData,
  ]);

  useEffect(() => {
    const seekerItems = getRoleTabItems('job_seeker').map((item) => item.key);
    if (view === 'seeker' && !seekerItems.includes(seekerTab)) {
      setSeekerTab((seekerItems[0] as SeekerTab | undefined) ?? 'dashboard');
    }
  }, [getRoleTabItems, seekerTab, view]);

  useEffect(() => {
    const companyItems = getRoleTabItems('company').map((item) => item.key);
    if (view === 'company' && !companyItems.includes(companyTab)) {
      setCompanyTab((companyItems[0] as CompanyTab | undefined) ?? 'dashboard');
    }
  }, [companyTab, getRoleTabItems, view]);

  useEffect(() => {
    const adminItems = getRoleTabItems('admin').map((item) => item.key);
    if (view === 'admin' && !adminItems.includes(adminTab)) {
      setAdminTab((adminItems[0] as AdminTab | undefined) ?? 'analytics');
    }
  }, [adminTab, getRoleTabItems, view]);

  useEffect(() => {
    if (view !== 'admin' || user?.role !== 'admin' || !selectedAdminCompanyId) {
      if (!selectedAdminCompanyId) setAdminCompanyPerformance(null);
      return;
    }

    loadAdminCompanyPerformanceDetail(selectedAdminCompanyId).catch(() => {});
  }, [loadAdminCompanyPerformanceDetail, selectedAdminCompanyId, user?.role, view]);

  useEffect(() => {
    if (view !== 'admin' || user?.role !== 'admin' || !selectedAdminCandidateId) {
      if (!selectedAdminCandidateId) setAdminCandidatePerformance(null);
      return;
    }

    loadAdminCandidatePerformanceDetail(selectedAdminCandidateId).catch(() => {});
  }, [loadAdminCandidatePerformanceDetail, selectedAdminCandidateId, user?.role, view]);

  useEffect(() => {
    if (view !== 'seeker' || seekerTab !== 'browse' || !user) {
      return;
    }

    const handle = window.setTimeout(() => {
      loadSeekerData().catch(() => {});
    }, 350);

    return () => {
      window.clearTimeout(handle);
    };
  }, [browseFilters, loadSeekerData, seekerTab, user, view]);

  useEffect(() => {
    if (!user || (view !== 'seeker' && view !== 'company')) {
      setReceivedReviews([]);
      setReceivedReviewSummary(null);
      setReceivedReviewsLoading(false);
      return;
    }

    const endpoint = user.role === 'company'
      ? `/reviews/company/${user.id}`
      : `/reviews/applicant/${user.id}`;

    setReceivedReviewsLoading(true);
    webApi.get(endpoint)
      .then((res) => {
        const payload = res.data as Partial<ReviewFeedResponse>;
        const normalizedReviews = Array.isArray(payload.reviews)
          ? payload.reviews.map(normalizeJobReview)
          : [];
        setReceivedReviews(normalizedReviews);
        setReceivedReviewSummary(payload.target as ReviewFeedResponse['target']);
      })
      .catch(() => {
        setReceivedReviews([]);
        setReceivedReviewSummary(null);
      })
      .finally(() => {
        setReceivedReviewsLoading(false);
      });
  }, [user, view]);

  useEffect(() => {
    setShowCreateJobModal(false);
    setShowCreateJobConfirmation(false);
    setShowDuplicateJobModal(false);
    // Reopen removed
    setShowEditJobModal(false);
    setSelectedJob(null);
    setExpandedJobId(null);
    setExpandedApplicantId(null);
    setRejectingAppId(null);
    setCompanyActionType('REJECT_CANDIDATE');
    setRejectReason('');
    setSeekerRejectingAppId(null);
    setSeekerRejectReason('');
    setCompanyReviewDrafts({});
    setCompanyReviewSubmittingId(null);
    setCompanyReviewEditingByApp({});
    setSelectedJobReviews([]);
    setSelectedJobReviewsLoading(false);
    setIsEditingSeekerReview(false);
    setEditingJobId(null);
    setEditJobForm(buildDefaultJobForm(user));
    setEditJobLoading(false);
    setDeleteJobLoadingId(null);
    setSeekerReviewRating(5);
    setSeekerReviewComment('');
    setSeekerReviewSubmitting(false);
    setSelectedCompanyJob(null);
    setViewJobLoadingId(null);
    setJobForm(buildDefaultJobForm(user));
    setDuplicateJobForm(buildDefaultJobForm(user));
    setPendingCreateJobForm(null);
    setAiChatPanelOpen(false);
    setAiChatInput('');
    if (aiChatRecognitionRef.current) {
      aiChatRecognitionRef.current.stop();
      aiChatRecognitionRef.current = null;
    }
    setAiChatVoiceListening(false);
    setAiChatMessages([
      { role: 'assistant', message: 'Hi! Ask me anything, or tell me to create a job and I will prefill the form for review.' },
    ]);
    setSeekerTab('dashboard');
    setCompanyTab('dashboard');
    setMyInvitations([]);
    setCompanyBestMatches([]);
    setCompanyDashboard(null);
    setApplicantDashboard(null);
    setProfileForm({
      fullName: user?.fullName ?? '',
      phone: user?.phone ?? '',
      password: '',
      city: user?.city ?? '',
      state: user?.state ?? '',
      country: user?.country ?? '',
      skills: (user?.skills ?? []).join(', '),
      resumeUrl: user?.resumeUrl ?? '',
      companyName: user?.companyName ?? '',
      industry: user?.industry ?? '',
      companyWebsite: user?.companyWebsite ?? '',
      companySize: user?.companySize ? String(user.companySize) : '',
    });
    setProfileSkillsInput((user?.skills ?? []).join(', '));
    setDeletionPassword('');
    setDeletionReason('');
    setDeletionOtp('');
    setDeletionOtpRequested(false);
    setDeletionRestoreDeadline(null);
  }, [user]);

  useEffect(() => {
    const selectedJobId = selectedJob?.id;
    if (!selectedJobId || !user) {
      setSelectedJobReviews([]);
      setSelectedJobReviewsLoading(false);
      setIsEditingSeekerReview(false);
      return;
    }

    setSelectedJobReviewsLoading(true);
    webApi.get(`/jobs/${selectedJobId}`)
      .then((res) => {
        const details = res.data.job as Job | undefined;
        const reviews = details && Array.isArray(details.reviews)
          ? details.reviews.map(normalizeJobReview)
          : [];
        setSelectedJob((prev) => (prev && details ? { ...prev, ...details } : prev));
        setSelectedJobReviews(reviews);
      })
      .catch(() => {
        setSelectedJobReviews([]);
      })
      .finally(() => {
        setSelectedJobReviewsLoading(false);
      });
  }, [selectedJob?.id, user]);

  const openCreateJobModal = useCallback(() => {
    const defaultForm = buildDefaultJobForm(user);
    const companyName = lockedCompanyName() || defaultForm.companyName;
    setJobForm((prev) => ({
      ...defaultForm,
      title: prev.title,
      description: prev.description,
      requiredSkills: prev.requiredSkills,
      workerSlots: prev.workerSlots,
      rateType: prev.rateType,
      rateAmount: prev.rateAmount,
      startAt: prev.startAt,
      endAt: prev.endAt,
      companyName,
      location: prev.location || defaultForm.location,
    }));
    setShowCreateJobModal(true);
    setJobSkillsInput((jobForm.requiredSkills ?? []).join(', '));
  }, [jobForm.requiredSkills, lockedCompanyName, user]);

  const applySuggestionToJobForm = useCallback((
    suggestion: ParsedJobSuggestion,
    options?: { resetBeforeApply?: boolean; clearSkillsWhenMissing?: boolean },
  ) => {
    setJobForm((prev) => {
      const base = options?.resetBeforeApply ? buildDefaultJobForm(user) : prev;
      const mappedRateType = suggestion.rateType === 'daily' ? 'fixed' : suggestion.rateType;
      const hasSuggestedSkills = (suggestion.requiredSkills?.length ?? 0) > 0;
      let nextRequiredSkills = base.requiredSkills;
      if (hasSuggestedSkills) {
        nextRequiredSkills = suggestion.requiredSkills ?? [];
      } else if (options?.clearSkillsWhenMissing) {
        nextRequiredSkills = [];
      }
      const next: JobForm = {
        ...base,
        title: suggestion.title?.trim() || base.title,
        companyName: lockedCompanyName() || base.companyName,
        category: suggestion.category?.trim() || base.category,
        location: suggestion.location?.trim() || base.location,
        description: suggestion.description ?? base.description,
        requiredSkills: nextRequiredSkills,
        rateType: mappedRateType || base.rateType,
        rateAmount: typeof suggestion.rateAmount === 'number' && suggestion.rateAmount > 0 ? suggestion.rateAmount : base.rateAmount,
        startAt: suggestion.startAt || base.startAt,
        endAt: suggestion.endAt || base.endAt,
        workerSlots: typeof suggestion.workerSlots === 'number' && suggestion.workerSlots > 0 ? suggestion.workerSlots : base.workerSlots,
      };
      return next;
    });

    const skillsText = suggestion.requiredSkills?.join(', ');
    if (skillsText) {
      setJobSkillsInput(skillsText);
      setSkillsValidationErrors((prev) => ({ ...prev, createJob: undefined }));
      return;
    }

    const shouldClearSkills = Boolean(options?.resetBeforeApply || options?.clearSkillsWhenMissing);
    if (shouldClearSkills) {
      setJobSkillsInput('');
      setSkillsValidationErrors((prev) => ({ ...prev, createJob: undefined }));
    }
  }, [lockedCompanyName, user]);

  const requestAiSuggestionWithRetry = useCallback(async (input: string) => {
    const fetchWithTimeout = async (url: string, init: RequestInit, timeoutMs: number): Promise<Response> => {
      const controller = new AbortController();
      const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await fetch(url, { ...init, signal: controller.signal });
      } finally {
        globalThis.clearTimeout(timer);
      }
    };

    const now = Date.now();
    const staleProbe = now - aiSuggestionAvailabilityRef.current.checkedAt > 30_000;
    if (staleProbe) {
      const healthUrl = `${aiApiBaseUrl.replace(/\/api\/ai\/?$/, '')}/api/health`;
      try {
        const health = await fetchWithTimeout(healthUrl, { method: 'GET' }, 1500);
        aiSuggestionAvailabilityRef.current = {
          checkedAt: Date.now(),
          available: health.ok,
        };
      } catch {
        aiSuggestionAvailabilityRef.current = {
          checkedAt: Date.now(),
          available: false,
        };
      }
    }

    if (!aiSuggestionAvailabilityRef.current.available) {
      throw new Error('AI_UNAVAILABLE_FAST');
    }

    let lastStatus: number | null = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await fetchWithTimeout(`${aiApiBaseUrl}/job-description`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ input }),
        }, 3500);

        if (res.ok) {
          const payload = await res.json() as {
            title?: string;
            description?: string;
            skills?: string[];
            location?: string;
            category?: string;
            rateAmount?: number;
            rateType?: 'hourly' | 'fixed' | 'daily';
            startAt?: string;
            endAt?: string;
            startDate?: string;
            endDate?: string;
          };

          return {
            title: payload.title,
            description: payload.description,
            requiredSkills: Array.isArray(payload.skills) ? payload.skills : undefined,
            location: payload.location,
            category: payload.category,
            rateAmount: payload.rateAmount,
            rateType: payload.rateType,
            startAt: payload.startAt ?? payload.startDate,
            endAt: payload.endAt ?? payload.endDate,
          } as ParsedJobSuggestion;
        }

        lastStatus = res.status;
        if (res.status >= 400 && res.status < 500) {
          break;
        }
      } catch {
        // retry on all errors
      }
    }

    throw new Error(lastStatus ? `HTTP_${lastStatus}` : 'AI_UNREACHABLE');
  }, [aiApiBaseUrl]);

  const handleParseFromDescription = useCallback(async (
    source: 'typed' | 'voice',
    descriptionOverride?: string,
  ) => {
    const input = (descriptionOverride ?? jobForm.description).trim();
    if (!input) {
      showToast('error', 'Please add job details in Description first.');
      return;
    }

    if (source === 'typed') {
      setAiDescriptionLoading(true);
    } else {
      setVoiceTaskLoading(true);
    }

    const localSuggestion = parseJobSuggestionFromText(input);

    try {
      const aiSuggestion = await requestAiSuggestionWithRetry(input);
      const merged = normalizeSuggestionForInput(input, {
        ...localSuggestion,
        ...compactSuggestion(aiSuggestion),
        description: aiSuggestion.description?.trim() || input,
      }, localSuggestion);
      const finalized = finalizeAiJobAutoFields(input, merged, localSuggestion);
      applySuggestionToJobForm(finalized, { clearSkillsWhenMissing: true });
      showToast('success', source === 'typed' ? 'AI parsed your typed details.' : 'Voice details parsed into job fields.');
    } catch {
      const normalizedFallback = normalizeSuggestionForInput(input, {
        ...localSuggestion,
        description: input,
      }, localSuggestion);
      const finalizedFallback = finalizeAiJobAutoFields(input, normalizedFallback, localSuggestion);
      applySuggestionToJobForm(finalizedFallback, { clearSkillsWhenMissing: true });
      showToast('success', 'Form details were prefilled successfully.');
    } finally {
      if (source === 'typed') {
        setAiDescriptionLoading(false);
      } else {
        setVoiceTaskLoading(false);
      }
    }
  }, [applySuggestionToJobForm, jobForm.description, requestAiSuggestionWithRetry, showToast]);

  const buildAiChatContext = useCallback(() => {
    const base = {
      user: {
        id: user?.id,
        role: user?.role,
        fullName: user?.fullName,
        companyName: user?.companyName ?? undefined,
      },
      history: aiChatMessages.slice(-6),
    };

    if (user?.role === 'company') {
      return {
        ...base,
        company: {
          myJobs: companyJobs.map((job) => ({
            id: job.id,
            title: job.title,
            status: job.status,
            workerSlots: job.workerSlots,
            filledSlots: job.filledSlots,
            applicationsCount: Array.isArray(job.applications) ? job.applications.length : 0,
          })),
        },
      };
    }

    return {
      ...base,
      seeker: {
        jobs: jobs.map((job) => ({
          id: job.id,
          title: job.title,
          companyName: job.companyName,
          location: job.location,
          status: job.status,
        })),
        myApplications: myApplications.map((job) => ({
          id: job.id,
          title: job.title,
          companyName: job.companyName,
          latestApplicationStatus: job.latestApplicationStatus ?? null,
        })),
      },
    };
  }, [aiChatMessages, companyJobs, jobs, myApplications, user?.companyName, user?.fullName, user?.id, user?.role]);

  const handleAiChat = async () => {
    const message = aiChatInput.trim();
    if (!message) {
      showToast('error', 'Enter a message for AI chat.');
      return;
    }

    setAiChatMessages((prev) => [...prev, { role: 'user', message }]);
    setAiChatInput('');
    setAiChatLoading(true);

    const tryHandleCreateIntent = async () => {
      if (user?.role !== 'company') return false;

      const localSuggestion = parseJobSuggestionFromText(message);
      const createIntent = /(create|post|add).*(job|shift)|\b(need|hire|hiring|looking for)\b.*\b(worker|workers|plumber|electrician|carpenter|technician|staff|person|people|candidate|helper|labor)\b/i.test(message);
      const parseableJobRequest = Boolean(
        localSuggestion.title
        || localSuggestion.location
        || localSuggestion.rateAmount
        || localSuggestion.startAt
        || localSuggestion.endAt
        || (localSuggestion.requiredSkills?.length ?? 0) > 0,
      );

      if (!createIntent && !parseableJobRequest) return false;

      let aiSuggestion: ParsedJobSuggestion = {};
      try {
        aiSuggestion = await requestAiSuggestionWithRetry(message);
      } catch {
        // fallback to local parsing only
      }

      const merged = normalizeSuggestionForInput(message, {
        ...localSuggestion,
        ...compactSuggestion(aiSuggestion),
        description: aiSuggestion.description?.trim() || message,
      }, localSuggestion);
      const finalized = finalizeAiJobAutoFields(message, merged, localSuggestion);

      applySuggestionToJobForm(finalized, { resetBeforeApply: true });
      if (user?.companyName?.trim()) {
        setJobForm((prev) => ({ ...prev, companyName: user.companyName?.trim() ?? prev.companyName }));
      }
      setShowCreateJobModal(true);
      setAiChatMessages((prev) => [
        ...prev,
        { role: 'assistant', message: 'Got it — I prefilled the create job form. Review and confirm to proceed.' },
      ]);
      return true;
    };

    const normalizedMessage = message.toLowerCase().replaceAll(/[^a-z0-9\s]/g, ' ').replaceAll(/\s+/g, ' ').trim();
    const applicationsCountPhrases = [
      'how many applications',
      'count applications',
      'total applications',
      'applications did i apply',
      'jobs did i apply',
      'applications so far',
      'applications yet',
    ];
    if (applicationsCountPhrases.some((phrase) => normalizedMessage.includes(phrase))) {
      setAiChatMessages((prev) => [
        ...prev,
        { role: 'assistant', message: `You have applied for ${myApplications.length} job${myApplications.length === 1 ? '' : 's'}.` },
      ]);
      setAiChatLoading(false);
      return;
    }

    const createHandled = await tryHandleCreateIntent();
    if (createHandled) {
      setAiChatLoading(false);
      return;
    }

    try {
      const res = await fetch(`${aiApiBaseUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          role: user?.role === 'company' ? 'professional' : 'customer',
          context: buildAiChatContext(),
        }),
      });

      if (!res.ok) {
        setAiChatMessages((prev) => [...prev, { role: 'assistant', message: "I'm here to help! Try asking about posting or finding jobs." }]);
        return;
      }

      const payload = await res.json() as { response?: string };
      setAiChatMessages((prev) => [...prev, { role: 'assistant', message: payload.response ?? 'No response returned.' }]);
    } catch {
      setAiChatMessages((prev) => [...prev, { role: 'assistant', message: "I'm here to help! Try asking about posting or finding jobs." }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  const renderJobSkeletonCard = (key: string) => (
    <div key={key} className="job-card job-card-skeleton" aria-hidden="true">
      <div className="job-card-top">
        <div className="company-initial skeleton-box" />
        <div className="job-card-title" style={{ width: '100%' }}>
          <div className="skeleton-line" style={{ width: '70%' }} />
          <div className="skeleton-line" style={{ width: '50%' }} />
        </div>
      </div>
      <div className="skeleton-line" style={{ width: '95%' }} />
      <div className="skeleton-line" style={{ width: '80%' }} />
      <div className="chip-row">
        <span className="chip chip-muted skeleton-box" style={{ width: 70, height: 20 }} />
        <span className="chip chip-muted skeleton-box" style={{ width: 88, height: 20 }} />
      </div>
    </div>
  );

  const handleStartVoiceCapture = () => {
    const recognitionCtor = (globalThis as any).SpeechRecognition || (globalThis as any).webkitSpeechRecognition;
    if (!recognitionCtor) {
      showToast('error', 'Voice capture is not supported in this browser. Paste transcript text instead.');
      return;
    }

    const recognition = new recognitionCtor();
    (recognition as any).__manualStopRequested = false;
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    let transcriptBuffer = '';
    let hasSpeechResult = false;
    recognition.onresult = (event: any) => {
      const transcript = event?.results?.[0]?.[0]?.transcript;
      if (typeof transcript === 'string' && transcript.trim()) {
        hasSpeechResult = true;
        transcriptBuffer = [transcriptBuffer, transcript.trim()].filter(Boolean).join(' ');
      }
    };
    recognition.onend = () => {
      setVoiceListening(false);
      voiceRecognitionRef.current = null;
      if ((recognition as any).__manualStopRequested && !transcriptBuffer.trim()) {
        return;
      }
      if (!transcriptBuffer.trim()) {
        showToast('error', 'No speech detected. Please try recording again.');
        return;
      }

      setJobForm((prev) => {
        const mergedDescription = [prev.description, transcriptBuffer.trim()].filter(Boolean).join(' ');
        handleParseFromDescription('voice', mergedDescription).catch(() => {});
        return {
          ...prev,
          description: mergedDescription,
        };
      });
    };
    recognition.onerror = (event: any) => {
      setVoiceListening(false);
      voiceRecognitionRef.current = null;
      const errorCode = typeof event?.error === 'string' ? event.error : '';
      if ((recognition as any).__manualStopRequested && errorCode === 'aborted') {
        return;
      }
      if (errorCode === 'no-speech') {
        if (hasSpeechResult || transcriptBuffer.trim()) {
          return;
        }
        showToast('error', 'No speech detected. Please try recording again.');
        return;
      }
      showToast('error', 'Voice capture failed. Try again or paste transcript manually.');
    };

    voiceRecognitionRef.current = recognition;
    setVoiceListening(true);
    recognition.start();
  };

  const handleStopVoiceCapture = () => {
    if (voiceRecognitionRef.current) {
      (voiceRecognitionRef.current as any).__manualStopRequested = true;
      voiceRecognitionRef.current.stop();
      setVoiceListening(false);
      voiceRecognitionRef.current = null;
    }
  };

  const handleStartAiChatVoiceCapture = () => {
    const recognitionCtor = (globalThis as any).SpeechRecognition || (globalThis as any).webkitSpeechRecognition;
    if (!recognitionCtor) {
      showToast('error', 'Voice capture is not supported in this browser.');
      return;
    }

    const recognition = new recognitionCtor();
    (recognition as any).__manualStopRequested = false;
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;
    let transcriptBuffer = '';
    let hasSpeechResult = false;
    recognition.onresult = (event: any) => {
      let chunk = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results?.[index]?.[0]?.transcript;
        if (typeof transcript === 'string' && transcript.trim()) {
          chunk = `${chunk} ${transcript.trim()}`.trim();
        }
      }

      if (chunk) {
        hasSpeechResult = true;
        transcriptBuffer = [transcriptBuffer, chunk].filter(Boolean).join(' ');
        setAiChatInput((prev) => [prev, chunk].filter(Boolean).join(' '));
      }
    };
    recognition.onend = () => {
      setAiChatVoiceListening(false);
      aiChatRecognitionRef.current = null;
      if (transcriptBuffer.trim()) {
        showToast('success', 'Voice text added to chat input.');
      } else if (!(recognition as any).__manualStopRequested) {
        showToast('error', 'No speech detected for AI assistant input.');
      }
    };
    recognition.onerror = (event: any) => {
      setAiChatVoiceListening(false);
      aiChatRecognitionRef.current = null;
      const errorCode = typeof event?.error === 'string' ? event.error : '';
      if ((recognition as any).__manualStopRequested && errorCode === 'aborted') {
        return;
      }
      if (errorCode === 'no-speech') {
        if (hasSpeechResult || transcriptBuffer.trim()) {
          return;
        }
        showToast('error', 'No speech detected for AI assistant input.');
        return;
      }
      showToast('error', 'Voice capture failed. Please try again.');
    };

    aiChatRecognitionRef.current = recognition;
    setAiChatVoiceListening(true);
    recognition.start();
  };

  const handleStopAiChatVoiceCapture = () => {
    if (aiChatRecognitionRef.current) {
      (aiChatRecognitionRef.current as any).__manualStopRequested = true;
      aiChatRecognitionRef.current.stop();
      aiChatRecognitionRef.current = null;
      setAiChatVoiceListening(false);
    }
  };

  const handleSendEmailCode = async () => {
    if (!registerForm.email.trim()) {
      showToast('error', 'Please enter your email first.');
      return;
    }

    setEmailVerificationLoading(true);
    try {
      const response = await webApi.post('/auth/send-email-code', { email: registerForm.email.trim() });
      setEmailVerificationSent(true);
      setEmailVerificationVerified(false);
      if (typeof response.data?.devCode === 'string') {
        setEmailVerificationCode(response.data.devCode);
      }
      const message = typeof response.data?.message === 'string'
        ? response.data.message
        : 'Verification code sent to your email.';
      showToast('success', message);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not send verification code'));
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    if (!registerForm.email.trim() || !emailVerificationCode.trim()) {
      showToast('error', 'Enter both email and verification code.');
      return;
    }

    setEmailVerificationLoading(true);
    try {
      await webApi.post('/auth/verify-email-code', {
        email: registerForm.email.trim(),
        code: emailVerificationCode.trim(),
      });
      setEmailVerificationVerified(true);
      showToast('success', 'Email verified. You can complete registration now.');
    } catch (err) {
      setEmailVerificationVerified(false);
      showToast('error', getWebApiErrorMessage(err, 'Invalid verification code'));
    } finally {
      setEmailVerificationLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) {
      showToast('error', 'Please enter your registered email.');
      return;
    }

    setForgotLoading(true);
    try {
      await webApi.post('/auth/forgot-password', { email: forgotEmail.trim() });
      showToast('success', 'If the email exists, a reset code has been sent.');
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not start password reset'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!forgotEmail.trim() || !forgotCode.trim() || forgotNewPassword.length < 8) {
      showToast('error', 'Enter email, code, and a new password (min 8 characters).');
      return;
    }

    setForgotLoading(true);
    try {
      await webApi.post('/auth/reset-password', {
        email: forgotEmail.trim(),
        code: forgotCode.trim(),
        newPassword: forgotNewPassword,
      });
      setShowForgotPasswordPanel(false);
      setForgotCode('');
      setForgotNewPassword('');
      setAuthTab('login');
      showToast('success', 'Password reset successful. Please sign in.');
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not reset password'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleProfileUpdate = async (updates: Record<string, unknown>) => {
    setProfileSaving(true);
    try {
      const res = await webApi.put('/profile/update', updates);
      const updatedUser = res.data?.user as SafeUser | undefined;
      if (updatedUser) {
        setUser(updatedUser);
      }
      showToast('success', 'Profile updated successfully.');
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not update profile'));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileFormSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();

    const updates: Record<string, unknown> = {
      fullName: profileForm.fullName.trim() || undefined,
      phone: profileForm.phone.trim() || undefined,
      city: profileForm.city.trim() || undefined,
      state: profileForm.state.trim() || undefined,
      country: profileForm.country.trim() || undefined,
      password: profileForm.password.trim() || undefined,
    };

    if (user?.role === 'job_seeker') {
      const parsedSkills = parseAndValidateSkillsInput(profileSkillsInput);
      if (parsedSkills.error) {
        setSkillsValidationErrors((prev) => ({ ...prev, profile: parsedSkills.error ?? undefined }));
        showToast('error', parsedSkills.error);
        return;
      }

      updates.skills = parsedSkills.skills;
      updates.resumeUrl = profileForm.resumeUrl.trim() || undefined;
    }

    if (user?.role === 'company') {
      if (isFieldVisible('company', 'companyName')) {
        updates.companyName = profileForm.companyName.trim() || undefined;
      }
      if (isFieldVisible('company', 'industry')) {
        updates.industry = profileForm.industry.trim() || undefined;
      }
      if (isFieldVisible('company', 'companyWebsite')) {
        updates.companyWebsite = profileForm.companyWebsite.trim() || undefined;
      }
      if (isFieldVisible('company', 'companySize')) {
        updates.companySize = profileForm.companySize ? Number(profileForm.companySize) : undefined;
      }
      if (isFieldVisible('company', 'location')) {
        updates.city = profileForm.city.trim() || undefined;
      }
    }

    await handleProfileUpdate(updates);
    setProfileForm((prev) => ({ ...prev, password: '' }));
  };

  const handleRequestAccountDeletion = async () => {
    if (!deletionPassword.trim()) {
      showToast('error', 'Enter your current password to request account deletion.');
      return;
    }

    setDeletionRequesting(true);
    try {
      const res = await webApi.post('/profile/deletion/request', {
        password: deletionPassword,
        reason: deletionReason.trim() || undefined,
      });

      setDeletionOtpRequested(true);
      setDeletionRestoreDeadline(typeof res.data?.restoreDeadline === 'string' ? res.data.restoreDeadline : null);
      if (typeof res.data?.devOtpCode === 'string') {
        setDeletionOtp(res.data.devOtpCode);
        showToast('success', 'OTP generated (dev mode). Please confirm deletion to continue.');
      } else {
        showToast('success', 'Deletion OTP sent. Check your email and confirm deletion.');
      }
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not start account deletion flow'));
    } finally {
      setDeletionRequesting(false);
    }
  };

  const handleConfirmAccountDeletion = async () => {
    if (!deletionOtp.trim()) {
      showToast('error', 'Enter the 6-digit OTP to confirm deletion.');
      return;
    }

    setDeletionConfirming(true);
    try {
      await webApi.post('/profile/deletion/confirm', { otp: deletionOtp.trim() });
      showToast('success', 'Account deletion confirmed. You have been signed out.');
      logout();
      setAuthTab('login');
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not confirm account deletion'));
    } finally {
      setDeletionConfirming(false);
    }
  };

  // ── Auth handlers ────────────────────────────────────────────────────
  const handleRegister: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!registerForm.acceptedLegalPolicies) {
      showToast('error', 'Please accept the Terms of Service and Privacy Policy to create an account.');
      return;
    }

    if (!emailVerificationVerified) {
      showToast('error', 'Please verify your email before creating an account.');
      return;
    }

    setAuthLoading(true);
    try {
      setJobs([]);
      setMyApplications([]);
      setCompanyJobs([]);

      const parsedRegisterSkills = parseAndValidateSkillsInput(registerSkillsInput);
      if (registerForm.userType === 'job_seeker' && parsedRegisterSkills.error) {
        setSkillsValidationErrors((prev) => ({ ...prev, register: parsedRegisterSkills.error ?? undefined }));
        showToast('error', parsedRegisterSkills.error);
        return;
      }

      const registerPayload = {
        userType: registerForm.userType,
        fullName: registerForm.fullName,
        email: registerForm.email,
        password: registerForm.password,
        acceptedLegalPolicies: registerForm.acceptedLegalPolicies,
        phone: registerForm.phone || undefined,
        skills: registerForm.userType === 'job_seeker' ? parsedRegisterSkills.skills : registerForm.skills,
        workExperienceYears: registerForm.workExperienceYears === '' ? undefined : registerForm.workExperienceYears,
        availability: registerForm.availability || undefined,
        resumeUrl: registerForm.resumeUrl || undefined,
        profilePictureUrl: registerForm.profilePictureUrl || undefined,
        city: registerForm.city || undefined,
        state: registerForm.state || undefined,
        country: registerForm.country || undefined,
        ratingAverage: registerForm.ratingAverage === '' ? undefined : registerForm.ratingAverage,
        ratingCount: registerForm.ratingCount === '' ? undefined : registerForm.ratingCount,
        reviews: registerForm.reviews,
        companyName: registerForm.companyName || undefined,
        industry: registerForm.industry || undefined,
        companySize: registerForm.companySize === '' ? undefined : registerForm.companySize,
        verifiedStatus: registerForm.verifiedStatus,
        companyLogoUrl: registerForm.companyLogoUrl || undefined,
        companyWebsite: registerForm.companyWebsite || undefined,
        locations:
          registerForm.userType === 'company'
            ? (registerForm.city
              ? [registerForm.city]
              : registerForm.locations)
            : registerForm.locations,
      };

      const res = await webApi.post('/auth/register', registerPayload);
      const { token: newToken, user: newUser } = res.data;
      localStorage.setItem(webAppConfig.storageKeys.authToken, newToken);
      setToken(newToken);
      setWebAuthToken(newToken);
      setUser(newUser);
      if (newUser.role === 'company') {
        setCompanyTab('dashboard');
        await Promise.all([loadCompanyData(), loadCompanyDashboard(), loadCompanyBestMatches(), loadNotifications()]);
        setView('company');
      } else if (newUser.role === 'admin') {
        await loadRegistrationFieldConfigs();
        setView('admin');
      } else {
        setSeekerTab('dashboard');
        await Promise.all([loadSeekerData(), loadApplicantDashboard(), loadSeekerInvitations(), loadNotifications()]);
        setView('seeker');
      }
      setRegisterForm(initialRegister);
      setRegisterSkillsInput('');
      setEmailVerificationCode('');
      setEmailVerificationSent(false);
      setEmailVerificationVerified(false);
      showToast('success', 'Account created successfully!');
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Registration failed'));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      setJobs([]);
      setMyApplications([]);
      setCompanyJobs([]);

      const res = await webApi.post('/auth/login', loginForm);
      const { token: newToken, user: newUser } = res.data;
      localStorage.setItem(webAppConfig.storageKeys.authToken, newToken);
      setToken(newToken);
      setWebAuthToken(newToken);
      setUser(newUser);
      if (newUser.role === 'company') {
        setCompanyTab('dashboard');
        await Promise.all([loadCompanyData(), loadCompanyDashboard(), loadCompanyBestMatches(), loadNotifications()]);
        setView('company');
      } else if (newUser.role === 'admin') {
        await loadRegistrationFieldConfigs();
        setView('admin');
      } else {
        setSeekerTab('dashboard');
        await Promise.all([loadSeekerData(), loadApplicantDashboard(), loadSeekerInvitations(), loadNotifications()]);
        setView('seeker');
      }
      setLoginForm(initialLogin);
      showToast('success', `Welcome back, ${newUser.fullName}!`);
    } catch (err) {
      const status = (err as any)?.response?.status;
      if (status === 401) {
        showToast('error', 'Invalid email or password.');
      } else {
        showToast('error', getWebApiErrorMessage(err, 'Login failed'));
      }
    } finally {
      setAuthLoading(false);
    }
  };

  // ── Job Seeker handlers ──────────────────────────────────────────────
  const handleApply = async (jobId: number) => {
    setApplyLoading(true);
    try {
      await webApi.post(`/jobs/${jobId}/apply`);
      showToast('success', 'Application submitted!');
      setSelectedJob(null);
      await Promise.all([loadSeekerData(), loadSeekerInvitations(), loadNotifications(), loadApplicantDashboard()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not apply'));
    } finally {
      setApplyLoading(false);
    }
  };

  const handleRespondToInvitation = async (inviteId: number, decision: 'accepted' | 'rejected') => {
    setInvitationRespondingId(inviteId);
    try {
      await webApi.post(`/jobs/invitations/${inviteId}/respond`, { decision });
      showToast('success', decision === 'accepted' ? 'Invitation accepted. You are now in the applicant list.' : 'Invitation declined.');
      await Promise.all([loadSeekerData(), loadSeekerInvitations(), loadNotifications(), loadApplicantDashboard()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not process invitation response'));
    } finally {
      setInvitationRespondingId(null);
    }
  };

  // ── Company handlers ─────────────────────────────────────────────────
  const handleCreateJob: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const parsedSkills = parseAndValidateSkillsInput(jobSkillsInput);
    if (parsedSkills.error) {
      setSkillsValidationErrors((prev) => ({ ...prev, createJob: parsedSkills.error ?? undefined }));
      showToast('error', parsedSkills.error);
      return;
    }

    const defaultCompany = buildDefaultJobForm(user).companyName;
    setPendingCreateJobForm({
      ...jobForm,
      requiredSkills: parsedSkills.skills,
      companyName: lockedCompanyName() || defaultCompany,
    });
    setShowCreateJobConfirmation(true);
  };

  const confirmCreateJob = async () => {
    if (!pendingCreateJobForm) return;

    setCreateJobLoading(true);
    try {
      const payload = {
        ...pendingCreateJobForm,
        companyName: lockedCompanyName() || pendingCreateJobForm.companyName,
      };
      const response = await webApi.post('/jobs', payload);
      const createdJob = response.data?.job as Job | undefined;
      const approvalStatus = createdJob?.approvalStatus ?? 'approved';
      if (approvalStatus === 'pending') {
        showToast('success', 'Job submitted and is awaiting admin approval.');
      } else {
        showToast('success', 'Job posted successfully!');
      }
      setShowCreateJobConfirmation(false);
      setPendingCreateJobForm(null);
      setShowCreateJobModal(false);
      setJobForm(buildDefaultJobForm(user));
      setJobSkillsInput('');
      await Promise.all([loadCompanyData(), loadCompanyDashboard(), loadCompanyBestMatches()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not create job'));
    } finally {
      setCreateJobLoading(false);
    }
  };

  const handleCompanyApplicantAction = async (
    applicationId: number,
    action: 'CONFIRM_CANDIDATE' | 'REJECT_CANDIDATE' | 'MARK_COMPLETED' | 'NO_SHOW',
    reason?: string,
  ) => {
    setReviewLoading(applicationId);
    try {
      await webApi.post(`/jobs/applications/${applicationId}/action`, { action, reason });
      showToast('success', 'Applicant status updated successfully.');
      setRejectingAppId(null);
      setCompanyActionType('REJECT_CANDIDATE');
      setRejectReason('');
      await Promise.all([loadCompanyData(), loadNotifications(), loadCompanyDashboard(), loadCompanyBestMatches()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not update applicant status'));
    } finally {
      setReviewLoading(null);
    }
  };

  const handleRespondToOffer = async (applicationId: number, decision: 'accepted' | 'rejected', reason?: string) => {
    setRespondLoading(applicationId);
    try {
      await webApi.post(`/jobs/applications/${applicationId}/respond`, { decision, reason });
      showToast('success', decision === 'accepted' ? 'Offer accepted!' : 'Offer declined');
      setSeekerRejectingAppId(null);
      setSeekerRejectReason('');
      setSelectedJob(null);
      await Promise.all([loadSeekerData(), loadSeekerInvitations(), loadNotifications(), loadApplicantDashboard()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not submit your response'));
    } finally {
      setRespondLoading(null);
    }
  };

  const handleCompanyReviewSubmit = async (jobId: number, applicationId: number, revieweeUserId: number) => {
    const draft = companyReviewDrafts[applicationId] ?? { rating: 5, comment: '' };
    if (!Number.isInteger(draft.rating) || draft.rating < 1 || draft.rating > 5) {
      showToast('error', 'Rating must be between 1 and 5.');
      return;
    }

    setCompanyReviewSubmittingId(applicationId);
    try {
      const res = await webApi.post(`/jobs/${jobId}/reviews`, {
        revieweeUserId,
        rating: draft.rating,
        comment: draft.comment,
      });
      const review = normalizeJobReview(res.data.review as JobReview);
      showToast('success', 'Review submitted successfully.');
      setCompanyReviewDrafts((prev) => ({ ...prev, [applicationId]: { rating: 5, comment: '' } }));
      setCompanyReviewEditingByApp((prev) => ({ ...prev, [applicationId]: false }));
      setCompanyJobs((prev) =>
        prev.map((job) => {
          if (job.id !== jobId) return job;
          const existing = (job.reviews ?? []).filter(
            (item) => !(item.reviewer_user_id === review.reviewer_user_id && item.reviewee_user_id === review.reviewee_user_id),
          );
          return {
            ...job,
            reviews: [...existing, review],
          };
        }),
      );
      loadCompanyData().catch(() => {});
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not submit review'));
    } finally {
      setCompanyReviewSubmittingId(null);
    }
  };

  const handleSeekerReviewSubmit = async (job: Job) => {
    const revieweeUserId = job.companyUserId ?? job.company?.id;
    if (!revieweeUserId) {
      showToast('error', 'Company details are missing for this job.');
      return;
    }

    if (!Number.isInteger(seekerReviewRating) || seekerReviewRating < 1 || seekerReviewRating > 5) {
      showToast('error', 'Rating must be between 1 and 5.');
      return;
    }

    setSeekerReviewSubmitting(true);
    try {
      const res = await webApi.post(`/jobs/${job.id}/reviews`, {
        revieweeUserId,
        rating: seekerReviewRating,
        comment: seekerReviewComment,
      });
      const review = normalizeJobReview(res.data.review as JobReview);
      showToast('success', 'Your review has been submitted.');
      setSeekerReviewRating(5);
      setSeekerReviewComment('');
      setIsEditingSeekerReview(false);
      setSelectedJobReviews((prev) => {
        const filtered = prev.filter(
          (item) => !(item.reviewer_user_id === review.reviewer_user_id && item.reviewee_user_id === review.reviewee_user_id),
        );
        return [...filtered, review];
      });
      loadSeekerData().catch(() => {});
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not submit review'));
    } finally {
      setSeekerReviewSubmitting(false);
    }
  };

  const openEditJobModal = (job: CompanyJob) => {
    setEditingJobId(job.id);
    setEditJobForm({
      title: job.title,
      companyName: job.companyName,
      category: job.category ?? '',
      location: job.location,
      description: job.description,
      requiredSkills: job.requiredSkills,
      workerSlots: job.workerSlots,
      rateType: job.rateType,
      rateAmount: job.rateAmount,
      startAt: toDateTimeLocalValue(job.startAt),
      endAt: toDateTimeLocalValue(job.endAt),
    });
    setEditJobSkillsInput((job.requiredSkills ?? []).join(', '));
    setShowEditJobModal(true);
  };

  const handleEditJobSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!editingJobId) return;

    const parsedSkills = parseAndValidateSkillsInput(editJobSkillsInput);
    if (parsedSkills.error) {
      setSkillsValidationErrors((prev) => ({ ...prev, editJob: parsedSkills.error ?? undefined }));
      showToast('error', parsedSkills.error);
      return;
    }

    setEditJobLoading(true);
    try {
      const { category: _category, ...editPayload } = editJobForm;
      await webApi.patch(`/jobs/mine/${editingJobId}`, { ...editPayload, requiredSkills: parsedSkills.skills });
      showToast('success', 'Job updated successfully.');
      setShowEditJobModal(false);
      setEditingJobId(null);
      await Promise.all([loadCompanyData(), loadCompanyDashboard(), loadCompanyBestMatches()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not update job'));
    } finally {
      setEditJobLoading(false);
    }
  };

  const handleDeleteJob = async (job: CompanyJob) => {
    if (!(job.status === 'OPEN' && job.applications.length === 0)) {
      showToast('error', 'Delete is allowed only when job is OPEN and has no applicants.');
      return;
    }

    const shouldDelete = globalThis.confirm(`Delete "${job.title}"? This cannot be undone.`);
    if (!shouldDelete) return;

    setDeleteJobLoadingId(job.id);
    try {
      await webApi.delete(`/jobs/mine/${job.id}`);
      showToast('success', 'Job deleted successfully.');
      setCompanyJobs((prev) => prev.filter((item) => item.id !== job.id));
      loadCompanyDashboard().catch(() => {});
      loadCompanyBestMatches().catch(() => {});
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not delete job'));
    } finally {
      setDeleteJobLoadingId(null);
    }
  };

  const handleDuplicateJob = async (job: CompanyJob) => {
    setDuplicateSourceJobId(job.id);
    setDuplicateJobForm({
      title: `${job.title} (Copy)`,
      companyName: job.companyName,
      category: job.category ?? '',
      location: job.location,
      description: job.description,
      requiredSkills: job.requiredSkills,
      workerSlots: job.workerSlots,
      rateType: job.rateType,
      rateAmount: job.rateAmount,
      startAt: toDateTimeLocalValue(job.startAt),
      endAt: toDateTimeLocalValue(job.endAt),
    });
    setShowDuplicateJobModal(true);
  };

  const handleInviteCandidate = async (jobId: number, candidate: CompanyBestMatchCandidate) => {
    const key = `${jobId}-${candidate.userId}`;
    setInviteLoadingKey(key);
    try {
      await webApi.post(`/jobs/${jobId}/invite`, { candidateUserId: candidate.userId });
      showToast('success', `Invitation sent to ${candidate.fullName ?? candidate.email}.`);
      await Promise.all([loadCompanyBestMatches(), loadNotifications()]);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not send invite to candidate'));
    } finally {
      setInviteLoadingKey(null);
    }
  };

  const handleViewCompanyJob = async (jobId: number) => {
    setViewJobLoadingId(jobId);
    try {
      const res = await webApi.get(`/jobs/mine/${jobId}`);
      setSelectedCompanyJob(res.data.job as CompanyJob);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not load job details'));
    } finally {
      setViewJobLoadingId(null);
    }
  };

  const submitDuplicateJob: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!duplicateSourceJobId) return;

    const parsedSkills = parseAndValidateSkillsInput((duplicateJobForm.requiredSkills ?? []).join(', '));
    if (parsedSkills.error) {
      showToast('error', parsedSkills.error);
      return;
    }

    setDuplicateJobLoading(true);
    try {
      const { category: _category, ...duplicatePayload } = duplicateJobForm;
      await webApi.post(`/jobs/${duplicateSourceJobId}/duplicate`, { ...duplicatePayload, requiredSkills: parsedSkills.skills });
      showToast('success', 'Job duplicated successfully');
      await Promise.all([loadCompanyData(), loadCompanyDashboard(), loadCompanyBestMatches()]);
      setShowDuplicateJobModal(false);
      setDuplicateSourceJobId(null);
    } catch (err) {
      showToast('error', getWebApiErrorMessage(err, 'Could not duplicate job'));
    } finally {
      setDuplicateJobLoading(false);
    }
  };

  const handleMarkNotificationRead = async (notificationId: number) => {
    try {
      await webApi.post(`/notifications/${notificationId}/read`);
      await loadNotifications();
    } catch {
      // no-op
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    const previousNotifications = notifications;
    const previousUnreadCount = unreadCount;
    setNotifications([]);
    setUnreadCount(0);
    setNotifOpen(false);
    try {
      await webApi.post('/notifications/read-all');
    } catch {
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════

  // ── Toasts ───────────────────────────────────────────────────────────
  const renderToasts = () => (
    <>
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <span className="toast-icon">{t.type === 'success' ? '✓' : '✕'}</span>
          <span>{t.message}</span>
          <button className="toast-close" onClick={() => dismissToast(t.id)}>×</button>
        </div>
      ))}
    </>
  );

  // ── Navbar ───────────────────────────────────────────────────────────
  const renderNavbar = () => (
    <Header
      className="navbar"
      brandHref={webAppConfig.routes.home}
      logoSrc={webAppConfig.brand.logoPath}
      brandText={webAppConfig.brand.name}
      brandClassName="navbar-brand"
      logoClassName="brand-logo"
      brandTextClassName="brand-name"
      rightContent={user ? (
        <div className="navbar-right">
          <div className="notif-wrap">
            <button className="notif-bell" onClick={() => setNotifOpen((prev) => !prev)}>
              🔔
              {unreadCount > 0 && <span className="notif-count">{unreadCount}</span>}
            </button>
            {notifOpen && (
              <div className="notif-panel">
                <div className="notif-header">
                  <strong>Notifications</strong>
                  <button className="link-btn" onClick={handleMarkAllNotificationsRead}>Mark all read</button>
                </div>
                {notifications.length === 0 ? (
                  <p className="empty-inline">No notifications yet.</p>
                ) : (
                  <div className="notif-list">
                    {notifications.map((n) => (
                      <button
                        key={n.id}
                        className={`notif-item ${n.is_read ? '' : 'unread'}`}
                        onClick={() => handleMarkNotificationRead(n.id)}
                      >
                        <div className="notif-item-title">{n.title}</div>
                        <div className="notif-item-message">{n.message}</div>
                        <div className="notif-item-time">{fmtDateTime(n.created_at)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="user-pill">
            <button
              className="avatar-menu-trigger"
              onClick={() => setAvatarMenuOpen((prev) => !prev)}
              aria-expanded={avatarMenuOpen}
              aria-label="Open account menu"
              type="button"
            >
              <div className="user-avatar">{getInitial(user.fullName)}</div>
              <span className="user-name">{user.fullName || user.email || 'User'}</span>
              <span className={`role-badge role-${user.role}`}>
                {user.role === 'company' ? 'Company' : user.role === 'admin' ? 'Admin' : 'Job Seeker'}
              </span>
            </button>
            {avatarMenuOpen && (
              <div className="avatar-menu">
                {user.role !== 'admin' && (
                  <button
                    className="avatar-menu-item"
                    onClick={() => {
                      if (user.role === 'company') {
                        setView('company');
                        setCompanyTab('profile');
                      } else {
                        setView('seeker');
                        setSeekerTab('profile');
                      }
                      setAvatarMenuOpen(false);
                    }}
                  >
                    Profile
                  </button>
                )}
                <button
                  className="avatar-menu-item"
                  onClick={() => {
                    openMyTabPreferences();
                    setAvatarMenuOpen(false);
                  }}
                >
                  Manage Tabs
                </button>
              </div>
            )}
          </div>
          <button className="btn-nav-logout" onClick={logout}>Logout</button>
          <button
            className="btn-ghost"
            onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
            aria-label="Toggle dark and light theme"
            title="Toggle dark and light theme"
          >
            {theme === 'light' ? '🌙 Dark' : '☀️ Light'}
          </button>
        </div>
      ) : (
        <span className="navbar-tagline">
          Find your next opportunity • <Link to={webAppConfig.routes.home} className="link-btn">Go to Home</Link>
        </span>
      )}
    />
  );

  // ── Auth View ────────────────────────────────────────────────────────
  const renderAuthView = () => (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src={webAppConfig.brand.logoPath} alt={`${webAppConfig.brand.name} logo`} className="auth-logo" width={56} height={56} loading="eager" decoding="async" />
          <p>Connect talent with opportunity</p>
        </div>
        <div className="auth-tabs">
          <button className={`auth-tab ${authTab === 'login' ? 'active' : ''}`} onClick={() => setAuthTab('login')}>
            Sign In
          </button>
          <button className={`auth-tab ${authTab === 'register' ? 'active' : ''}`} onClick={() => setAuthTab('register')}>
            Sign Up
          </button>
        </div>
        {authTab === 'login' ? (
          <form className="auth-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                required
              />
            </div>
            <div className="form-group" style={{ alignItems: 'flex-end' }}>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setShowForgotPasswordPanel((prev) => !prev);
                  setForgotEmail(loginForm.email);
                }}
              >
                Forgot Password?
              </button>
            </div>

            {showForgotPasswordPanel && (
              <div className="reject-panel" style={{ borderColor: '#bfdbfe', background: '#f8fbff' }}>
                <label htmlFor="forgot-email">Registered Email</label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                <div className="reject-panel-actions">
                  <button type="button" className="btn-primary" disabled={forgotLoading} onClick={() => { handleForgotPassword().catch(() => {}); }}>
                    {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                  </button>
                </div>
                <label htmlFor="forgot-code">Reset Code</label>
                <input
                  id="forgot-code"
                  type="text"
                  value={forgotCode}
                  onChange={(e) => setForgotCode(e.target.value)}
                  placeholder="Enter code"
                />
                <label htmlFor="forgot-new-password">New Password</label>
                <input
                  id="forgot-new-password"
                  type="password"
                  value={forgotNewPassword}
                  onChange={(e) => setForgotNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                />
                <div className="reject-panel-actions">
                  <button type="button" className="btn-success" disabled={forgotLoading} onClick={() => { handleResetPassword().catch(() => {}); }}>
                    {forgotLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </div>
            )}
            <button type="submit" className="btn-primary btn-full" disabled={authLoading}>
              {authLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <p className="auth-switch">
              Don't have an account?{' '}
              <button type="button" className="link-btn" onClick={() => setAuthTab('register')}>
                Sign up
              </button>
            </p>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleRegister}>
            {fieldConfigLoading && <p className="empty-inline">Loading form preferences...</p>}
            <p className="empty-inline">Fields marked with * are required. Other visible fields are optional.</p>
            <div className="form-group">
              <label htmlFor="register-user-type">I am a...</label>
              <div className="toggle-group">
                <button
                  id="register-user-type"
                  type="button"
                  className={`toggle-btn ${registerForm.userType === 'job_seeker' ? 'active' : ''}`}
                  onClick={() => setRegisterForm({ ...registerForm, userType: 'job_seeker' })}
                >
                  Job Seeker
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${registerForm.userType === 'company' ? 'active' : ''}`}
                  onClick={() => setRegisterForm({ ...registerForm, userType: 'company' })}
                >
                  Company
                </button>
              </div>
            </div>

            {isFieldVisible(registerForm.userType, 'fullName') && (
              <div className="form-group">
                <label htmlFor="register-full-name">Username</label>
                <input
                  id="register-full-name"
                  type="text"
                  placeholder="Choose your username"
                  value={registerForm.fullName}
                  onChange={(e) => setRegisterForm({ ...registerForm, fullName: e.target.value })}
                  required={isFieldMandatory(registerForm.userType, 'fullName')}
                />
              </div>
            )}

            {isFieldVisible(registerForm.userType, 'email') && (
              <div className="form-group">
                <label htmlFor="register-email">Email</label>
                <input
                  id="register-email"
                  type="email"
                  placeholder="you@example.com"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  required={isFieldMandatory(registerForm.userType, 'email')}
                />
                <div className="reject-panel-actions" style={{ marginTop: 8 }}>
                  <div className="reject-panel" style={{ marginTop: 2, borderColor: '#bfdbfe', background: '#f8fbff' }}>
                    <div className="reject-panel-actions">
                      <button type="button" className="btn-ghost" disabled={emailVerificationLoading} onClick={() => { handleSendEmailCode().catch(() => {}); }}>
                        {emailVerificationLoading ? 'Sending...' : 'Send code'}
                      </button>
                      <input
                        type="text"
                        placeholder="Enter verification code"
                        value={emailVerificationCode}
                        onChange={(e) => setEmailVerificationCode(e.target.value)}
                      />
                      <button type="button" className="btn-success" disabled={emailVerificationLoading} onClick={() => { handleVerifyEmailCode().catch(() => {}); }}>
                        {emailVerificationLoading ? 'Verifying...' : emailVerificationVerified ? 'Verified' : 'Verify'}
                      </button>
                    </div>
                    {emailVerificationSent && !emailVerificationVerified && (
                      <p className="empty-inline">Verification code sent. Check your inbox for the 6-digit code.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isFieldVisible(registerForm.userType, 'password') && (
              <div className="form-group">
                <label htmlFor="register-password">Password</label>
                <input
                  id="register-password"
                  type="password"
                  placeholder="••••••••"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  required={isFieldMandatory(registerForm.userType, 'password')}
                  minLength={8}
                />
              </div>
            )}

            {isFieldVisible(registerForm.userType, 'phone') && (
              <div className="form-group">
                <label htmlFor="register-phone">Phone</label>
                <input
                  id="register-phone"
                  type="text"
                  placeholder="+1 555 123 4567"
                  value={registerForm.phone}
                  onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                  required={isFieldMandatory(registerForm.userType, 'phone')}
                />
              </div>
            )}

            {registerForm.userType === 'job_seeker' && (
              <>
                {isFieldVisible('job_seeker', 'skills') && (
                  <div className="form-group">
                    <label htmlFor="register-skills">Skill Set (comma-separated)</label>
                    <input
                      id="register-skills"
                      type="text"
                      placeholder={`e.g. ${PRESET_SKILLS.slice(0, 3).join(', ')}`}
                      value={registerSkillsInput}
                      onChange={(e) => {
                        const nextInput = e.target.value;
                        setRegisterSkillsInput(nextInput);
                        const parsed = parseAndValidateSkillsInput(nextInput);
                        setSkillsValidationErrors((prev) => ({ ...prev, register: parsed.error ?? undefined }));
                        if (!parsed.error) {
                          setRegisterForm((prev) => ({ ...prev, skills: parsed.skills }));
                        }
                      }}
                      required={isFieldMandatory('job_seeker', 'skills')}
                    />
                    {skillsValidationErrors.register && <p className="empty-inline">{skillsValidationErrors.register}</p>}
                  </div>
                )}

                {isFieldVisible('job_seeker', 'resumeUrl') && (
                  <div className="form-group">
                    <label htmlFor="register-resume">Resume Upload (PDF)</label>
                    <input
                      id="register-resume"
                      type="file"
                      accept="application/pdf"
                      required={isFieldMandatory('job_seeker', 'resumeUrl') && !registerForm.resumeUrl}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.type !== 'application/pdf') {
                          showToast('error', 'Resume must be a PDF file.');
                          return;
                        }
                        try {
                          const data = await readFileAsDataUrl(file);
                          setRegisterForm((prev) => ({ ...prev, resumeUrl: data }));
                        } catch {
                          showToast('error', 'Could not read resume file.');
                        }
                      }}
                    />
                  </div>
                )}

                {(isFieldVisible('job_seeker', 'workExperienceYears') || isFieldVisible('job_seeker', 'availability')) && (
                  <div className="form-row">
                    {isFieldVisible('job_seeker', 'workExperienceYears') && (
                      <div className="form-group">
                        <label htmlFor="register-work-exp">Work Experience (years)</label>
                        <input
                          id="register-work-exp"
                          type="number"
                          min="0"
                          value={registerForm.workExperienceYears}
                          onChange={(e) => setRegisterForm({ ...registerForm, workExperienceYears: e.target.value === '' ? '' : Number(e.target.value) })}
                          required={isFieldMandatory('job_seeker', 'workExperienceYears')}
                        />
                      </div>
                    )}
                    {isFieldVisible('job_seeker', 'availability') && (
                      <div className="form-group">
                        <label htmlFor="register-availability">Availability</label>
                        <select
                          id="register-availability"
                          value={registerForm.availability}
                          onChange={(e) => setRegisterForm({ ...registerForm, availability: e.target.value as RegisterForm['availability'] })}
                          required={isFieldMandatory('job_seeker', 'availability')}
                        >
                          <option value="">Select availability</option>
                          <option value="full-time">Full-time</option>
                          <option value="part-time">Part-time</option>
                          <option value="freelance">Freelance</option>
                          <option value="hourly">Hourly</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {(isFieldVisible('job_seeker', 'profilePictureUrl') || isFieldVisible('job_seeker', 'ratingAverage') || isFieldVisible('job_seeker', 'ratingCount')) && (
                  <div className="form-row">
                    {isFieldVisible('job_seeker', 'profilePictureUrl') && (
                      <div className="form-group">
                        <label htmlFor="register-profile-pic">Profile Picture</label>
                        <input
                          id="register-profile-pic"
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const data = await readFileAsDataUrl(file);
                              setRegisterForm((prev) => ({ ...prev, profilePictureUrl: data }));
                            } catch {
                              showToast('error', 'Could not read profile image.');
                            }
                          }}
                        />
                      </div>
                    )}
                    {isFieldVisible('job_seeker', 'ratingAverage') && (
                      <div className="form-group">
                        <label htmlFor="register-rating-average">Rating</label>
                        <input
                          id="register-rating-average"
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                          value={registerForm.ratingAverage}
                          onChange={(e) => setRegisterForm({ ...registerForm, ratingAverage: e.target.value === '' ? '' : Number(e.target.value) })}
                        />
                      </div>
                    )}
                    {isFieldVisible('job_seeker', 'ratingCount') && (
                      <div className="form-group">
                        <label htmlFor="register-rating-count">Review Count</label>
                        <input
                          id="register-rating-count"
                          type="number"
                          min="0"
                          value={registerForm.ratingCount}
                          onChange={(e) => setRegisterForm({ ...registerForm, ratingCount: e.target.value === '' ? '' : Number(e.target.value) })}
                        />
                      </div>
                    )}
                  </div>
                )}

                {(isFieldVisible('job_seeker', 'city') || isFieldVisible('job_seeker', 'state')) && (
                  <div className="form-row">
                    {isFieldVisible('job_seeker', 'city') && (
                      <div className="form-group">
                        <label htmlFor="register-city">Location</label>
                        <input
                          id="register-city"
                          type="text"
                          value={registerForm.city}
                          onChange={(e) => setRegisterForm({ ...registerForm, city: e.target.value })}
                          required={isFieldMandatory('job_seeker', 'city')}
                        />
                      </div>
                    )}
                    {isFieldVisible('job_seeker', 'state') && (
                      <div className="form-group">
                        <label htmlFor="register-state">State</label>
                        <input
                          id="register-state"
                          type="text"
                          value={registerForm.state}
                          onChange={(e) => setRegisterForm({ ...registerForm, state: e.target.value })}
                          required={isFieldMandatory('job_seeker', 'state')}
                        />
                      </div>
                    )}
                  </div>
                )}

                {isFieldVisible('job_seeker', 'country') && (
                  <div className="form-group">
                    <label htmlFor="register-country">Country</label>
                    <input
                      id="register-country"
                      type="text"
                      value={registerForm.country}
                      onChange={(e) => setRegisterForm({ ...registerForm, country: e.target.value })}
                      required={isFieldMandatory('job_seeker', 'country')}
                    />
                  </div>
                )}
              </>
            )}

            {registerForm.userType === 'company' && (
              <>
                {isFieldVisible('company', 'companyName') && (
                  <div className="form-group">
                    <label htmlFor="register-company-name">Company Name</label>
                    <input
                      id="register-company-name"
                      type="text"
                      value={registerForm.companyName}
                      onChange={(e) => setRegisterForm({ ...registerForm, companyName: e.target.value })}
                      required={isFieldMandatory('company', 'companyName')}
                    />
                  </div>
                )}

                {isFieldVisible('company', 'industry') && (
                  <div className="form-group">
                    <label htmlFor="register-industry">Industry</label>
                    <input
                      id="register-industry"
                      type="text"
                      value={registerForm.industry}
                      onChange={(e) => setRegisterForm({ ...registerForm, industry: e.target.value })}
                      required={isFieldMandatory('company', 'industry')}
                    />
                  </div>
                )}

                {isFieldVisible('company', 'location') && (
                  <div className="form-group">
                    <label htmlFor="register-company-location">Location</label>
                    <input
                      id="register-company-location"
                      type="text"
                      value={registerForm.city}
                      onChange={(e) => {
                        const location = e.target.value;
                        setRegisterForm((prev) => ({
                          ...prev,
                          city: location,
                          locations: location ? [location] : [],
                        }));
                      }}
                      required={isFieldMandatory('company', 'location')}
                    />
                  </div>
                )}

                {(isFieldVisible('company', 'companyWebsite') || isFieldVisible('company', 'phone')) && (
                  <div className="form-row">
                    {isFieldVisible('company', 'companyWebsite') && (
                      <div className="form-group">
                        <label htmlFor="register-company-website">Company Website</label>
                        <input
                          id="register-company-website"
                          type="url"
                          value={registerForm.companyWebsite}
                          onChange={(e) => setRegisterForm({ ...registerForm, companyWebsite: e.target.value })}
                          required={isFieldMandatory('company', 'companyWebsite')}
                        />
                      </div>
                    )}
                    {isFieldVisible('company', 'phone') && (
                      <div className="form-group">
                        <label htmlFor="register-company-phone">Phone</label>
                        <input
                          id="register-company-phone"
                          type="text"
                          value={registerForm.phone}
                          onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                          required={isFieldMandatory('company', 'phone')}
                        />
                      </div>
                    )}
                  </div>
                )}

                {(isFieldVisible('company', 'companySize') || isFieldVisible('company', 'verifiedStatus')) && (
                  <div className="form-row">
                    {isFieldVisible('company', 'companySize') && (
                      <div className="form-group">
                        <label htmlFor="register-company-size">Company Size</label>
                        <input
                          id="register-company-size"
                          type="number"
                          min="1"
                          value={registerForm.companySize}
                          onChange={(e) => setRegisterForm({ ...registerForm, companySize: e.target.value === '' ? '' : Number(e.target.value) })}
                          required={isFieldMandatory('company', 'companySize')}
                        />
                      </div>
                    )}
                    {isFieldVisible('company', 'verifiedStatus') && (
                      <div className="form-group">
                        <label htmlFor="register-verified-status">Verified Status</label>
                        <label className="checkbox-inline">
                          <input
                            id="register-verified-status"
                            type="checkbox"
                            checked={registerForm.verifiedStatus}
                            onChange={(e) => setRegisterForm({ ...registerForm, verifiedStatus: e.target.checked })}
                          />
                          Mark company as verified
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {isFieldVisible('company', 'companyLogoUrl') && (
                  <div className="form-group">
                    <label htmlFor="register-company-logo">Company Logo</label>
                    <input
                      id="register-company-logo"
                      type="file"
                      accept="image/*"
                      required={isFieldMandatory('company', 'companyLogoUrl') && !registerForm.companyLogoUrl}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const data = await readFileAsDataUrl(file);
                          setRegisterForm((prev) => ({ ...prev, companyLogoUrl: data }));
                        } catch {
                          showToast('error', 'Could not read company logo.');
                        }
                      }}
                    />
                  </div>
                )}
              </>
            )}
            <div className="form-group legal-consent-group">
              <label className="checkbox-inline legal-consent-checkbox" htmlFor="register-legal-consent">
                <input
                  id="register-legal-consent"
                  type="checkbox"
                  checked={registerForm.acceptedLegalPolicies}
                  onChange={(e) => setRegisterForm({ ...registerForm, acceptedLegalPolicies: e.target.checked })}
                  required
                />
                <span>
                  By creating an account, you agree to the{' '}
                  <Link to={webAppConfig.routes.terms} target="_blank" rel="noreferrer">Terms of Service</Link>{' '}
                  and{' '}
                  <Link to={webAppConfig.routes.privacy} target="_blank" rel="noreferrer">Privacy Policy</Link>.
                </span>
              </label>
            </div>
            <button type="submit" className="btn-primary btn-full" disabled={authLoading}>
              {authLoading ? 'Creating account...' : 'Create Account'}
            </button>
            <p className="auth-switch">
              Already have an account?{' '}
              <button type="button" className="link-btn" onClick={() => setAuthTab('login')}>
                Sign in
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );

  // ── Job Card (Seeker) ────────────────────────────────────────────────
  const renderJobCard = (job: Job) => {
    const requiredSkills = Array.isArray(job.requiredSkills) ? job.requiredSkills : [];

    return (
    <button key={job.id} className="job-card" onClick={() => setSelectedJob(job)}>
      <div className="job-card-top">
        <div className="company-initial">{getInitial(job.companyName)}</div>
        <div className="job-card-title">
          <h3>{job.title || 'Untitled Job'}</h3>
          <span className="company-meta"><strong className="company-name-strong">{job.companyName || 'Company'}</strong> • {job.location || 'Location TBD'}</span>
          <span className="company-meta company-rating-chip">{renderRatingSummary(job.company?.ratingAverage, job.company?.ratingCount)}</span>
        </div>
        <span className="rate">
          ${Number(job.rateAmount || 0).toFixed(2)}{job.rateType === 'hourly' ? '/hr' : ' fixed'}
        </span>
      </div>
      <p className="job-desc job-desc-pretty">{buildJobDescriptionCardPreview(job.description)}</p>
      <div className="chip-row">
        {requiredSkills.slice(0, 3).map((s) => (
          <span key={s} className="chip">{s}</span>
        ))}
        {requiredSkills.length > 3 && (
          <span className="chip chip-muted">+{requiredSkills.length - 3}</span>
        )}
      </div>
      <div className="job-card-footer">
        <span className="date-range">{fmtDateTime(job.startAt)} – {fmtDateTime(job.endAt)}</span>
        {job.latestApplicationStatus && (
          <span className={pillClass(job.latestApplicationStatus)}>{prettyApplicationStatus(job.latestApplicationStatus)}</span>
        )}
        {job.latestApplicationStatus === 'REJECTED' && job.reapplyAt && !job.canApply && (
          <span className="reapply-msg">You can reapply after {fmtDateTime(job.reapplyAt)}</span>
        )}
        {!job.latestApplicationStatus && job.canApply && <span className="card-cta">View & Apply →</span>}
      </div>
    </button>
    );
  };

  // ── Job Modal (Seeker) ───────────────────────────────────────────────
  const renderJobModal = () => {
    if (!selectedJob) return null;
    const job = selectedJob;
    const companyRevieweeId = job.companyUserId ?? job.company?.id;
    const hasApplied = !!job.latestApplicationStatus;
    const awaitingSeekerResponse = job.latestApplicationStatus === 'RESPONSE_AWAITED' && !!job.applicationId;
    const companyReviewsFromDetails = Array.isArray(job.company?.reviews)
      ? job.company!.reviews.map(normalizeJobReview)
      : [];
    const companyReviews = (companyReviewsFromDetails.length ? companyReviewsFromDetails : selectedJobReviews)
      .filter((review) => review.reviewee_user_id === companyRevieweeId);
    const existingSeekerReview = selectedJobReviews.find(
      (review) => review.reviewer_user_id === user?.id && review.reviewee_user_id === companyRevieweeId,
    );
    const statusTone =
      job.latestApplicationStatus === 'ACCEPTED' || job.latestApplicationStatus === 'COMPLETED'
        ? 'status-success'
        : job.latestApplicationStatus === 'REJECTED' || job.latestApplicationStatus === 'NO_SHOW'
          ? 'status-error'
          : 'status-pending';

    return (
      <div
        className="modal-overlay"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) setSelectedJob(null);
        }}
        onKeyDown={(e) => handleModalOverlayKeyDown(e, () => setSelectedJob(null))}
      >
        <div className="modal modal-wide job-details-modal" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <div className="modal-job-header modal-job-header-sticky">
            <div className="company-initial lg">{getInitial(job.companyName)}</div>
            <div className="modal-job-title">
              <h2>{job.title || 'Untitled Job'}</h2>
              <span className="company-meta">{job.companyName || 'Company'} • {job.location || 'Location TBD'}</span>
            </div>
            <span className="rate lg">
              ${Number(job.rateAmount || 0).toFixed(2)}{job.rateType === 'hourly' ? '/hr' : ' fixed'}
            </span>
            <button className="modal-close" onClick={() => setSelectedJob(null)}>×</button>
          </div>
          <div className="modal-detail-rows job-details-grid">
            <div className="detail-row">
              <span>Duration</span>
              <strong>{fmtDateTime(job.startAt)} – {fmtDateTime(job.endAt)}</strong>
            </div>
            <div className="detail-row">
              <span>Positions</span>
              <strong>{job.filledSlots} / {job.workerSlots} filled</strong>
            </div>
            <div className="detail-row">
              <span>Status</span>
              <span className={pillClass(job.status)}>{job.status}</span>
            </div>
          </div>
          <div className="detail-section">
            <span className="section-label">Description</span>
            {renderJobDescriptionContent(job.description, 'No description provided.')}
          </div>
          <div className="detail-section">
            <span className="section-label">Required Skills</span>
            <div className="chip-row">
              {(Array.isArray(job.requiredSkills) ? job.requiredSkills : []).map((s) => (
                <span key={s} className="chip">{s}</span>
              ))}
            </div>
          </div>
          <div className="modal-apply modal-apply-sticky">
            {hasApplied ? (
              <>
                <div className={`status-info lg ${statusTone}`}>
                  {job.latestApplicationStatus ? prettyApplicationStatus(job.latestApplicationStatus) : 'Application update unavailable'}
                  {job.rejectionReason ? ` • Reason: ${job.rejectionReason}` : ''}
                </div>
                {awaitingSeekerResponse && (
                  seekerRejectingAppId === job.applicationId ? (
                    <div className="reject-panel">
                      <label htmlFor={`seeker-reject-reason-${job.applicationId}`}>Reason for declining (optional)</label>
                      <textarea
                        id={`seeker-reject-reason-${job.applicationId}`}
                        className="reject-textarea"
                        value={seekerRejectReason}
                        onChange={(e) => setSeekerRejectReason(e.target.value)}
                        placeholder="Share context for the company"
                      />
                      <div className="reject-panel-actions">
                        <button
                          className="btn-danger"
                          disabled={respondLoading === job.applicationId}
                          onClick={() => handleRespondToOffer(job.applicationId!, 'rejected', seekerRejectReason)}
                        >
                          {respondLoading === job.applicationId ? 'Submitting...' : 'Confirm Decline'}
                        </button>
                        <button className="btn-ghost" onClick={() => { setSeekerRejectingAppId(null); setSeekerRejectReason(''); }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="applicant-actions">
                      <button
                        className="btn-success"
                        disabled={respondLoading === job.applicationId}
                        onClick={() => handleRespondToOffer(job.applicationId!, 'accepted')}
                      >
                        {respondLoading === job.applicationId ? 'Submitting...' : 'Accept Offer'}
                      </button>
                      <button
                        className="btn-danger-outline"
                        onClick={() => setSeekerRejectingAppId(job.applicationId!)}
                      >
                        Decline Offer
                      </button>
                    </div>
                  )
                )}
                {job.status === 'COMPLETED' && (job.latestApplicationStatus === 'ACCEPTED' || job.latestApplicationStatus === 'COMPLETED') && (
                  <div className="reject-panel" style={{ marginTop: 12 }}>
                    {existingSeekerReview && !isEditingSeekerReview ? (
                      <>
                        <div className="status-info status-success" style={{ marginBottom: 10 }}>
                          <strong>Your review:</strong> {existingSeekerReview.rating}/5
                          {existingSeekerReview.comment ? ` • ${existingSeekerReview.comment}` : ''}
                        </div>
                        <div className="reject-panel-actions">
                          <button
                            className="btn-ghost"
                            onClick={() => {
                              setSeekerReviewRating(existingSeekerReview.rating);
                              setSeekerReviewComment(existingSeekerReview.comment ?? '');
                              setIsEditingSeekerReview(true);
                            }}
                          >
                            Edit Review
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <label htmlFor="seeker-review-rating">Rate this company (1-5)</label>
                        <input
                          id="seeker-review-rating"
                          type="number"
                          min={1}
                          max={5}
                          value={seekerReviewRating}
                          onChange={(e) => setSeekerReviewRating(Number.parseInt(e.target.value || '5', 10) || 5)}
                        />
                        <label htmlFor="seeker-review-comment">Review (optional)</label>
                        <textarea
                          id="seeker-review-comment"
                          className="reject-textarea"
                          placeholder="Share your working experience"
                          value={seekerReviewComment}
                          onChange={(e) => setSeekerReviewComment(e.target.value)}
                        />
                        <div className="reject-panel-actions">
                          <button
                            className="btn-primary"
                            disabled={seekerReviewSubmitting}
                            onClick={() => {
                              handleSeekerReviewSubmit(job).catch(() => {});
                            }}
                          >
                            {seekerReviewSubmitting ? 'Submitting...' : existingSeekerReview ? 'Update Review' : 'Submit Review'}
                          </button>
                          {existingSeekerReview && (
                            <button className="btn-ghost" onClick={() => setIsEditingSeekerReview(false)}>
                              Cancel
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : job.canApply ? (
              <button
                className="btn-primary btn-apply"
                onClick={() => handleApply(job.id)}
                disabled={applyLoading}
              >
                {applyLoading ? 'Applying...' : 'Apply Now'}
              </button>
            ) : (
              <div className="status-info status-pending">This position is no longer accepting applications.</div>
            )}
          </div>
          <div className="detail-section">
            <span className="section-label">Company Reviews</span>
            <p className="detail-text">
              <strong>Company Rating:</strong>{' '}
              {typeof job.company?.ratingAverage === 'number' && (job.company?.ratingCount ?? 0) > 0
                ? `⭐ ${job.company.ratingAverage.toFixed(1)}`
                : '⭐ New'}
            </p>
            <p className="detail-text">
              <strong>Total Reviews:</strong> {job.company?.ratingCount ?? 0}
            </p>
            <button
              className="link-btn"
              onClick={() => {
                const element = document.getElementById('company-reviews-list');
                element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              type="button"
            >
              View All Reviews
            </button>
            <p className="detail-text" id="company-reviews-list" style={{ marginTop: 8 }}>
              {renderRatingSummary(job.company?.ratingAverage, job.company?.ratingCount)}
            </p>
            {selectedJobReviewsLoading ? (
              <p className="detail-text">Loading reviews...</p>
            ) : companyReviews.length === 0 ? (
              <p className="detail-text">No reviews yet for this company.</p>
            ) : (
              <div className="applicants-list">
                {companyReviews.map((review) => (
                  <div key={review.id} className="applicant-card">
                    <div className="applicant-top">
                      <div className="applicant-info">
                        <strong>{review.reviewer_name ?? 'Reviewer'}</strong>
                        <span>{fmtDateTime(review.created_at)}</span>
                      </div>
                      <span className="pill pill-confirmed">{renderStars(review.rating)} ({review.rating}/5)</span>
                    </div>
                    {review.comment && <p className="detail-text">Comment: {review.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Seeker View ──────────────────────────────────────────────────────
  const bestMatchJobs = React.useMemo(() => {
    const candidateBaseTokens = buildStrictSkillTokenSet(user?.skills ?? []);
    if (!candidateBaseTokens.size) {
      return [];
    }

    const seekerTokens = buildBestMatchTokenSet(user?.skills ?? []);
    const candidateDomain = classifyPrimaryBestMatchDomain(seekerTokens);
    if (!candidateDomain) return [];
    const techKeywordPattern = /\b(react|node(?:\.js)?|typescript|javascript|api|backend|frontend|software|developer|engineer|database)\b/i;

    const overlapCount = (left: Set<string>, right: Set<string>) => {
      let count = 0;
      left.forEach((token) => {
        if (right.has(token)) count += 1;
      });
      return count;
    };

    const scoreJob = (job: Job) => {
      const searchableJobTokens = buildBestMatchTokenSet([
        job.title,
        job.description,
        ...(job.requiredSkills ?? []),
        job.category ?? '',
      ]);
      const jobDomain = classifyPrimaryBestMatchDomain(searchableJobTokens);

      if (!jobDomain || jobDomain !== candidateDomain) {
        return 0;
      }

      if (candidateDomain === 'lawncare' && techKeywordPattern.test(`${job.title} ${job.description} ${(job.requiredSkills ?? []).join(' ')}`)) {
        return 0;
      }

      const totalOverlap = overlapCount(seekerTokens, searchableJobTokens);
      if (totalOverlap === 0) {
        return 0;
      }

      return totalOverlap / Math.max(1, candidateBaseTokens.size);
    };

    return [...jobs]
      .map((job) => ({ job, score: scoreJob(job) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.job);
  }, [jobs, user?.skills]);

  const filteredCompanyJobs = React.useMemo(() => {
    const query = companyJobFilters.search.trim().toLowerCase();
    const getCreatedTime = (job: CompanyJob) => new Date(job.createdAt ?? job.startAt).getTime();

    let next = [...companyJobs];
    if (query) {
      next = next.filter((job) =>
        [job.title, job.description, job.location, job.companyName]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(query)),
      );
    }

    if (companyJobFilters.status !== 'ALL') {
      next = next.filter((job) => job.status === companyJobFilters.status);
    }

    switch (companyJobFilters.sortBy) {
      case 'oldest':
        next.sort((a, b) => getCreatedTime(a) - getCreatedTime(b));
        break;
      case 'pay-high':
        next.sort((a, b) => b.rateAmount - a.rateAmount);
        break;
      case 'pay-low':
        next.sort((a, b) => a.rateAmount - b.rateAmount);
        break;
      case 'applicants-high':
        next.sort((a, b) => b.applications.length - a.applications.length);
        break;
      case 'newest':
      default:
        next.sort((a, b) => getCreatedTime(b) - getCreatedTime(a));
        break;
    }

    return next;
  }, [companyJobFilters.search, companyJobFilters.sortBy, companyJobFilters.status, companyJobs]);

  const hasPendingInvitations = myInvitations.length > 0;

  useEffect(() => {
    if (seekerTab === 'your-invitations' && !hasPendingInvitations) {
      setSeekerTab('my-applications');
    }
  }, [hasPendingInvitations, seekerTab]);

  const renderSeekerView = () => (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2>Applicant Workspace</h2>
          <p className="sub">Track performance, manage applications, and browse jobs</p>
        </div>
      </div>
      <div className="tabs">
        {getRoleTabItems('job_seeker').map((tabItem) => {
          if (tabItem.key === 'your-invitations' && !hasPendingInvitations) return null;
          const count =
            tabItem.key === 'browse' ? jobs.length
              : tabItem.key === 'best-match' ? bestMatchJobs.length
                : tabItem.key === 'my-applications' ? myApplications.length
                  : tabItem.key === 'your-invitations' ? myInvitations.length
                    : null;

          return (
            <button key={tabItem.key} className={`tab ${seekerTab === tabItem.key ? 'active' : ''}`} onClick={() => setSeekerTab(tabItem.key as SeekerTab)}>
              <span>{tabItem.label}</span>
              {typeof count === 'number' ? <span className="tab-count">{count}</span> : null}
            </button>
          );
        })}
      </div>
      {seekerTab === 'dashboard' && (
        <ApplicantDashboardPage loading={applicantDashboardLoading} data={applicantDashboard} />
      )}
      {seekerTab === 'browse' && (
        <>
          <div className="company-job-card" style={{ marginBottom: 12 }}>
            <div className="applicants-section">
              <div className="form-actions" style={{ justifyContent: 'space-between', borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
                <strong>Browse Filters</strong>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setBrowseFiltersCollapsed((prev) => !prev)}
                >
                  {browseFiltersCollapsed ? 'Show Filters' : 'Hide Filters'}
                </button>
              </div>

              {!browseFiltersCollapsed && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="browse-search">Search</label>
                      <input
                        id="browse-search"
                        value={browseFilters.search}
                        onChange={(e) => setBrowseFilters((prev) => ({ ...prev, search: e.target.value }))}
                        placeholder="Job title or description"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="browse-location">Location</label>
                      <input
                        id="browse-location"
                        value={browseFilters.location}
                        onChange={(e) => setBrowseFilters((prev) => ({ ...prev, location: e.target.value }))}
                        placeholder="City or remote"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="browse-skill">Skill</label>
                      <input
                        id="browse-skill"
                        value={browseFilters.skill}
                        onChange={(e) => setBrowseFilters((prev) => ({ ...prev, skill: e.target.value }))}
                        placeholder="e.g. React"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="browse-category">Category</label>
                      <input
                        id="browse-category"
                        value={browseFilters.category}
                        onChange={(e) => setBrowseFilters((prev) => ({ ...prev, category: e.target.value }))}
                        placeholder="e.g. Delivery"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="browse-min-pay">Min Pay</label>
                      <input
                        id="browse-min-pay"
                        type="number"
                        min="0"
                        value={browseFilters.minPay}
                        onChange={(e) => setBrowseFilters((prev) => ({ ...prev, minPay: e.target.value }))}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="browse-max-pay">Max Pay</label>
                      <input
                        id="browse-max-pay"
                        type="number"
                        min="0"
                        value={browseFilters.maxPay}
                        onChange={(e) => setBrowseFilters((prev) => ({ ...prev, maxPay: e.target.value }))}
                        placeholder="Any"
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn-primary" onClick={() => loadSeekerData().catch(() => {})}>Apply Filters</button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => {
                        setBrowseFilters({ search: '', location: '', skill: '', category: '', minPay: '', maxPay: '' });
                        globalThis.setTimeout(() => {
                          loadSeekerData().catch(() => {});
                        }, 0);
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="jobs-grid">
            {seekerDataLoading && jobs.length === 0 ? (
              Array.from({ length: 6 }).map((_, idx) => renderJobSkeletonCard(`browse-skeleton-${idx}`))
            ) : jobs.length === 0 ? (
              <div className="empty-state-block">
                <span className="empty-icon">📋</span>
                <p>No jobs available at the moment. Check back soon!</p>
              </div>
            ) : (
              jobs.map(renderJobCard)
            )}
          </div>
        </>
      )}
      {seekerTab === 'best-match' && (
        <div className="jobs-grid">
          {seekerDataLoading && bestMatchJobs.length === 0 ? (
            Array.from({ length: 6 }).map((_, idx) => renderJobSkeletonCard(`match-skeleton-${idx}`))
          ) : bestMatchJobs.length === 0 ? (
            <div className="empty-state-block">
              <span className="empty-icon">🎯</span>
              <p>No strong skill matches yet. Add more skills in your profile or check Browse Jobs.</p>
            </div>
          ) : (
            bestMatchJobs.map(renderJobCard)
          )}
        </div>
      )}
      {seekerTab === 'my-applications' && (
        <div className="company-jobs-list">
          <div className="jobs-grid">
            {seekerDataLoading && myApplications.length === 0 ? (
              Array.from({ length: 4 }).map((_, idx) => renderJobSkeletonCard(`apps-skeleton-${idx}`))
            ) : myApplications.length === 0 ? (
              <div className="empty-state-block">
                <span className="empty-icon">📝</span>
                <p>You haven't applied to any jobs yet.</p>
              </div>
            ) : (
              myApplications.map(renderJobCard)
            )}
          </div>
        </div>
      )}
      {seekerTab === 'your-invitations' && (
        <div className="company-jobs-list">
          <div className="company-job-card">
            <div className="company-job-header" style={{ cursor: 'default' }}>
              <div className="company-job-info">
                <h3>Your Invitations</h3>
                <div className="company-job-meta">
                  <span>Respond to invitations from companies</span>
                </div>
              </div>
              <div className="company-job-stats">
                <span className="badge-count badge-neutral">{myInvitations.length} pending</span>
              </div>
            </div>
            <div className="applicants-section">
              {invitationLoading ? (
                <p className="empty-inline">Loading invitations...</p>
              ) : myInvitations.length === 0 ? (
                <p className="empty-inline">No pending invitations right now.</p>
              ) : (
                <div className="applicants-list">
                  {myInvitations.map((invite) => (
                    <div key={`invite-${invite.id}`} className="applicant-card">
                      <div className="applicant-top">
                        <div className="applicant-info">
                          <strong>{invite.job.title}</strong>
                          <span>{invite.job.companyName} • {invite.job.location}</span>
                        </div>
                        <span className="pill pill-pending">Invite</span>
                      </div>
                      <p className="detail-text">Rate: ${invite.job.rateAmount.toFixed(2)}{invite.job.rateType === 'hourly' ? '/hr' : ' fixed'}</p>
                      <p className="detail-text">Duration: {fmtDateTime(invite.job.startAt)} – {fmtDateTime(invite.job.endAt)}</p>
                      {invite.note ? <p className="detail-text"><strong>Note:</strong> {invite.note}</p> : null}
                      <div className="applicant-actions">
                        <button
                          className="btn-success"
                          disabled={invitationRespondingId === invite.id}
                          onClick={() => handleRespondToInvitation(invite.id, 'accepted').catch(() => {})}
                        >
                          {invitationRespondingId === invite.id ? 'Submitting...' : 'Accept Invite'}
                        </button>
                        <button
                          className="btn-danger-outline"
                          disabled={invitationRespondingId === invite.id}
                          onClick={() => handleRespondToInvitation(invite.id, 'rejected').catch(() => {})}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {seekerTab === 'profile' && (
        <div className="company-job-card">
          <div className="company-job-header" style={{ cursor: 'default' }}>
            <div className="company-job-info">
              <h3>Applicant Profile</h3>
              <div className="company-job-meta">
                <span>{renderRatingSummary(receivedReviewSummary?.averageRating, receivedReviewSummary?.totalReviews)}</span>
                <span>Total Reviews: {receivedReviewSummary?.totalReviews ?? 0}</span>
              </div>
            </div>
          </div>
          <div className="applicants-section">
            <form className="create-job-form" onSubmit={handleProfileFormSubmit} style={{ marginBottom: 16 }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input value={profileForm.fullName} onChange={(e) => setProfileForm((prev) => ({ ...prev, fullName: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Phone (Optional)</label>
                  <input value={profileForm.phone} onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Skill Set (comma-separated) *</label>
                  <input
                    value={profileSkillsInput}
                    onChange={(e) => {
                      const nextInput = e.target.value;
                      setProfileSkillsInput(nextInput);
                      const parsed = parseAndValidateSkillsInput(nextInput);
                      setSkillsValidationErrors((prev) => ({ ...prev, profile: parsed.error ?? undefined }));
                      if (!parsed.error) {
                        setProfileForm((prev) => ({ ...prev, skills: parsed.skills.join(', ') }));
                      }
                    }}
                    placeholder="Comma-separated only"
                  />
                  {skillsValidationErrors.profile && <p className="empty-inline">{skillsValidationErrors.profile}</p>}
                </div>
                <div className="form-group">
                  <label>Resume URL (Optional)</label>
                  <input value={profileForm.resumeUrl} onChange={(e) => setProfileForm((prev) => ({ ...prev, resumeUrl: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City (Optional)</label>
                  <input value={profileForm.city} onChange={(e) => setProfileForm((prev) => ({ ...prev, city: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>State (Optional)</label>
                  <input value={profileForm.state} onChange={(e) => setProfileForm((prev) => ({ ...prev, state: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Country (Optional)</label>
                  <input value={profileForm.country} onChange={(e) => setProfileForm((prev) => ({ ...prev, country: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>New Password (Optional)</label>
                  <input type="password" value={profileForm.password} onChange={(e) => setProfileForm((prev) => ({ ...prev, password: e.target.value }))} />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={profileSaving}>{profileSaving ? 'Saving...' : 'Update Profile'}</button>
              </div>
            </form>
            <div className="company-job-card" style={{ borderColor: '#f97316', marginBottom: 16 }}>
              <div className="company-job-header" style={{ cursor: 'default' }}>
                <div className="company-job-info">
                  <h3>Settings &gt; Privacy &amp; Security &gt; Delete Account</h3>
                  <div className="company-job-meta">
                    <span>Soft-delete with a restore grace period.</span>
                  </div>
                </div>
              </div>
              <div className="applicants-section">
                <div className="form-row">
                  <div className="form-group">
                    <label>Current Password *</label>
                    <input
                      type="password"
                      value={deletionPassword}
                      onChange={(e) => setDeletionPassword(e.target.value)}
                      placeholder="Required to request deletion"
                    />
                  </div>
                  <div className="form-group">
                    <label>Reason (optional)</label>
                    <input
                      value={deletionReason}
                      onChange={(e) => setDeletionReason(e.target.value)}
                      placeholder="Tell us why you're leaving"
                    />
                  </div>
                </div>
                <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                  <button
                    type="button"
                    className="btn-danger-outline"
                    disabled={deletionRequesting}
                    onClick={() => { handleRequestAccountDeletion().catch(() => {}); }}
                  >
                    {deletionRequesting ? 'Requesting...' : 'Request Deletion OTP'}
                  </button>
                </div>
                {deletionOtpRequested && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Deletion OTP *</label>
                        <input
                          value={deletionOtp}
                          onChange={(e) => setDeletionOtp(e.target.value)}
                          placeholder="Enter 6-digit code"
                        />
                      </div>
                    </div>
                    {deletionRestoreDeadline && (
                      <p className="detail-text">Restore available until: {fmtDateTime(deletionRestoreDeadline)}</p>
                    )}
                    <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={deletionConfirming}
                        onClick={() => { handleConfirmAccountDeletion().catch(() => {}); }}
                      >
                        {deletionConfirming ? 'Deleting...' : 'Confirm Delete Account'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            {receivedReviewsLoading ? (
              <div className="applicants-list">
                <div className="applicant-card">Loading profile reviews...</div>
              </div>
            ) : receivedReviews.length === 0 ? (
              <p className="empty-inline">No reviews from companies yet.</p>
            ) : (
              <div className="applicants-list">
                {receivedReviews.map((review) => (
                  <div key={`seeker-review-${review.id}`} className="applicant-card">
                    <div className="applicant-top">
                      <div className="applicant-info">
                        <strong>{review.reviewer_name ?? 'Company reviewer'}</strong>
                        <span>{review.job_title ?? 'Job review'}</span>
                      </div>
                      <span className="pill pill-confirmed">⭐ {review.rating}/5</span>
                    </div>
                    {review.comment ? <p className="detail-text">{review.comment}</p> : null}
                    <p className="detail-text">Date: {fmtDateTime(review.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {renderJobModal()}
    </div>
  );

  // ── Company Job Card ─────────────────────────────────────────────────
  const renderCompanyJobCard = (job: CompanyJob) => {
    const isExpanded = expandedJobId === job.id;
    const pendingCount = job.applications.filter((a) => a.status === 'APPLIED' || a.status === 'RESPONSE_AWAITED').length;
    const confirmedCount = job.applications.filter((a) => a.status === 'ACCEPTED' || a.status === 'COMPLETED').length;
    const page = applicantPageByJob[job.id] ?? 1;
    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(job.applications.length / pageSize));
    const pagedApplications = job.applications.slice((page - 1) * pageSize, page * pageSize);

    return (
      <div key={job.id} className={`company-job-card ${isExpanded ? 'expanded' : ''}`}>
        <button className="company-job-header" onClick={() => setExpandedJobId(isExpanded ? null : job.id)}>
          <div className="company-job-info">
            <h3>{job.title}</h3>
            <div className="company-job-meta">
              <span>{job.location}</span>
              <span>${job.rateAmount.toFixed(2)}{job.rateType === 'hourly' ? '/hr' : ' fixed'}</span>
              <span>{fmtDateTime(job.startAt)} – {fmtDateTime(job.endAt)}</span>
            </div>
          </div>
          <div className="company-job-stats">
            <span className={pillClass(job.status)}>{job.status}</span>
            {pendingCount > 0 && <span className="badge-count badge-pending">{pendingCount} pending</span>}
            {confirmedCount > 0 && <span className="badge-count badge-confirmed">{confirmedCount} confirmed</span>}
            <span className="badge-count badge-neutral">Filled {job.filledSlots} / {job.workerSlots}</span>
            {job.status === 'OPEN' && job.applications.length === 0 && (
              <button className="btn-ghost" onClick={(e) => { e.stopPropagation(); openEditJobModal(job); }}>
                Edit
              </button>
            )}
            {job.status === 'OPEN' && job.applications.length === 0 && (
              <button
                className="btn-danger-outline"
                disabled={deleteJobLoadingId === job.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteJob(job).catch(() => {});
                }}
              >
                {deleteJobLoadingId === job.id ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button className="btn-ghost" onClick={(e) => { e.stopPropagation(); handleDuplicateJob(job); }}>
              Duplicate
            </button>
            <button
              className="btn-ghost"
              disabled={viewJobLoadingId === job.id}
              onClick={(e) => {
                e.stopPropagation();
                handleViewCompanyJob(job.id).catch(() => {});
              }}
            >
              {viewJobLoadingId === job.id ? 'Loading...' : 'View'}
            </button>
            <span className="expand-chevron">{isExpanded ? '▲' : '▼'}</span>
          </div>
        </button>
        {isExpanded && (
          <div className="applicants-section">
            <div className="applicants-label">Applicants ({job.applications.length})</div>
            <div className="applicants-list">
              {job.applications.length === 0 ? (
                <p className="empty-inline">No applications yet.</p>
              ) : (
                pagedApplications.map((app) => (
                  <div key={app.id} className={`applicant-card applicant-${app.status}`}>
                    <div className="applicant-top">
                      <div className="applicant-avatar">{getInitial(app.applicant_name)}</div>
                      <div className="applicant-info">
                        <button
                          className="applicant-name-link"
                          onClick={() => setExpandedApplicantId(expandedApplicantId === app.id ? null : app.id)}
                        >
                          <strong>{app.applicant_name || 'Applicant'}</strong>
                          <span className="applicant-name-toggle">
                            {expandedApplicantId === app.id ? 'Hide profile ▲' : 'View profile ▼'}
                          </span>
                        </button>
                        <span>{app.applicant_email || 'No email provided'}</span>
                      </div>
                      <span className={pillClass(app.status)}>{prettyApplicationStatus(app.status)}</span>
                    </div>
                    {expandedApplicantId === app.id && (
                      <div className="applicant-profile-panel">
                        {app.applicant_profile_picture_url && (
                          <p>
                            <strong>Profile picture:</strong>{' '}
                            <a href={app.applicant_profile_picture_url} target="_blank" rel="noreferrer">Open image</a>
                          </p>
                        )}
                        {app.applicant_phone && <p><strong>Phone:</strong> {app.applicant_phone}</p>}
                        {(app.applicant_city || app.applicant_state || app.applicant_country) && (
                          <p>
                            <strong>Location:</strong> {[app.applicant_city, app.applicant_state, app.applicant_country].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {app.applicant_availability && <p><strong>Availability:</strong> {app.applicant_availability}</p>}
                        {typeof app.applicant_work_experience_years === 'number' && (
                          <p><strong>Work experience:</strong> {app.applicant_work_experience_years} years</p>
                        )}
                        {(app.applicant_expected_rate_min || app.applicant_expected_rate_max) && (
                          <p><strong>Expected Rate:</strong> ${app.applicant_expected_rate_min ?? '?'} – ${app.applicant_expected_rate_max ?? '?'}</p>
                        )}
                        {app.applicant_skills && app.applicant_skills.length > 0 && (
                          <p><strong>Skills:</strong> {app.applicant_skills.join(', ')}</p>
                        )}
                        {app.applicant_resume_url && (
                          (() => {
                            const resumeHref = normalizeResumeUrl(app.applicant_resume_url);
                            if (!resumeHref) return null;
                            return (
                          <p>
                            <strong>Resume:</strong>{' '}
                            <a href={resumeHref} target="_blank" rel="noreferrer">Open resume</a>
                            {' • '}
                            <a href={resumeHref} download={buildResumeFilename(app.applicant_name)}>Download resume</a>
                          </p>
                            );
                          })()
                        )}
                        {(typeof app.applicant_rating_average === 'number' || typeof app.applicant_rating_count === 'number') && (
                          <p>
                            <strong>Applicant Rating:</strong>{' '}
                            {typeof app.applicant_rating_average === 'number'
                              ? `⭐ ${app.applicant_rating_average.toFixed(1)}`
                              : '⭐ New'}{' '}
                            • Total Reviews: {app.applicant_rating_count ?? 0}
                          </p>
                        )}
                        {Array.isArray(app.applicant_reviews) && app.applicant_reviews.length > 0 && (
                          <div>
                            <strong>View Reviews:</strong>
                            <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                              {app.applicant_reviews
                                .map((entry) => formatLegacyReviewEntry(entry))
                                .filter((entry): entry is string => Boolean(entry))
                                .map((entry, index) => (
                                  <li key={`${app.id}-review-${index}`}>{entry}</li>
                                ))}
                            </ul>
                          </div>
                        )}
                        {app.applicant_bio && <p><strong>Bio:</strong> {app.applicant_bio}</p>}
                      </div>
                    )}
                    {(app.status === 'APPLIED' || app.status === 'RESPONSE_AWAITED' || app.status === 'ACCEPTED') && (
                      <>
                        {rejectingAppId === app.id ? (
                          <div className="reject-panel">
                            <label htmlFor={`company-reject-reason-${app.id}`}>
                              {companyActionType === 'NO_SHOW' ? 'No-show reason (optional)' : 'Rejection reason (optional)'}
                            </label>
                            <textarea
                              id={`company-reject-reason-${app.id}`}
                              className="reject-textarea"
                              placeholder="Provide feedback for the applicant..."
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                            />
                            <div className="reject-panel-actions">
                              <button
                                className="btn-danger"
                                disabled={reviewLoading === app.id}
                                onClick={() =>
                                  handleCompanyApplicantAction(
                                    app.id,
                                    companyActionType === 'NO_SHOW' ? 'NO_SHOW' : 'REJECT_CANDIDATE',
                                    rejectReason,
                                  )
                                }
                              >
                                {reviewLoading === app.id
                                  ? companyActionType === 'NO_SHOW'
                                    ? 'Saving...'
                                    : 'Rejecting...'
                                  : companyActionType === 'NO_SHOW'
                                    ? 'Confirm No Show'
                                    : 'Confirm Reject'}
                              </button>
                              <button
                                className="btn-ghost"
                                onClick={() => {
                                  setRejectingAppId(null);
                                  setCompanyActionType('REJECT_CANDIDATE');
                                  setRejectReason('');
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="applicant-actions">
                            {app.status === 'APPLIED' && (
                              <>
                                <button
                                  className="btn-success"
                                  disabled={reviewLoading === app.id}
                                  onClick={() => handleCompanyApplicantAction(app.id, 'CONFIRM_CANDIDATE')}
                                >
                                  {reviewLoading === app.id ? 'Confirming...' : 'Confirm'}
                                </button>
                                <button
                                  className="btn-danger-outline"
                                  onClick={() => {
                                    setCompanyActionType('REJECT_CANDIDATE');
                                    setRejectingAppId(app.id);
                                  }}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {app.status === 'ACCEPTED' && (
                              <>
                                <button
                                  className="btn-success"
                                  disabled={reviewLoading === app.id}
                                  onClick={() => handleCompanyApplicantAction(app.id, 'MARK_COMPLETED')}
                                >
                                  {reviewLoading === app.id ? 'Saving...' : 'Mark Completed'}
                                </button>
                                <button
                                  className="btn-danger-outline"
                                  onClick={() => {
                                    setCompanyActionType('NO_SHOW');
                                    setRejectingAppId(app.id);
                                  }}
                                >
                                  No Show
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {app.status === 'REJECTED' && app.rejection_reason && (
                      <div className="status-info status-error">
                        <strong>Rejection reason:</strong> {app.rejection_reason}
                      </div>
                    )}
                    {job.status === 'COMPLETED' && (app.status === 'ACCEPTED' || app.status === 'COMPLETED') && app.job_seeker_user_id && (
                      <div className="reject-panel" style={{ marginTop: 12 }}>
                        {(() => {
                          const existingReview = (job.reviews ?? []).find(
                            (review) => review.reviewer_user_id === user?.id && review.reviewee_user_id === app.job_seeker_user_id,
                          );

                          if (existingReview && !companyReviewEditingByApp[app.id]) {
                            return (
                              <>
                                <div className="status-info status-success" style={{ marginBottom: 10 }}>
                                  <strong>Your review:</strong> {existingReview.rating}/5
                                  {existingReview.comment ? ` • ${existingReview.comment}` : ''}
                                </div>
                                <div className="reject-panel-actions">
                                  <button
                                    className="btn-ghost"
                                    onClick={() => {
                                      setCompanyReviewDrafts((prev) => ({
                                        ...prev,
                                        [app.id]: {
                                          rating: existingReview.rating,
                                          comment: existingReview.comment ?? '',
                                        },
                                      }));
                                      setCompanyReviewEditingByApp((prev) => ({ ...prev, [app.id]: true }));
                                    }}
                                  >
                                    Edit Review
                                  </button>
                                </div>
                              </>
                            );
                          }

                          return (
                            <>
                              <label htmlFor={`company-review-rating-${app.id}`}>Rate worker (1-5)</label>
                              <input
                                id={`company-review-rating-${app.id}`}
                                type="number"
                                min={1}
                                max={5}
                                value={companyReviewDrafts[app.id]?.rating ?? 5}
                                onChange={(e) => {
                                  const rating = Number.parseInt(e.target.value || '5', 10) || 5;
                                  setCompanyReviewDrafts((prev) => ({
                                    ...prev,
                                    [app.id]: { rating, comment: prev[app.id]?.comment ?? '' },
                                  }));
                                }}
                              />
                              <label htmlFor={`company-review-comment-${app.id}`}>Review (optional)</label>
                              <textarea
                                id={`company-review-comment-${app.id}`}
                                className="reject-textarea"
                                placeholder="Share feedback for this worker"
                                value={companyReviewDrafts[app.id]?.comment ?? ''}
                                onChange={(e) => {
                                  const comment = e.target.value;
                                  setCompanyReviewDrafts((prev) => ({
                                    ...prev,
                                    [app.id]: { rating: prev[app.id]?.rating ?? 5, comment },
                                  }));
                                }}
                              />
                              <div className="reject-panel-actions">
                                <button
                                  className="btn-primary"
                                  disabled={companyReviewSubmittingId === app.id}
                                  onClick={() => {
                                    handleCompanyReviewSubmit(job.id, app.id, app.job_seeker_user_id!).catch(() => {});
                                  }}
                                >
                                  {companyReviewSubmittingId === app.id ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
                                </button>
                                {existingReview && (
                                  <button
                                    className="btn-ghost"
                                    onClick={() => setCompanyReviewEditingByApp((prev) => ({ ...prev, [app.id]: false }))}
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            {job.applications.length > 10 && (
              <div className="applicants-pagination">
                <button
                  className="btn-ghost"
                  disabled={page <= 1}
                  onClick={() => setApplicantPageByJob((prev) => ({ ...prev, [job.id]: Math.max(1, page - 1) }))}
                >
                  Previous
                </button>
                <span>Page {page} / {totalPages}</span>
                <button
                  className="btn-ghost"
                  disabled={page >= totalPages}
                  onClick={() => setApplicantPageByJob((prev) => ({ ...prev, [job.id]: Math.min(totalPages, page + 1) }))}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDuplicateJobModal = () => {
    if (!showDuplicateJobModal) return null;

    return (
      <div
        className="modal-overlay"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) setShowDuplicateJobModal(false);
        }}
        onKeyDown={(e) => handleModalOverlayKeyDown(e, () => setShowDuplicateJobModal(false))}
      >
        <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowDuplicateJobModal(false)}>×</button>
          <h2 className="modal-form-title">Duplicate Job (Edit before create)</h2>
          <form className="create-job-form" onSubmit={submitDuplicateJob}>
            <div className="form-row">
              <div className="form-group">
                <label>Job Title</label>
                <input value={duplicateJobForm.title} onChange={(e) => setDuplicateJobForm((prev) => ({ ...prev, title: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input value={duplicateJobForm.location} onChange={(e) => setDuplicateJobForm((prev) => ({ ...prev, location: e.target.value }))} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date & Time</label>
                <input type="datetime-local" value={duplicateJobForm.startAt} onChange={(e) => setDuplicateJobForm((prev) => ({ ...prev, startAt: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label>End Date & Time</label>
                <input type="datetime-local" value={duplicateJobForm.endAt} onChange={(e) => setDuplicateJobForm((prev) => ({ ...prev, endAt: e.target.value }))} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Rate Type</label>
                <select value={duplicateJobForm.rateType} onChange={(e) => setDuplicateJobForm((prev) => ({ ...prev, rateType: e.target.value as 'hourly' | 'fixed' }))}>
                  <option value="hourly">Hourly</option>
                  <option value="fixed">Fixed</option>
                </select>
              </div>
              <div className="form-group">
                <label>Pay</label>
                <input type="number" min="0" step="0.01" value={duplicateJobForm.rateAmount || ''} onChange={(e) => setDuplicateJobForm((prev) => ({ ...prev, rateAmount: Number(e.target.value) || 0 }))} required />
              </div>
            </div>
            <div className="form-group">
              <label>Slots</label>
              <input type="number" min="1" value={duplicateJobForm.workerSlots} onChange={(e) => setDuplicateJobForm((prev) => ({ ...prev, workerSlots: Number(e.target.value) || 1 }))} required />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowDuplicateJobModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={duplicateJobLoading}>
                {duplicateJobLoading ? 'Creating...' : 'Create Duplicated Job'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderCompanyJobDetailsModal = () => {
    if (!selectedCompanyJob) return null;

    const job = selectedCompanyJob;
    return (
      <div
        className="modal-overlay"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) setSelectedCompanyJob(null);
        }}
        onKeyDown={(e) => handleModalOverlayKeyDown(e, () => setSelectedCompanyJob(null))}
      >
        <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setSelectedCompanyJob(null)}>×</button>
          <h2 className="modal-form-title">Job Details</h2>
          <div className="modal-detail-rows">
            <div className="detail-row"><span>Title</span><strong>{job.title}</strong></div>
            <div className="detail-row"><span>Company</span><strong>{job.companyName}</strong></div>
            <div className="detail-row"><span>Status</span><strong>{job.status}</strong></div>
            <div className="detail-row"><span>Location</span><strong>{job.location}</strong></div>
            <div className="detail-row"><span>Rate</span><strong>${job.rateAmount.toFixed(2)}{job.rateType === 'hourly' ? '/hr' : ' fixed'}</strong></div>
            <div className="detail-row"><span>Duration</span><strong>{fmtDateTime(job.startAt)} – {fmtDateTime(job.endAt)}</strong></div>
            <div className="detail-row"><span>Slots</span><strong>{job.filledSlots} / {job.workerSlots} filled</strong></div>
            <div className="detail-row"><span>Applicants</span><strong>{job.applications.length}</strong></div>
          </div>
          <div className="detail-section">
            <span className="section-label">Description</span>
            {renderJobDescriptionContent(job.description, 'No description provided.')}
          </div>
          <div className="detail-section">
            <span className="section-label">Required Skills</span>
            <div className="chip-row">
              {(job.requiredSkills ?? []).map((skill) => (
                <span key={skill} className="chip">{skill}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Create Job Modal ─────────────────────────────────────────────────
  const renderCreateJobModal = () => {
    if (!showCreateJobModal) return null;
    return (
      <div
        className="modal-overlay"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) setShowCreateJobModal(false);
        }}
        onKeyDown={(e) => handleModalOverlayKeyDown(e, () => setShowCreateJobModal(false))}
      >
        <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowCreateJobModal(false)}>×</button>
          <h2 className="modal-form-title">Post a New Job</h2>
          <form className="create-job-form" onSubmit={handleCreateJob}>
            <div className="form-group">
              <label>Company Name</label>
              <input type="text" value={jobForm.companyName} readOnly disabled required />
              <p className="empty-inline">Company name is auto-filled from your company profile.</p>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} required />
              <div className="form-actions" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0, justifyContent: 'flex-start' }}>
                <button type="button" className="btn-primary" disabled={aiDescriptionLoading} onClick={() => { handleParseFromDescription('typed').catch(() => {}); }}>
                  {aiDescriptionLoading ? 'Generating...' : 'Generate with AI'}
                </button>
                {!voiceListening ? (
                  <button type="button" className="btn-ghost" onClick={handleStartVoiceCapture}>Record here</button>
                ) : (
                  <button type="button" className="btn-danger-outline" onClick={handleStopVoiceCapture}>Stop recording</button>
                )}
                {voiceTaskLoading && <span className="empty-inline">Parsing voice details…</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Job Title</label>
              <input type="text" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input type="text" value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Required Skills (comma-separated)</label>
              <input
                type="text"
                value={jobSkillsInput}
                onChange={(e) => {
                  const nextInput = e.target.value;
                  setJobSkillsInput(nextInput);
                  const parsed = parseAndValidateSkillsInput(nextInput);
                  setSkillsValidationErrors((prev) => ({ ...prev, createJob: parsed.error ?? undefined }));
                  if (!parsed.error) {
                    setJobForm((prev) => ({ ...prev, requiredSkills: parsed.skills }));
                  }
                }}
              />
              {skillsValidationErrors.createJob && <p className="empty-inline">{skillsValidationErrors.createJob}</p>}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Rate Type</label>
                <div className="toggle-group">
                  <button type="button" className={`toggle-btn ${jobForm.rateType === 'hourly' ? 'active' : ''}`} onClick={() => setJobForm({ ...jobForm, rateType: 'hourly' })}>Hourly</button>
                  <button type="button" className={`toggle-btn ${jobForm.rateType === 'fixed' ? 'active' : ''}`} onClick={() => setJobForm({ ...jobForm, rateType: 'fixed' })}>Fixed</button>
                </div>
              </div>
              <div className="form-group">
                <label>Rate Amount ($)</label>
                <input type="number" min="0" step="0.01" value={jobForm.rateAmount || ''} onChange={(e) => setJobForm({ ...jobForm, rateAmount: parseFloat(e.target.value) || 0 })} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date & Time</label>
                <input type="datetime-local" value={jobForm.startAt} onChange={(e) => setJobForm({ ...jobForm, startAt: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>End Date & Time</label>
                <input type="datetime-local" value={jobForm.endAt} onChange={(e) => setJobForm({ ...jobForm, endAt: e.target.value })} required />
              </div>
            </div>
            <div className="form-group">
              <label>Worker Slots</label>
              <input type="number" min="1" value={jobForm.workerSlots} onChange={(e) => setJobForm({ ...jobForm, workerSlots: parseInt(e.target.value, 10) || 1 })} required />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowCreateJobModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={createJobLoading}>{createJobLoading ? 'Creating...' : 'Create Job'}</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderEditJobModal = () => {
    if (!showEditJobModal) return null;

    return (
      <div
        className="modal-overlay"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) setShowEditJobModal(false);
        }}
        onKeyDown={(e) => handleModalOverlayKeyDown(e, () => setShowEditJobModal(false))}
      >
        <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowEditJobModal(false)}>×</button>
          <h2 className="modal-form-title">Edit Job</h2>
          <form className="create-job-form" onSubmit={handleEditJobSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  value={editJobForm.title}
                  onChange={(e) => setEditJobForm({ ...editJobForm, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={editJobForm.companyName}
                  onChange={(e) => setEditJobForm({ ...editJobForm, companyName: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={editJobForm.location}
                onChange={(e) => setEditJobForm({ ...editJobForm, location: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editJobForm.description}
                onChange={(e) => setEditJobForm({ ...editJobForm, description: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Required Skills (comma-separated)</label>
              <input
                type="text"
                value={editJobSkillsInput}
                onChange={(e) => {
                  const nextInput = e.target.value;
                  setEditJobSkillsInput(nextInput);
                  const parsed = parseAndValidateSkillsInput(nextInput);
                  setSkillsValidationErrors((prev) => ({ ...prev, editJob: parsed.error ?? undefined }));
                  if (!parsed.error) {
                    setEditJobForm({ ...editJobForm, requiredSkills: parsed.skills });
                  }
                }}
              />
              {skillsValidationErrors.editJob && <p className="empty-inline">{skillsValidationErrors.editJob}</p>}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date & Time</label>
                <input
                  type="datetime-local"
                  value={editJobForm.startAt}
                  onChange={(e) => setEditJobForm({ ...editJobForm, startAt: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date & Time</label>
                <input
                  type="datetime-local"
                  value={editJobForm.endAt}
                  onChange={(e) => setEditJobForm({ ...editJobForm, endAt: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Rate Type</label>
                <div className="toggle-group">
                  <button
                    type="button"
                    className={`toggle-btn ${editJobForm.rateType === 'hourly' ? 'active' : ''}`}
                    onClick={() => setEditJobForm({ ...editJobForm, rateType: 'hourly' })}
                  >
                    Hourly
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${editJobForm.rateType === 'fixed' ? 'active' : ''}`}
                    onClick={() => setEditJobForm({ ...editJobForm, rateType: 'fixed' })}
                  >
                    Fixed
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Rate Amount ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editJobForm.rateAmount || ''}
                  onChange={(e) => setEditJobForm({ ...editJobForm, rateAmount: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Worker Slots</label>
              <input
                type="number"
                min="1"
                value={editJobForm.workerSlots}
                onChange={(e) => setEditJobForm({ ...editJobForm, workerSlots: parseInt(e.target.value, 10) || 1 })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => setShowEditJobModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={editJobLoading}>
                {editJobLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderCreateJobConfirmationModal = () => {
    if (!showCreateJobConfirmation || !pendingCreateJobForm) return null;

    const missingFields: string[] = [];
    if (!pendingCreateJobForm.title.trim()) missingFields.push('Title');
    if (!pendingCreateJobForm.description.trim()) missingFields.push('Description');
    if (!pendingCreateJobForm.location.trim()) missingFields.push('Location');
    if (!(pendingCreateJobForm.requiredSkills ?? []).length) missingFields.push('Required Skills');
    if (!(pendingCreateJobForm.rateAmount > 0)) missingFields.push('Rate Amount ($)');
    if (!pendingCreateJobForm.rateType) missingFields.push('Rate Type');
    if (!pendingCreateJobForm.startAt.trim()) missingFields.push('Start Date & Time');
    if (!pendingCreateJobForm.endAt.trim()) missingFields.push('End Date & Time');
    if (!(pendingCreateJobForm.workerSlots > 0)) missingFields.push('Worker Slots');

    return (
      <div
        className="modal-overlay"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) setShowCreateJobConfirmation(false);
        }}
        onKeyDown={(e) => handleModalOverlayKeyDown(e, () => setShowCreateJobConfirmation(false))}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setShowCreateJobConfirmation(false)}>×</button>
          <h2 className="modal-form-title">Confirm Job Creation</h2>
          <p className="detail-text">Do you want to proceed with this job using the parsed and reviewed details?</p>
          {missingFields.length > 0 && (
            <div className="status-info status-pending">
              Missing or ambiguous fields: {missingFields.join(', ')}. Please review before creating the job.
            </div>
          )}
          <div className="modal-detail-rows">
            <div className={`detail-row ${pendingCreateJobForm.companyName ? '' : 'detail-row-missing'}`}><span>Company</span><strong>{pendingCreateJobForm.companyName || '—'}</strong></div>
            <div className={`detail-row ${pendingCreateJobForm.title ? '' : 'detail-row-missing'}`}><span>Title</span><strong>{pendingCreateJobForm.title || '—'}</strong></div>
            <div className={`detail-row ${pendingCreateJobForm.location ? '' : 'detail-row-missing'}`}><span>Location</span><strong>{pendingCreateJobForm.location || '—'}</strong></div>
            <div className={`detail-row ${pendingCreateJobForm.startAt ? '' : 'detail-row-missing'}`}><span>Start</span><strong>{pendingCreateJobForm.startAt || '—'}</strong></div>
            <div className={`detail-row ${pendingCreateJobForm.endAt ? '' : 'detail-row-missing'}`}><span>End</span><strong>{pendingCreateJobForm.endAt || '—'}</strong></div>
            <div className={`detail-row ${pendingCreateJobForm.rateAmount > 0 ? '' : 'detail-row-missing'}`}><span>Rate</span><strong>${pendingCreateJobForm.rateAmount || 0} ({pendingCreateJobForm.rateType || '—'})</strong></div>
            <div className={`detail-row ${pendingCreateJobForm.workerSlots > 0 ? '' : 'detail-row-missing'}`}><span>Slots</span><strong>{pendingCreateJobForm.workerSlots || '—'}</strong></div>
            <div className={`detail-row ${pendingCreateJobForm.description ? '' : 'detail-row-missing'}`}><span>Description</span><strong>{pendingCreateJobForm.description || '—'}</strong></div>
          </div>
          <div className="chip-row">
            {(pendingCreateJobForm.requiredSkills ?? []).map((skill) => (
              <span key={skill} className="chip">{skill}</span>
            ))}
          </div>
          <div className="form-actions">
            <button type="button" className="btn-ghost" onClick={() => setShowCreateJobConfirmation(false)}>Review Again</button>
            <button type="button" className="btn-primary" disabled={createJobLoading} onClick={() => { confirmCreateJob().catch(() => {}); }}>
              {createJobLoading ? 'Creating...' : 'Yes, Create Job'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Company View ─────────────────────────────────────────────────────
  const renderCompanyView = () => (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2>Company Workspace</h2>
          <p className="sub">Run hiring operations from one place</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => { setCompanyTab('jobs'); openCreateJobModal(); }}>
            + Post New Job
          </button>
        </div>
      </div>
      <div className="tabs">
        {getRoleTabItems('company').map((tabItem) => {
          const count =
            tabItem.key === 'jobs' ? companyJobs.length
              : tabItem.key === 'best-matches' ? companyBestMatches.reduce((sum, group) => sum + group.candidates.length, 0)
                : null;
          return (
            <button key={tabItem.key} className={`tab ${companyTab === tabItem.key ? 'active' : ''}`} onClick={() => setCompanyTab(tabItem.key as CompanyTab)}>
              <span>{tabItem.label}</span>
              {typeof count === 'number' ? <span className="tab-count">{count}</span> : null}
            </button>
          );
        })}
      </div>

      {companyTab === 'dashboard' && (
        <CompanyDashboardPage
          loading={companyDashboardLoading}
          data={companyDashboard}
          onOpenJob={(jobId) => {
            setCompanyTab('jobs');
            setExpandedJobId(jobId);
          }}
        />
      )}

      {companyTab === 'jobs' && (
        <div className="company-jobs-list">
          <div className="company-job-card" style={{ marginBottom: 12 }}>
            <div className="applicants-section">
              <div className="form-actions" style={{ justifyContent: 'space-between', borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
                <strong>Job Management Filters</strong>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => setCompanyFiltersCollapsed((prev) => !prev)}
                >
                  {companyFiltersCollapsed ? 'Show Filters' : 'Hide Filters'}
                </button>
              </div>

              {!companyFiltersCollapsed && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company-jobs-search">Search Jobs</label>
                    <input
                      id="company-jobs-search"
                      value={companyJobFilters.search}
                      onChange={(e) => setCompanyJobFilters((prev) => ({ ...prev, search: e.target.value }))}
                      placeholder="Title, location, or description"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="company-jobs-status">Status</label>
                    <select
                      id="company-jobs-status"
                      value={companyJobFilters.status}
                      onChange={(e) => setCompanyJobFilters((prev) => ({ ...prev, status: e.target.value as 'ALL' | JobStatus }))}
                    >
                      <option value="ALL">All</option>
                      <option value="OPEN">Open</option>
                      <option value="IN_SELECTION">In Selection</option>
                      <option value="FILLED">Filled</option>
                      <option value="COMPLETED">Completed</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="company-jobs-sort">Sort By</label>
                    <select
                      id="company-jobs-sort"
                      value={companyJobFilters.sortBy}
                      onChange={(e) => setCompanyJobFilters((prev) => ({ ...prev, sortBy: e.target.value as typeof prev.sortBy }))}
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                      <option value="applicants-high">Most applicants</option>
                      <option value="pay-high">Highest pay</option>
                      <option value="pay-low">Lowest pay</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
          {companyDataLoading ? (
            <div className="empty-state-card">
              <span className="empty-icon">⏳</span>
              <h3>Loading jobs...</h3>
              <p>Fetching your jobs and applicants.</p>
            </div>
          ) : filteredCompanyJobs.length === 0 ? (
            <div className="empty-state-card">
              <span className="empty-icon">📌</span>
              <h3>No jobs found for current filters</h3>
              <p>Try adjusting search, status, or sorting.</p>
              <button className="btn-primary" onClick={openCreateJobModal}>
                Post New Job
              </button>
            </div>
          ) : (
            filteredCompanyJobs.map(renderCompanyJobCard)
          )}
        </div>
      )}

      {companyTab === 'best-matches' && (
        <div className="company-jobs-list">
          {companyBestMatchesLoading ? (
            <div className="empty-state-card">
              <span className="empty-icon">🎯</span>
              <h3>Finding best matches...</h3>
              <p>Looking for candidates by skills and location.</p>
            </div>
          ) : companyBestMatches.length === 0 ? (
            <div className="empty-state-card">
              <span className="empty-icon">🔎</span>
              <h3>No best matches yet</h3>
              <p>Post jobs with clear skills and location to see matching candidates.</p>
            </div>
          ) : (
            companyBestMatches.map((group) => (
              <div key={`best-match-job-${group.job.id}`} className="company-job-card">
                <div className="company-job-header" style={{ cursor: 'default' }}>
                  <div className="company-job-info">
                    <h3>{group.job.title}</h3>
                    <div className="company-job-meta">
                      <span>{group.job.location}</span>
                      <span>${group.job.rateAmount.toFixed(2)}{group.job.rateType === 'hourly' ? '/hr' : ' fixed'}</span>
                      <span>{group.job.status}</span>
                    </div>
                  </div>
                  <div className="company-job-stats">
                    <span className="badge-count badge-neutral">{group.candidates.length} matches</span>
                  </div>
                </div>
                <div className="applicants-section">
                  {group.candidates.length === 0 ? (
                    <p className="empty-inline">No matching candidates for this job yet.</p>
                  ) : (
                    <div className="applicants-list">
                      {group.candidates.map((candidate) => {
                        const key = `${group.job.id}-${candidate.userId}`;
                        const inviteDisabled = !candidate.eligibleForInvite || candidate.inviteStatus === 'PENDING';
                        const inviteCtaLabel =
                          inviteLoadingKey === key
                            ? 'Inviting...'
                            : candidate.inviteStatus === 'PENDING'
                              ? 'Invite Sent'
                              : candidate.alreadyApplicant
                                ? 'Already Applicant'
                                : candidate.latestApplicationStatus === 'REJECTED' && !candidate.autoRejectedByFilledPosition
                                  ? 'Not Eligible to Re-invite'
                                  : !candidate.eligibleForInvite
                                    ? 'No Slots Available'
                                    : 'Invite to Apply';

                        return (
                          <div key={key} className="applicant-card">
                            <div className="applicant-top">
                              <div className="applicant-avatar">{getInitial(candidate.fullName)}</div>
                              <div className="applicant-info">
                                <strong>{candidate.fullName ?? candidate.email}</strong>
                                <span>{candidate.email}</span>
                              </div>
                              <span className="pill pill-confirmed">{candidate.matchScore}% match</span>
                            </div>
                            <p className="detail-text">
                              <strong>Location:</strong> {[candidate.city, candidate.state, candidate.country].filter(Boolean).join(', ') || 'Not provided'}
                            </p>
                            <p className="detail-text">
                              <strong>Matched skills:</strong> {candidate.matchedSkills.join(', ')}
                            </p>
                            {candidate.resumeUrl && (
                              (() => {
                                const resumeHref = normalizeResumeUrl(candidate.resumeUrl);
                                if (!resumeHref) return null;
                                return (
                              <p className="detail-text">
                                <strong>Resume:</strong>{' '}
                                <a href={resumeHref} target="_blank" rel="noreferrer">Open resume</a>
                              </p>
                                );
                              })()
                            )}
                            <div className="applicant-actions">
                              <button
                                className="btn-primary"
                                disabled={inviteDisabled || inviteLoadingKey === key}
                                onClick={() => {
                                  handleInviteCandidate(group.job.id, candidate).catch(() => {});
                                }}
                              >
                                {inviteCtaLabel}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {companyTab === 'profile' && (
        <div className="company-job-card">
          <div className="company-job-header" style={{ cursor: 'default' }}>
            <div className="company-job-info">
              <h3>Company Profile</h3>
              <div className="company-job-meta">
                <span>{renderRatingSummary(receivedReviewSummary?.averageRating, receivedReviewSummary?.totalReviews)}</span>
                <span>Total Reviews: {receivedReviewSummary?.totalReviews ?? 0}</span>
              </div>
            </div>
          </div>
          <div className="applicants-section">
            <form className="create-job-form" onSubmit={handleProfileFormSubmit} style={{ marginBottom: 16 }}>
              <div className="form-row">
                {isFieldVisible('company', 'fullName') && (
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input value={profileForm.fullName} onChange={(e) => setProfileForm((prev) => ({ ...prev, fullName: e.target.value }))} required />
                  </div>
                )}
                {isFieldVisible('company', 'phone') && (
                  <div className="form-group">
                    <label>Phone {isFieldMandatory('company', 'phone') ? '*' : '(Optional)'}</label>
                    <input
                      value={profileForm.phone}
                      required={isFieldMandatory('company', 'phone')}
                      onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                )}
              </div>
              <div className="form-row">
                {isFieldVisible('company', 'companyName') && (
                  <div className="form-group">
                    <label>Company Name {isFieldMandatory('company', 'companyName') ? '*' : '(Optional)'}</label>
                    <input
                      value={profileForm.companyName}
                      required={isFieldMandatory('company', 'companyName')}
                      onChange={(e) => setProfileForm((prev) => ({ ...prev, companyName: e.target.value }))}
                    />
                  </div>
                )}
                {isFieldVisible('company', 'industry') && (
                  <div className="form-group">
                    <label>Industry {isFieldMandatory('company', 'industry') ? '*' : '(Optional)'}</label>
                    <input
                      value={profileForm.industry}
                      required={isFieldMandatory('company', 'industry')}
                      onChange={(e) => setProfileForm((prev) => ({ ...prev, industry: e.target.value }))}
                    />
                  </div>
                )}
              </div>
              <div className="form-row">
                {isFieldVisible('company', 'companyWebsite') && (
                  <div className="form-group">
                    <label>Company Website {isFieldMandatory('company', 'companyWebsite') ? '*' : '(Optional)'}</label>
                    <input
                      value={profileForm.companyWebsite}
                      required={isFieldMandatory('company', 'companyWebsite')}
                      onChange={(e) => setProfileForm((prev) => ({ ...prev, companyWebsite: e.target.value }))}
                    />
                  </div>
                )}
                {isFieldVisible('company', 'companySize') && (
                  <div className="form-group">
                    <label>Company Size {isFieldMandatory('company', 'companySize') ? '*' : '(Optional)'}</label>
                    <input
                      type="number"
                      min="1"
                      value={profileForm.companySize}
                      required={isFieldMandatory('company', 'companySize')}
                      onChange={(e) => setProfileForm((prev) => ({ ...prev, companySize: e.target.value }))}
                    />
                  </div>
                )}
              </div>
              <div className="form-row">
                {isFieldVisible('company', 'location') && (
                  <div className="form-group">
                    <label>Location {isFieldMandatory('company', 'location') ? '*' : '(Optional)'}</label>
                    <input
                      value={profileForm.city}
                      required={isFieldMandatory('company', 'location')}
                      onChange={(e) => setProfileForm((prev) => ({ ...prev, city: e.target.value }))}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>State (Optional)</label>
                  <input value={profileForm.state} onChange={(e) => setProfileForm((prev) => ({ ...prev, state: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Country (Optional)</label>
                  <input value={profileForm.country} onChange={(e) => setProfileForm((prev) => ({ ...prev, country: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>New Password (Optional)</label>
                  <input type="password" value={profileForm.password} onChange={(e) => setProfileForm((prev) => ({ ...prev, password: e.target.value }))} />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={profileSaving}>{profileSaving ? 'Saving...' : 'Update Company Profile'}</button>
              </div>
            </form>
            <div className="company-job-card" style={{ borderColor: '#f97316', marginBottom: 16 }}>
              <div className="company-job-header" style={{ cursor: 'default' }}>
                <div className="company-job-info">
                  <h3>Settings &gt; Privacy &amp; Security &gt; Delete Account</h3>
                  <div className="company-job-meta">
                    <span>Soft-delete with a restore grace period.</span>
                  </div>
                </div>
              </div>
              <div className="applicants-section">
                <div className="form-row">
                  <div className="form-group">
                    <label>Current Password *</label>
                    <input
                      type="password"
                      value={deletionPassword}
                      onChange={(e) => setDeletionPassword(e.target.value)}
                      placeholder="Required to request deletion"
                    />
                  </div>
                  <div className="form-group">
                    <label>Reason (optional)</label>
                    <input
                      value={deletionReason}
                      onChange={(e) => setDeletionReason(e.target.value)}
                      placeholder="Tell us why you're leaving"
                    />
                  </div>
                </div>
                <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                  <button
                    type="button"
                    className="btn-danger-outline"
                    disabled={deletionRequesting}
                    onClick={() => { handleRequestAccountDeletion().catch(() => {}); }}
                  >
                    {deletionRequesting ? 'Requesting...' : 'Request Deletion OTP'}
                  </button>
                </div>
                {deletionOtpRequested && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Deletion OTP *</label>
                        <input
                          value={deletionOtp}
                          onChange={(e) => setDeletionOtp(e.target.value)}
                          placeholder="Enter 6-digit code"
                        />
                      </div>
                    </div>
                    {deletionRestoreDeadline && (
                      <p className="detail-text">Restore available until: {fmtDateTime(deletionRestoreDeadline)}</p>
                    )}
                    <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={deletionConfirming}
                        onClick={() => { handleConfirmAccountDeletion().catch(() => {}); }}
                      >
                        {deletionConfirming ? 'Deleting...' : 'Confirm Delete Account'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            {receivedReviewsLoading ? (
              <div className="applicants-list">
                <div className="applicant-card">Loading company reviews...</div>
              </div>
            ) : receivedReviews.length === 0 ? (
              <p className="empty-inline">No reviews about your company yet.</p>
            ) : (
              <div className="applicants-list">
                {receivedReviews.map((review) => (
                  <div key={`company-review-${review.id}`} className="applicant-card">
                    <div className="applicant-top">
                      <div className="applicant-info">
                        <strong>{review.reviewer_name ?? 'Reviewer'}</strong>
                        <span>{review.job_title ?? 'Job review'}</span>
                      </div>
                      <span className="pill pill-confirmed">⭐ {review.rating}/5</span>
                    </div>
                    {review.comment ? <p className="detail-text">{review.comment}</p> : null}
                    <p className="detail-text">Date: {fmtDateTime(review.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {renderCreateJobModal()}
      {renderCreateJobConfirmationModal()}
      {renderEditJobModal()}
      {renderDuplicateJobModal()}
      {renderCompanyJobDetailsModal()}
      {/* Reopen modal removed */}
    </div>
  );

  const renderAdminFieldTable = (userType: RegistrationUserType, title: string) => {
    const rows = normalizeFieldConfigs(userType, registrationFieldConfigs)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((cfg) => ({
        ...cfg,
        label: getCatalog(userType).find((item) => item.fieldKey === cfg.fieldKey)?.label ?? cfg.fieldKey,
      }));

    return (
      <div className="company-job-card">
        <div className="company-job-header" style={{ cursor: 'default' }}>
          <div className="company-job-info">
            <h3>{title}</h3>
            <div className="company-job-meta">
              <span>Control visibility and required fields for registration.</span>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="btn-primary"
              onClick={() => saveFieldConfig(userType)}
              disabled={adminSavingByType[userType]}
            >
              {adminSavingByType[userType] ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        <div className="applicants-section">
          {fieldConfigLoading ? (
            <p className="empty-inline">Loading configuration...</p>
          ) : (
            <div className="applicants-list">
              {rows.map((row) => (
                <div key={`${row.userType}-${row.fieldKey}`} className="applicant-card">
                  <div className="applicant-top">
                    <div className="applicant-info">
                      <strong>{row.label}</strong>
                      <span>{row.fieldKey}</span>
                    </div>
                    <div className="applicant-actions">
                      <label className="checkbox-inline" style={{ marginRight: 12 }}>
                        <input
                          type="checkbox"
                          checked={row.isVisible}
                          onChange={() => toggleFieldConfig(userType, row.fieldKey, 'visible')}
                        />
                        Visible
                      </label>
                      <label className="checkbox-inline">
                        <input
                          type="checkbox"
                          checked={row.isMandatory}
                          disabled={!row.isVisible}
                          onChange={() => toggleFieldConfig(userType, row.fieldKey, 'mandatory')}
                        />
                        Mandatory
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAdminView = () => (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h2>Admin Console</h2>
          <p className="sub">Switch between analytics and registration field management.</p>
        </div>
        <div className="header-actions">
          {adminTab === 'analytics' && (
            <button className="btn-primary" onClick={() => loadAdminAnalytics().catch(() => {})}>
              Refresh Analytics
            </button>
          )}
          {adminTab === 'job-approvals' && (
            <button className="btn-primary" onClick={() => loadAdminJobControls().catch(() => {})}>
              Refresh Job Approvals
            </button>
          )}
          {adminTab === 'performance' && (
            <button className="btn-primary" onClick={() => loadAdminPerformanceIndex().catch(() => {})}>
              Refresh Performance
            </button>
          )}
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {getRoleTabItems('admin').map((tabItem) => (
          <button key={tabItem.key} className={`tab ${adminTab === tabItem.key ? 'active' : ''}`} onClick={() => setAdminTab(tabItem.key as AdminTab)}>
            <span>{tabItem.label}</span>
          </button>
        ))}
      </div>

      {adminTab === 'analytics' && (
        <div className="company-jobs-list" style={{ marginBottom: 16 }}>
          <div className="company-job-card">
            <div className="company-job-header" style={{ cursor: 'default' }}>
              <div className="company-job-info">
                <h3>Analytics Overview</h3>
                <div className="company-job-meta">
                  <span>Active users, traffic, conversion, and job posting trends</span>
                </div>
              </div>
            </div>
            <div className="applicants-section">
              {adminAnalyticsLoading ? (
                <p className="empty-inline">Loading analytics...</p>
              ) : (
                <div className="applicants-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Active Daily Users - Trend Details', (adminUserAnalytics?.growth.daily ?? []).map((p) => ({ label: p.bucket, value: p.value })))}><div className="applicant-info"><span>Active Daily</span><strong>{adminUserAnalytics?.activeUsers.daily ?? 0}</strong></div></button>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Active Weekly Users - Trend Details', (adminUserAnalytics?.growth.weekly ?? []).map((p) => ({ label: p.bucket, value: p.value })))}><div className="applicant-info"><span>Active Weekly</span><strong>{adminUserAnalytics?.activeUsers.weekly ?? 0}</strong></div></button>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Active Monthly Users - Trend Details', (adminUserAnalytics?.growth.monthly ?? []).map((p) => ({ label: p.bucket, value: p.value })))}><div className="applicant-info"><span>Active Monthly</span><strong>{adminUserAnalytics?.activeUsers.monthly ?? 0}</strong></div></button>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Unique Visitors Details', [{ label: 'Unique Visitors', value: adminTrafficAnalytics?.kpis.uniqueVisitors ?? 0 }, { label: 'Sessions', value: adminTrafficAnalytics?.kpis.sessions ?? 0 }, { label: 'Page Views', value: adminTrafficAnalytics?.kpis.pageViews ?? 0 }])}><div className="applicant-info"><span>Unique Visitors</span><strong>{adminTrafficAnalytics?.kpis.uniqueVisitors ?? 0}</strong></div></button>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Page Views Details', (adminTrafficAnalytics?.trends.jobPosting ?? []).map((p) => ({ label: p.bucket, value: p.value })))}><div className="applicant-info"><span>Page Views</span><strong>{adminTrafficAnalytics?.kpis.pageViews ?? 0}</strong></div></button>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Sessions Details', [{ label: 'Sessions', value: adminTrafficAnalytics?.kpis.sessions ?? 0 }, { label: 'Unique Visitors', value: adminTrafficAnalytics?.kpis.uniqueVisitors ?? 0 }])}><div className="applicant-info"><span>Sessions</span><strong>{adminTrafficAnalytics?.kpis.sessions ?? 0}</strong></div></button>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Conversion Funnel Details', [{ label: 'Submitted', value: adminConversionAnalytics?.conversion.submitted ?? 0 }, { label: 'Accepted', value: adminConversionAnalytics?.conversion.accepted ?? 0 }, { label: 'Completed', value: adminConversionAnalytics?.conversion.completed ?? 0 }, { label: 'Conversion Rate (%)', value: adminConversionAnalytics?.summary.conversionRate ?? 0 }])}><div className="applicant-info"><span>Conversion Rate</span><strong>{adminConversionAnalytics?.summary.conversionRate ?? 0}%</strong></div></button>
                  <button type="button" className="applicant-card" onClick={() => openAdminMetricDetail('Jobs Completed Details', [{ label: 'Jobs Completed', value: adminConversionAnalytics?.summary.jobsCompleted ?? 0 }, { label: 'Accepted', value: adminConversionAnalytics?.conversion.accepted ?? 0 }])}><div className="applicant-info"><span>Jobs Completed</span><strong>{adminConversionAnalytics?.summary.jobsCompleted ?? 0}</strong></div></button>
                  <button
                    type="button"
                    className="applicant-card"
                    onClick={() => openAdminMetricDetail(
                      'Registered Users Details',
                      (adminUserAnalytics?.registrySummary?.users ?? []).map((item) => ({
                        label: `${item.name} • ${item.email}${item.isDeleted ? ' • Deleted account' : ''}`,
                        value: item.isDeleted
                          ? `Created: ${fmtDateTime(item.createdAt)}${item.deletedAt ? ` | Deleted: ${fmtDateTime(item.deletedAt)}` : ''}`
                          : `Created: ${fmtDateTime(item.createdAt)}`,
                      })),
                    )}
                  >
                    <div className="applicant-info"><span>Total Users</span><strong>{adminUserAnalytics?.registrySummary?.totalUsers ?? 0}</strong></div>
                  </button>
                  <button
                    type="button"
                    className="applicant-card"
                    onClick={() => openAdminMetricDetail(
                      'Registered Companies Details',
                      (adminUserAnalytics?.registrySummary?.companies ?? []).map((item) => ({
                        label: `${item.name} • ${item.email}${item.isDeleted ? ' • Deleted account' : ''}`,
                        value: item.isDeleted
                          ? `Created: ${fmtDateTime(item.createdAt)}${item.deletedAt ? ` | Deleted: ${fmtDateTime(item.deletedAt)}` : ''}`
                          : `Created: ${fmtDateTime(item.createdAt)}`,
                      })),
                    )}
                  >
                    <div className="applicant-info"><span>Total Companies</span><strong>{adminUserAnalytics?.registrySummary?.totalCompanies ?? 0}</strong></div>
                  </button>
                </div>
              )}
            </div>

            <div className="applicants-section" style={{ borderTop: '1px solid #e2e8f0' }}>
              <h4 style={{ marginBottom: 10 }}>Job Publishing Controls</h4>
              <p className="detail-text" style={{ marginBottom: 12 }}>
                Configure auto-approval and broadcast email behavior for newly created jobs.
              </p>
              <div className="applicants-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12 }}>
                <label className="applicant-card" style={{ cursor: 'pointer' }}>
                  <div className="applicant-top">
                    <div className="applicant-info">
                      <strong>Auto-approve new jobs</strong>
                      <span>{adminJobControls.autoApproveJobs ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={adminJobControls.autoApproveJobs}
                      onChange={(e) => setAdminJobControls((prev) => ({ ...prev, autoApproveJobs: e.target.checked }))}
                    />
                  </div>
                </label>
                <label className="applicant-card" style={{ cursor: 'pointer' }}>
                  <div className="applicant-top">
                    <div className="applicant-info">
                      <strong>Send auto-email to seekers</strong>
                      <span>{adminJobControls.sendAutoEmailOnNewJob ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={adminJobControls.sendAutoEmailOnNewJob}
                      onChange={(e) => setAdminJobControls((prev) => ({ ...prev, sendAutoEmailOnNewJob: e.target.checked }))}
                    />
                  </div>
                </label>
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: 12 }}>
                <button className="btn-primary" disabled={adminJobControlsSaving} onClick={() => { saveAdminJobControls().catch(() => {}); }}>
                  {adminJobControlsSaving ? 'Saving...' : 'Save Job Controls'}
                </button>
                <button className="btn-ghost" onClick={() => { loadAdminJobControls().catch(() => {}); }} disabled={adminJobControlsLoading}>
                  {adminJobControlsLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              <h4 style={{ marginTop: 20, marginBottom: 10 }}>Pending Job Approvals</h4>
              {adminJobControlsLoading ? (
                <p className="empty-inline">Loading pending approvals...</p>
              ) : adminPendingApprovalJobs.length === 0 ? (
                <p className="empty-inline">No pending jobs. Inbox is clean 🧼</p>
              ) : (
                <div className="applicants-list">
                  {adminPendingApprovalJobs.map((job) => (
                    <div key={`pending-job-${job.id}`} className="applicant-card">
                      <div className="applicant-top">
                        <div className="applicant-info">
                          <strong>{job.title}</strong>
                          <span>{job.companyName} • {job.location}</span>
                        </div>
                        <span className="pill pill-pending">Pending</span>
                      </div>
                      <p className="detail-text">
                        {fmtDateTime(job.startAt)} - {fmtDateTime(job.endAt)} • {job.workerSlots} slot{job.workerSlots === 1 ? '' : 's'} • {job.rateType === 'hourly' ? `$${job.rateAmount}/hr` : `$${job.rateAmount} fixed`}
                      </p>
                      <div className="applicant-actions">
                        <button
                          className="btn-primary"
                          disabled={adminPendingDecisionLoadingId === job.id}
                          onClick={() => { decidePendingJobApproval(job.id, 'approve').catch(() => {}); }}
                        >
                          {adminPendingDecisionLoadingId === job.id ? 'Working...' : 'Approve'}
                        </button>
                        <button
                          className="btn-danger-outline"
                          disabled={adminPendingDecisionLoadingId === job.id}
                          onClick={() => { decidePendingJobApproval(job.id, 'reject').catch(() => {}); }}
                        >
                          {adminPendingDecisionLoadingId === job.id ? 'Working...' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="applicants-section" style={{ borderTop: '1px solid #e2e8f0' }}>
              <h4 style={{ marginBottom: 10 }}>User Growth Trend (last 30 days)</h4>
              {adminUserAnalytics?.growth.monthly?.length ? (
                <div className="applicants-list">
                  {adminUserAnalytics.growth.monthly.slice(-10).map((point) => {
                    const max = Math.max(...adminUserAnalytics.growth.monthly.map((p) => p.value), 1);
                    const width = Math.max((point.value / max) * 100, 4);
                    return (
                      <div key={`user-growth-${point.bucket}`} className="applicant-card" style={{ padding: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span>{point.bucket}</span>
                          <strong>{point.value}</strong>
                        </div>
                        <div style={{ height: 8, background: '#e2e8f0', borderRadius: 999 }}>
                          <div style={{ width: `${width}%`, height: '100%', borderRadius: 999, background: '#2563eb' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-inline">No user growth data available.</p>
              )}
            </div>

            <div className="applicants-section" style={{ borderTop: '1px solid #e2e8f0' }}>
              <h4 style={{ marginBottom: 10 }}>Job Posting Trend (last 30 days)</h4>
              {adminTrafficAnalytics?.trends.jobPosting?.length ? (
                <div className="applicants-list">
                  {adminTrafficAnalytics.trends.jobPosting.slice(-10).map((point) => {
                    const max = Math.max(...adminTrafficAnalytics.trends.jobPosting.map((p) => p.value), 1);
                    const width = Math.max((point.value / max) * 100, 4);
                    return (
                      <div key={`job-trend-${point.bucket}`} className="applicant-card" style={{ padding: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span>{point.bucket}</span>
                          <strong>{point.value}</strong>
                        </div>
                        <div style={{ height: 8, background: '#e2e8f0', borderRadius: 999 }}>
                          <div style={{ width: `${width}%`, height: '100%', borderRadius: 999, background: '#7c3aed' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-inline">No job trend data available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {adminTab === 'performance' && (
        <div className="company-jobs-list" style={{ marginBottom: 16 }}>
          <div className="company-job-card">
            <div className="company-job-header" style={{ cursor: 'default' }}>
              <div className="company-job-info">
                <h3>Company Performance</h3>
                <div className="company-job-meta">
                  <span>Select a company to review openings, completed jobs, hours contracted, and total spend.</span>
                </div>
              </div>
            </div>
            <div className="applicants-section">
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label htmlFor="admin-company-performance-select">Select Company</label>
                <select
                  id="admin-company-performance-select"
                  value={selectedAdminCompanyId ?? ''}
                  onChange={(event) => {
                    const next = Number.parseInt(event.target.value, 10);
                    setSelectedAdminCompanyId(Number.isInteger(next) ? next : null);
                  }}
                  disabled={adminPerformanceLoading || adminCompanyPerformanceList.length === 0}
                >
                  {adminCompanyPerformanceList.length === 0 ? (
                    <option value="">No companies found</option>
                  ) : (
                    adminCompanyPerformanceList.map((company) => (
                      <option key={`company-${company.companyUserId}`} value={company.companyUserId}>
                        {company.companyName} ({company.email})
                      </option>
                    ))
                  )}
                </select>
              </div>

              {adminPerformanceLoading ? (
                <p className="empty-inline">Loading company performance...</p>
              ) : !adminCompanyPerformance ? (
                <p className="empty-inline">Select a company to view performance metrics.</p>
              ) : (
                <div className="applicants-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
                  <div className="applicant-card"><div className="applicant-info"><span>Total Jobs Created</span><strong>{adminCompanyPerformance.totalJobsCreated}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Currently Open Jobs</span><strong>{adminCompanyPerformance.activeJobs}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Completed Jobs</span><strong>{adminCompanyPerformance.completedJobs}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Total Applicants</span><strong>{adminCompanyPerformance.totalApplicants}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Accepted Applicants</span><strong>{adminCompanyPerformance.acceptedApplicants}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Total Hours Contracted</span><strong>{adminCompanyPerformance.totalHoursContracted.toFixed(2)} hrs</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Total Spend</span><strong>${adminCompanyPerformance.totalSpent.toFixed(2)}</strong></div></div>
                </div>
              )}
            </div>
          </div>

          <div className="company-job-card">
            <div className="company-job-header" style={{ cursor: 'default' }}>
              <div className="company-job-info">
                <h3>Candidate Performance</h3>
                <div className="company-job-meta">
                  <span>Select a candidate to review completed hours, earnings, and application outcomes.</span>
                </div>
              </div>
            </div>
            <div className="applicants-section">
              <div className="form-group" style={{ maxWidth: 420 }}>
                <label htmlFor="admin-candidate-performance-select">Select Candidate</label>
                <select
                  id="admin-candidate-performance-select"
                  value={selectedAdminCandidateId ?? ''}
                  onChange={(event) => {
                    const next = Number.parseInt(event.target.value, 10);
                    setSelectedAdminCandidateId(Number.isInteger(next) ? next : null);
                  }}
                  disabled={adminPerformanceLoading || adminCandidatePerformanceList.length === 0}
                >
                  {adminCandidatePerformanceList.length === 0 ? (
                    <option value="">No candidates found</option>
                  ) : (
                    adminCandidatePerformanceList.map((candidate) => (
                      <option key={`candidate-${candidate.candidateUserId}`} value={candidate.candidateUserId}>
                        {candidate.candidateName} ({candidate.email})
                      </option>
                    ))
                  )}
                </select>
              </div>

              {adminPerformanceLoading ? (
                <p className="empty-inline">Loading candidate performance...</p>
              ) : !adminCandidatePerformance ? (
                <p className="empty-inline">Select a candidate to view performance metrics.</p>
              ) : (
                <div className="applicants-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
                  <div className="applicant-card"><div className="applicant-info"><span>Applications Submitted</span><strong>{adminCandidatePerformance.applicationsSubmitted}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Applications Accepted</span><strong>{adminCandidatePerformance.applicationsAccepted}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Applications Rejected</span><strong>{adminCandidatePerformance.applicationsRejected}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Applications Pending</span><strong>{adminCandidatePerformance.applicationsPending}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Jobs Completed</span><strong>{adminCandidatePerformance.jobsCompleted}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Total Hours Worked</span><strong>{adminCandidatePerformance.totalHoursWorked.toFixed(2)} hrs</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Total Earned</span><strong>${adminCandidatePerformance.totalEarned.toFixed(2)}</strong></div></div>
                  <div className="applicant-card"><div className="applicant-info"><span>Average Rating</span><strong>{adminCandidatePerformance.averageRating ? `${adminCandidatePerformance.averageRating.toFixed(2)} ⭐` : 'New'}</strong></div></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {adminTab === 'job-approvals' && (
        <div className="company-jobs-list" style={{ marginBottom: 16 }}>
          <div className="company-job-card">
            <div className="company-job-header" style={{ cursor: 'default' }}>
              <div className="company-job-info">
                <h3>Job Publishing Controls</h3>
                <div className="company-job-meta">
                  <span>Configure auto-approval and seeker email broadcast behavior for newly approved jobs.</span>
                </div>
              </div>
            </div>

            <div className="applicants-section" style={{ borderTop: '1px solid #e2e8f0' }}>
              <div className="applicants-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12 }}>
                <label className="applicant-card" style={{ cursor: 'pointer' }}>
                  <div className="applicant-top">
                    <div className="applicant-info">
                      <strong>Auto-approve new jobs</strong>
                      <span>{adminJobControls.autoApproveJobs ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={adminJobControls.autoApproveJobs}
                      onChange={(e) => setAdminJobControls((prev) => ({ ...prev, autoApproveJobs: e.target.checked }))}
                    />
                  </div>
                </label>
                <label className="applicant-card" style={{ cursor: 'pointer' }}>
                  <div className="applicant-top">
                    <div className="applicant-info">
                      <strong>Send auto-email to seekers</strong>
                      <span>{adminJobControls.sendAutoEmailOnNewJob ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={adminJobControls.sendAutoEmailOnNewJob}
                      onChange={(e) => setAdminJobControls((prev) => ({ ...prev, sendAutoEmailOnNewJob: e.target.checked }))}
                    />
                  </div>
                </label>
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: 12 }}>
                <button className="btn-primary" disabled={adminJobControlsSaving} onClick={() => { saveAdminJobControls().catch(() => {}); }}>
                  {adminJobControlsSaving ? 'Saving...' : 'Save Job Controls'}
                </button>
                <button className="btn-ghost" onClick={() => { loadAdminJobControls().catch(() => {}); }} disabled={adminJobControlsLoading}>
                  {adminJobControlsLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              <h4 style={{ marginTop: 20, marginBottom: 10 }}>Pending Job Approvals</h4>
              {adminJobControlsLoading ? (
                <p className="empty-inline">Loading pending approvals...</p>
              ) : adminPendingApprovalJobs.length === 0 ? (
                <p className="empty-inline">No pending jobs. Inbox is clean 🧼</p>
              ) : (
                <div className="applicants-list">
                  {adminPendingApprovalJobs.map((job) => (
                    <div key={`pending-job-tab-${job.id}`} className="applicant-card">
                      <div className="applicant-top">
                        <div className="applicant-info">
                          <strong>{job.title}</strong>
                          <span>{job.companyName} • {job.location}</span>
                        </div>
                        <span className="pill pill-pending">Pending</span>
                      </div>
                      <p className="detail-text">
                        {fmtDateTime(job.startAt)} - {fmtDateTime(job.endAt)} • {job.workerSlots} slot{job.workerSlots === 1 ? '' : 's'} • {job.rateType === 'hourly' ? `$${job.rateAmount}/hr` : `$${job.rateAmount} fixed`}
                      </p>
                      <div className="applicant-actions">
                        <button
                          className="btn-primary"
                          disabled={adminPendingDecisionLoadingId === job.id}
                          onClick={() => { decidePendingJobApproval(job.id, 'approve').catch(() => {}); }}
                        >
                          {adminPendingDecisionLoadingId === job.id ? 'Working...' : 'Approve'}
                        </button>
                        <button
                          className="btn-danger-outline"
                          disabled={adminPendingDecisionLoadingId === job.id}
                          onClick={() => { decidePendingJobApproval(job.id, 'reject').catch(() => {}); }}
                        >
                          {adminPendingDecisionLoadingId === job.id ? 'Working...' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {adminTab === 'manage-tabs' && (
        <div className="company-jobs-list">
          <div className="company-job-card">
            <div className="company-job-header" style={{ cursor: 'default' }}>
              <div className="company-job-info">
                <h3>Manage Global Tab</h3>
                <div className="company-job-meta">
                  <span>Admin controls default tabs for each role. Individual users can still override.</span>
                </div>
              </div>
              <div className="header-actions">
                <select
                  value={adminDefaultTabRole}
                  onChange={(e) => {
                    const nextRole = e.target.value as RegistrationUserType;
                    setAdminDefaultTabRole(nextRole);
                    loadAdminRoleDefaults(nextRole).catch(() => {});
                  }}
                >
                  <option value="job_seeker">Job Seeker</option>
                  <option value="company">Company</option>
                </select>
                <button className="btn-primary" onClick={() => saveAdminRoleDefaults().catch(() => {})} disabled={adminDefaultTabsLoading}>
                  {adminDefaultTabsLoading ? 'Saving...' : 'Save Role Defaults'}
                </button>
              </div>
            </div>
            <div className="applicants-section">
              {(tabPreferencesByRole[adminDefaultTabRole]?.tabOrder ?? []).map((key, index, array) => (
                <div key={`admin-role-tab-${key}`} className="applicant-card">
                  <div className="applicant-top">
                    <div className="applicant-info">
                      <strong>{DEFAULT_TAB_LABELS[adminDefaultTabRole][key] ?? key}</strong>
                      <span>{key}</span>
                    </div>
                    <div className="applicant-actions">
                      <button
                        className="btn-ghost"
                        onClick={() => setTabPreferencesByRole((current) => {
                          const config = current[adminDefaultTabRole];
                          const currentIndex = config.tabOrder.indexOf(key);
                          if (currentIndex <= 0) return current;
                          const nextOrder = [...config.tabOrder];
                          [nextOrder[currentIndex - 1], nextOrder[currentIndex]] = [nextOrder[currentIndex], nextOrder[currentIndex - 1]];
                          return { ...current, [adminDefaultTabRole]: { ...config, tabOrder: nextOrder } };
                        })}
                        disabled={index === 0}
                      >
                        ←
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => setTabPreferencesByRole((current) => {
                          const config = current[adminDefaultTabRole];
                          const currentIndex = config.tabOrder.indexOf(key);
                          if (currentIndex < 0 || currentIndex >= config.tabOrder.length - 1) return current;
                          const nextOrder = [...config.tabOrder];
                          [nextOrder[currentIndex + 1], nextOrder[currentIndex]] = [nextOrder[currentIndex], nextOrder[currentIndex + 1]];
                          return { ...current, [adminDefaultTabRole]: { ...config, tabOrder: nextOrder } };
                        })}
                        disabled={index === array.length - 1}
                      >
                        →
                      </button>
                      <label className="checkbox-inline">
                        <input
                          type="checkbox"
                          checked={tabPreferencesByRole[adminDefaultTabRole]?.tabVisibility[key] !== false}
                          onChange={(e) => setTabPreferencesByRole((current) => {
                            const config = current[adminDefaultTabRole];
                            return {
                              ...current,
                              [adminDefaultTabRole]: {
                                ...config,
                                tabVisibility: {
                                  ...config.tabVisibility,
                                  [key]: e.target.checked,
                                },
                              },
                            };
                          })}
                        />
                        Visible
                      </label>
                    </div>
                  </div>
                  <input
                    value={tabPreferencesByRole[adminDefaultTabRole]?.tabLabels[key] ?? ''}
                    placeholder={`Custom label for ${DEFAULT_TAB_LABELS[adminDefaultTabRole][key] ?? key}`}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTabPreferencesByRole((current) => {
                        const config = current[adminDefaultTabRole];
                        return {
                          ...current,
                          [adminDefaultTabRole]: {
                            ...config,
                            tabLabels: {
                              ...config.tabLabels,
                              [key]: value,
                            },
                          },
                        };
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {adminTab === 'registration' && (
        <div className="company-jobs-list">
          {renderAdminFieldTable('job_seeker', 'Job Seeker Registration')}
          {renderAdminFieldTable('company', 'Company Registration')}
        </div>
      )}

      {adminMetricDetail && (
        <div
          className="modal-overlay"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setAdminMetricDetail(null);
              setAdminMetricPage(1);
            }
          }}
          onKeyDown={(e) => handleModalOverlayKeyDown(e, () => {
            setAdminMetricDetail(null);
            setAdminMetricPage(1);
          })}
        >
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => {
                setAdminMetricDetail(null);
                setAdminMetricPage(1);
              }}
            >
              ×
            </button>
            <h2 className="modal-form-title">{adminMetricDetail.title}</h2>
            {adminMetricDetail.rows.length === 0 ? (
              <p className="empty-inline">No details available for this metric.</p>
            ) : (
              <div className="applicants-list">
                {adminMetricDetail.rows
                  .slice((adminMetricPage - 1) * 10, adminMetricPage * 10)
                  .map((row, index) => (
                  <div key={`${row.label}-${index}`} className="applicant-card">
                    <div className="applicant-top">
                      <div className="applicant-info">
                        <strong>{row.label}</strong>
                      </div>
                      <span className="pill pill-neutral">{row.value}</span>
                    </div>
                  </div>
                  ))}
                {adminMetricDetail.rows.length > 10 ? (
                  <div className="applicants-pagination">
                    <button
                      className="btn-ghost"
                      disabled={adminMetricPage <= 1}
                      onClick={() => setAdminMetricPage((page) => Math.max(1, page - 1))}
                    >
                      Previous
                    </button>
                    <span>Page {adminMetricPage} / {Math.max(1, Math.ceil(adminMetricDetail.rows.length / 10))}</span>
                    <button
                      className="btn-ghost"
                      disabled={adminMetricPage >= Math.max(1, Math.ceil(adminMetricDetail.rows.length / 10))}
                      onClick={() => setAdminMetricPage((page) => Math.min(Math.max(1, Math.ceil(adminMetricDetail.rows.length / 10)), page + 1))}
                    >
                      Next
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderPersistentAiChatPanel = () => {
    if (!user || view === 'auth') return null;

    return (
      <div className={`ai-chat-fab ${aiChatPanelOpen ? 'open' : ''}`}>
        {!aiChatPanelOpen ? (
          <button className="ai-chat-launch" onClick={() => setAiChatPanelOpen(true)}>
            💬 AI Assistant
          </button>
        ) : (
          <div className="ai-chat-panel">
            <div className="ai-chat-header">
              <strong>AI Assistant</strong>
              <button
                className="btn-ghost"
                onClick={() => {
                  handleStopAiChatVoiceCapture();
                  setAiChatPanelOpen(false);
                }}
              >
                Close
              </button>
            </div>
            <div className="ai-chat-messages">
              {aiChatMessages.map((entry, index) => (
                <div key={`${entry.role}-${index}`} className={`ai-chat-message ${entry.role}`}>
                  {entry.message}
                </div>
              ))}
            </div>
            <div className="ai-chat-input-row">
              <input
                value={aiChatInput}
                onChange={(e) => setAiChatInput(e.target.value)}
                placeholder={user.role === 'company' ? 'Try: Create a skilled trade job from Apr 1 to Apr 3 in Lehi at $200/day' : 'Ask about applications, jobs, and status'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAiChat().catch(() => {});
                  }
                }}
              />
              {!aiChatVoiceListening ? (
                <button type="button" className="btn-ghost" onClick={handleStartAiChatVoiceCapture}>🎙️</button>
              ) : (
                <button type="button" className="btn-danger-outline" onClick={handleStopAiChatVoiceCapture}>Stop</button>
              )}
              <button className="btn-primary" disabled={aiChatLoading} onClick={() => { handleAiChat().catch(() => {}); }}>
                {aiChatLoading ? '...' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabPreferencesModal = () => {
    if (!showTabPreferencesModal || !tabPreferencesDraft) return null;

    return (
      <div
        className="modal-overlay"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowTabPreferencesModal(false);
            setTabPreferencesDraft(null);
          }
        }}
        onKeyDown={(e) => handleModalOverlayKeyDown(e, () => {
          setShowTabPreferencesModal(false);
          setTabPreferencesDraft(null);
        })}
      >
        <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => { setShowTabPreferencesModal(false); setTabPreferencesDraft(null); }}>×</button>
          <h2 className="modal-form-title">Manage My Tabs</h2>
          <div className="applicants-list">
            {tabPreferencesDraft.tabOrder.map((key, index, array) => (
              <div key={`my-tab-${key}`} className="applicant-card">
                <div className="applicant-top">
                  <div className="applicant-info">
                    <strong>{DEFAULT_TAB_LABELS[tabPreferencesDraft.role][key] ?? key}</strong>
                    <span>{key}</span>
                  </div>
                  <div className="applicant-actions">
                    <button className="btn-ghost" onClick={() => moveTabInDraft(key, 'left')} disabled={index === 0}>←</button>
                    <button className="btn-ghost" onClick={() => moveTabInDraft(key, 'right')} disabled={index === array.length - 1}>→</button>
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={tabPreferencesDraft.tabVisibility[key] !== false}
                        onChange={(e) => setTabPreferencesDraft((current) => current ? {
                          ...current,
                          tabVisibility: {
                            ...current.tabVisibility,
                            [key]: e.target.checked,
                          },
                        } : current)}
                      />
                      Visible
                    </label>
                  </div>
                </div>
                <input
                  value={tabPreferencesDraft.tabLabels[key] ?? ''}
                  placeholder={`Custom label for ${DEFAULT_TAB_LABELS[tabPreferencesDraft.role][key] ?? key}`}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTabPreferencesDraft((current) => current ? {
                      ...current,
                      tabLabels: {
                        ...current.tabLabels,
                        [key]: value,
                      },
                    } : current);
                  }}
                />
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button className="btn-ghost" onClick={() => { setShowTabPreferencesModal(false); setTabPreferencesDraft(null); }}>Cancel</button>
            <button className="btn-primary" onClick={() => saveMyTabPreferences().catch(() => {})} disabled={tabPreferencesSaving}>
              {tabPreferencesSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════
  return (
    <MainLayout
      className="app-shell"
      header={renderNavbar()}
      footer={(
        <Footer className="landing-footer" variant="landing" />
      )}
    >
      {sessionLoading && (
        <div className="session-loading-screen">
          <div className="session-loading-card">
            <div className="spinner" />
            <h3>Loading your workspace...</h3>
            <p>Taking you to the right dashboard.</p>
          </div>
        </div>
      )}
      {renderToasts()}
      {!sessionLoading && view === 'auth' && renderAuthView()}
      {!sessionLoading && view === 'seeker' && renderSeekerView()}
      {!sessionLoading && view === 'company' && renderCompanyView()}
      {!sessionLoading && view === 'admin' && renderAdminView()}
      {!sessionLoading && renderPersistentAiChatPanel()}
      {!sessionLoading && renderTabPreferencesModal()}
    </MainLayout>
  );
}

export default App;
